import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Save, RotateCcw, ExternalLink, Plus, Trash2, ChevronUp, ChevronDown, Tv, Smartphone, Monitor, EyeOff, Eye, Download, Wrench, Database, Loader2, AlertTriangle, CheckCircle2, Settings, RefreshCw } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { useAdminAuth, API } from '../context/AdminAuthContext';
import { useAdminPermissions } from '../hooks/useAdminPermissions';
import { useSettings } from '../context/SettingsContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import ImageUpload from '../components/ImageUpload';
import DocumentsPanel from '../components/DocumentsPanel';
import TypographyPanel from '../components/TypographyPanel';
import NotificationsPanel from '../components/NotificationsPanel';
import { formatDateTime } from '../lib/dateFormat';
import { useToast } from '../hooks/use-toast';

const Section = ({ title, desc, children }) => (
  <div className="bg-white rounded-2xl border border-neutral-200 p-6 lg:p-8">
    <div className="mb-5">
      <h2 className="text-lg font-bold text-[#0F172A]">{title}</h2>
      {desc && <p className="text-sm text-neutral-500 mt-1">{desc}</p>}
    </div>
    <div className="space-y-4">{children}</div>
  </div>
);

const Field = ({ label, hint, children, full = false }) => (
  <div className={full ? 'col-span-2' : ''}>
    <label className="text-xs font-semibold text-neutral-700 uppercase tracking-wide">{label}</label>
    <div className="mt-1">{children}</div>
    {hint && <p className="text-[11px] text-neutral-500 mt-1">{hint}</p>}
  </div>
);

/**
 * DeviceVisibilityRow — 4-state segmented control for a homepage section.
 *
 * Reads/writes three fields on the settings form:
 *   - <baseKey>            (master toggle, kept for backward compat)
 *   - <baseKey>_mobile     (true → render on mobile)
 *   - <baseKey>_desktop    (true → render on desktop)
 *
 * Modes:
 *   both    →   master ON,  mobile=true,  desktop=true
 *   desktop →   master ON,  mobile=false, desktop=true
 *   mobile  →   master ON,  mobile=true,  desktop=false
 *   hidden  →   master OFF (mobile/desktop are forced false too)
 */
const DeviceVisibilityRow = ({ baseKey, label, hint, form, update }) => {
  const master = form[baseKey] !== false;
  const mobile = master && form[`${baseKey}_mobile`] !== false;
  const desktop = master && form[`${baseKey}_desktop`] !== false;

  let mode = 'hidden';
  if (master) {
    if (mobile && desktop) mode = 'both';
    else if (desktop) mode = 'desktop';
    else if (mobile) mode = 'mobile';
    else mode = 'hidden';
  }

  const setMode = (next) => {
    const map = {
      both:    { [baseKey]: true,  [`${baseKey}_mobile`]: true,  [`${baseKey}_desktop`]: true },
      desktop: { [baseKey]: true,  [`${baseKey}_mobile`]: false, [`${baseKey}_desktop`]: true },
      mobile:  { [baseKey]: true,  [`${baseKey}_mobile`]: true,  [`${baseKey}_desktop`]: false },
      hidden:  { [baseKey]: false, [`${baseKey}_mobile`]: false, [`${baseKey}_desktop`]: false },
    };
    update(map[next]);
  };

  const opts = [
    { id: 'both',    icon: Eye,       label: 'Both' },
    { id: 'desktop', icon: Monitor,   label: 'Desktop' },
    { id: 'mobile',  icon: Smartphone,label: 'Mobile' },
    { id: 'hidden',  icon: EyeOff,    label: 'Hide' },
  ];

  return (
    <div
      className={`p-3 rounded-xl border transition ${
        mode === 'hidden'
          ? 'bg-slate-50 border-slate-200 opacity-75'
          : 'bg-[#DEECF9]/30 border-[#0078D4]/30'
      }`}
      data-testid={`toggle-${baseKey}`}
    >
      <div className="min-w-0 mb-2.5">
        <div className="text-sm font-semibold text-[#0F172A]">{label}</div>
        {hint && <div className="text-xs text-slate-500 mt-0.5">{hint}</div>}
      </div>
      <div className="flex bg-white border border-slate-200 rounded-full p-0.5">
        {opts.map(({ id, icon: Icon, label: l }) => {
          const active = mode === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => setMode(id)}
              className={`flex-1 inline-flex items-center justify-center gap-1 px-1.5 py-1.5 rounded-full text-[11px] font-semibold transition ${
                active
                  ? id === 'hidden'
                    ? 'bg-slate-700 text-white'
                    : 'bg-[#0078D4] text-white shadow'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
              data-testid={`toggle-${baseKey}-${id}`}
              title={`Show on ${l.toLowerCase()}`}
            >
              <Icon className="w-3 h-3" />
              <span className="hidden xl:inline">{l}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

const AdminSettings = () => {
  const { authHeaders } = useAdminAuth();
  const { isSuper } = useAdminPermissions();
  const { refresh: refreshSettings } = useSettings();
  const { toast } = useToast();
  const [form, setForm] = useState(null);
  const [original, setOriginal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  // Maintenance mode state — independent of the main `form` blob so the
  // super admin can flip it without re-saving every storefront setting.
  const [maint, setMaint] = useState({
    enabled: false, message: '',
    branch_enabled: false, branch_message: '',
  });
  const [maintSaving, setMaintSaving] = useState(false);
  // Tab navigation — persist last tab across refreshes so admin stays where they were
  const [tab, setTab] = useState(() => {
    try {
      return localStorage.getItem('mr_settings_tab') || 'visibility';
    } catch {
      return 'visibility';
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem('mr_settings_tab', tab);
    } catch {
      // ignore
    }
  }, [tab]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/settings`, { headers: authHeaders });
      setForm(data);
      setOriginal(data);
    } catch (e) {
      toast({ title: 'Failed to load settings', description: e.response?.data?.detail || 'Try again' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Load maintenance status — only super admin sees this tab anyway,
    // but the public endpoint is cheap so we always fetch.
    axios.get(`${API}/maintenance`).then(({ data }) => {
      setMaint({
        enabled: !!data.enabled,
        message: data.message || '',
        branch_enabled: !!(data.branch && data.branch.enabled),
        branch_message: (data.branch && data.branch.message) || '',
      });
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // (Feb 2026) Branch portal category assignment moved to per-branch
  // config on /admin/branches — no global setting to load here anymore.

  const saveMaintenance = async (next) => {
    setMaintSaving(true);
    try {
      // Always send the full payload so partial toggles don't wipe the
      // other flag (backend replaces the whole `maintenance` sub-doc).
      const body = {
        enabled: maint.enabled,
        message: maint.message,
        branch_enabled: maint.branch_enabled,
        branch_message: maint.branch_message,
        ...next,
      };
      const { data } = await axios.put(`${API}/admin/maintenance`, body, { headers: authHeaders });
      setMaint({
        enabled: !!data.enabled,
        message: data.message || '',
        branch_enabled: !!(data.branch && data.branch.enabled),
        branch_message: (data.branch && data.branch.message) || '',
      });
      // Detect WHICH toggle changed (if any) to pick the right toast copy.
      let title = 'Maintenance settings saved';
      let description = '';
      if ('enabled' in next && next.enabled !== undefined) {
        title = data.enabled ? 'Storefront is in maintenance mode' : 'Storefront is live';
        description = data.enabled ? 'Customers see the maintenance page. Admin, branch portal + driver still work.' : 'Visitors now see the normal store.';
      } else if ('branch_enabled' in next && next.branch_enabled !== undefined) {
        title = data.branch?.enabled ? 'Branch portal is in maintenance mode' : 'Branch portal is live';
        description = data.branch?.enabled ? 'Branch staff see the maintenance page. Storefront + admin still work.' : 'Branch staff now see the normal /branch portal.';
      }
      toast({ title, description });
    } catch (e) {
      toast({ title: 'Failed to save maintenance toggle', description: e.response?.data?.detail || 'Try again' });
    } finally {
      setMaintSaving(false);
    }
  };

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const updateStat = (idx, patch) =>
    setForm((f) => ({
      ...f,
      stats: f.stats.map((s, i) => (i === idx ? { ...s, ...patch } : s)),
    }));

  // --- Featured slides helpers ---
  const addSlide = () => {
    const blank = {
      id: `slide-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      eyebrow: 'Featured for retailers',
      title: '',
      badge: '',
      image: '',
      bg: '#E0F2FE',
      color: '#0EA5E9',
      price_label: 'From',
      price: '',
      cta_label: 'View',
      link: '',
    };
    setForm((f) => ({ ...f, featured_slides: [...(f.featured_slides || []), blank] }));
  };
  const updateSlide = (idx, patch) =>
    setForm((f) => ({
      ...f,
      featured_slides: f.featured_slides.map((s, i) => (i === idx ? { ...s, ...patch } : s)),
    }));
  const removeSlide = (idx) =>
    setForm((f) => ({
      ...f,
      featured_slides: f.featured_slides.filter((_, i) => i !== idx),
    }));
  const moveSlide = (idx, dir) =>
    setForm((f) => {
      const arr = [...f.featured_slides];
      const ni = idx + dir;
      if (ni < 0 || ni >= arr.length) return f;
      [arr[idx], arr[ni]] = [arr[ni], arr[idx]];
      return { ...f, featured_slides: arr };
    });

  const dirty = form && original && JSON.stringify(form) !== JSON.stringify(original);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/settings`, form, { headers: authHeaders });
      setForm(data);
      setOriginal(data);
      await refreshSettings();
      toast({ title: 'Homepage updated', description: 'Changes are now live on the storefront.' });
    } catch (e) {
      toast({ title: 'Save failed', description: e.response?.data?.detail || 'Try again' });
    } finally {
      setSaving(false);
    }
  };

  const revert = () => setForm(original);

  if (loading || !form) {
    return (
      <AdminLayout>
        <div className="p-10 text-neutral-500">Loading…</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 lg:p-10 max-w-4xl">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-extrabold text-[#0F172A] font-display">
              Homepage settings
            </h1>
            <p className="text-neutral-500 mt-1">
              Use the tabs below to edit every part of your storefront — visibility, hero, slideshow, sections, and footer.
            </p>
          </div>
          <a
            href="/"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 text-sm font-medium text-[#0078D4] hover:text-[#005A9E]"
            data-testid="preview-storefront-link"
          >
            <ExternalLink className="w-4 h-4" /> Preview storefront
          </a>
          {/* Feb 2026 v14 — Cut-off scheduler lives on its own page (calendar
              + branch multi-select UX is too big for the tab row). */}
          <Link
            to="/admin/settings/cutoff"
            className="inline-flex items-center gap-2 text-sm font-medium text-[#0078D4] hover:text-[#005A9E]"
            data-testid="cutoff-schedule-link"
          >
            <ExternalLink className="w-4 h-4" /> Cut-off schedule
          </Link>
        </div>

        <div className="space-y-6 pb-32">
          {/* Tabs bar */}
          <div
            className="sticky top-0 z-20 -mx-6 px-6 lg:-mx-10 lg:px-10 py-3 bg-[#F8FAFC]/95 backdrop-blur border-b border-slate-200"
            data-testid="settings-tabs"
          >
            <div className="flex flex-wrap gap-2">
              {[
                { id: 'visibility', label: 'Visibility', hint: 'Show / hide sections' },
                { id: 'branding', label: 'Branding', hint: 'Logos & promises' },
                { id: 'hero', label: 'Hero', hint: 'Main panel, pill & stats' },
                { id: 'slideshow', label: 'Slideshow', hint: 'TV-screen slides' },
                { id: 'sections', label: 'Sections', hint: 'Headings & CTAs' },
                { id: 'footer', label: 'Footer', hint: 'Contact info' },
                { id: 'typography', label: 'Typography', hint: 'Fonts & sizes' },
                { id: 'documents', label: 'Documents', hint: 'PDFs & invoices' },
                { id: 'notifications', label: 'Notifications', hint: 'Email & SMS alerts' },
                ...(isSuper ? [{ id: 'warehouse', label: 'Warehouse', hint: 'Auto-ASN & FEFO' }] : []),
                ...(isSuper ? [{ id: 'maintenance', label: 'Maintenance', hint: 'Take store offline' }] : []),
              ].map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setTab(t.id)}
                  className={`shrink-0 px-4 h-10 rounded-full text-sm font-semibold transition flex items-center gap-2 ${
                    tab === t.id
                      ? 'bg-[#0F172A] text-white shadow-sm'
                      : 'bg-white text-slate-600 border border-slate-200 hover:border-[#0078D4] hover:text-[#0078D4]'
                  }`}
                  data-testid={`tab-${t.id}`}
                >
                  {t.label}
                  <span className={`hidden xl:inline text-[10px] font-medium ${tab === t.id ? 'text-white/60' : 'text-slate-400'}`}>
                    · {t.hint}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Section visibility */}
          {tab === 'visibility' && (
          <Section
            title="Homepage sections"
            desc="Choose where each block appears. Pick Both, Desktop only, Mobile only, or Hide to remove it entirely."
          >
            <div className="grid sm:grid-cols-2 gap-3" data-testid="visibility-toggles">
              {[
                { key: 'show_hero', label: 'Hero — main blue panel', hint: 'Headline, subtitle, CTA buttons' },
                { key: 'show_hero_eyebrow', label: 'Live status pill', hint: '"B2B trade portal · live" badge above the hero' },
                { key: 'show_featured_card', label: 'Featured slideshow / card', hint: 'The right-side TV-screen' },
                { key: 'show_stats', label: 'Stat tiles', hint: '500+ partners · 24h dispatch · etc.' },
                { key: 'show_categories', label: 'Division grid', hint: 'Glace · Muscat Chips · Kingsy · Saroj' },
                { key: 'show_featured_products', label: 'Bestseller products slider', hint: 'Horizontal product carousel' },
                { key: 'show_brand_promise', label: 'Brand promise grid', hint: 'Scheduled delivery · Tax invoices · etc.' },
                { key: 'show_b2b_cta', label: 'B2B account request', hint: '"Open a trade account" form' },
              ].map(({ key, label, hint }) => (
                <DeviceVisibilityRow
                  key={key}
                  baseKey={key}
                  label={label}
                  hint={hint}
                  form={form}
                  update={update}
                />
              ))}
            </div>
          </Section>
          )}

          {/* Branding tab — logos + brand promise tiles + CTA image */}
          {tab === 'branding' && (
          <>
          <Section
            title="Logos"
            desc="Replace the navbar and footer logos. Square images work best; transparent PNGs look cleanest."
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Navbar logo" full>
                <ImageUpload
                  value={form.logo_image}
                  onChange={(url) => update({ logo_image: url })}
                  bg="#FFFFFF"
                  testId="logo-image"
                />
              </Field>
              <Field label="Footer logo" full hint="If empty, the navbar logo is reused.">
                <ImageUpload
                  value={form.footer_logo_image}
                  onChange={(url) => update({ footer_logo_image: url })}
                  bg="#0F172A"
                  testId="footer-logo-image"
                />
              </Field>
            </div>
          </Section>

          <Section
            title="Invoice / Tax document"
            desc="Controls the layout + tax fields printed on the PDF invoice."
          >
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="VAT TRN" hint="Oman VAT registration number — printed on the top-right of the invoice under Status. Leave blank to hide.">
                <Input
                  value={form.company_vat_number || ''}
                  onChange={(e) => update({ company_vat_number: e.target.value })}
                  placeholder="OM1100000000"
                  data-testid="company-vat-number-input"
                />
              </Field>
              <Field label="Header gap (mm)" hint="Vertical space between the brand-name line and the address line in the invoice header. Default 4mm.">
                <Input
                  type="number"
                  min={0}
                  max={20}
                  step={0.5}
                  value={form.invoice_header_gap_mm ?? 4}
                  onChange={(e) => update({ invoice_header_gap_mm: parseFloat(e.target.value) || 0 })}
                  data-testid="invoice-header-gap-input"
                />
              </Field>
            </div>
          </Section>

          <Section
            title="Brand promise tiles"
            desc="The four small cards in the Why-mrpco section (Delivery, VAT invoices, Trade pricing, Dispatch). Edit the section heading + each tile here."
          >
            <div className="grid grid-cols-2 gap-4 mb-4 pb-4 border-b border-neutral-200">
              <Field label="Section eyebrow" hint="The small blue label above the heading">
                <Input
                  value={form.promises_eyebrow || ''}
                  onChange={(e) => update({ promises_eyebrow: e.target.value })}
                  data-testid="promises-eyebrow-input"
                />
              </Field>
              <Field label="Section heading">
                <Input
                  value={form.promises_heading || ''}
                  onChange={(e) => update({ promises_heading: e.target.value })}
                  data-testid="promises-heading-input"
                />
              </Field>
            </div>
            <div className="space-y-3" data-testid="brand-promises-list">
              {(form.brand_promises || []).map((p, idx) => (
                <div
                  key={p.id || idx}
                  className="border border-neutral-200 rounded-2xl p-4 bg-neutral-50/40"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-sm font-semibold text-[#0F172A]">
                      Tile {idx + 1}
                      {p.title ? <span className="text-slate-500 font-normal"> — {p.title}</span> : null}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        if (window.confirm('Delete this tile?')) {
                          setForm((f) => ({
                            ...f,
                            brand_promises: f.brand_promises.filter((_, i) => i !== idx),
                          }));
                        }
                      }}
                      className="text-red-600 hover:bg-red-50 rounded p-1"
                      data-testid={`promise-delete-${idx}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <Field label="Title">
                      <Input
                        value={p.title}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            brand_promises: f.brand_promises.map((x, i) =>
                              i === idx ? { ...x, title: e.target.value } : x,
                            ),
                          }))
                        }
                      />
                    </Field>
                    <Field label="Lucide icon name" hint="e.g. Truck, FileText, Wallet, ShieldCheck">
                      <Input
                        value={p.icon}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            brand_promises: f.brand_promises.map((x, i) =>
                              i === idx ? { ...x, icon: e.target.value } : x,
                            ),
                          }))
                        }
                      />
                    </Field>
                    <Field label="Description" full>
                      <Textarea
                        rows={2}
                        value={p.desc}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            brand_promises: f.brand_promises.map((x, i) =>
                              i === idx ? { ...x, desc: e.target.value } : x,
                            ),
                          }))
                        }
                      />
                    </Field>
                    <Field label="Accent colour">
                      <Input
                        value={p.color}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            brand_promises: f.brand_promises.map((x, i) =>
                              i === idx ? { ...x, color: e.target.value } : x,
                            ),
                          }))
                        }
                      />
                    </Field>
                    <Field label="Background">
                      <Input
                        value={p.bg}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            brand_promises: f.brand_promises.map((x, i) =>
                              i === idx ? { ...x, bg: e.target.value } : x,
                            ),
                          }))
                        }
                      />
                    </Field>
                    <Field label="Tile image (optional)" full>
                      <ImageUpload
                        value={p.image}
                        onChange={(url) =>
                          setForm((f) => ({
                            ...f,
                            brand_promises: f.brand_promises.map((x, i) =>
                              i === idx ? { ...x, image: url } : x,
                            ),
                          }))
                        }
                        bg={p.bg || '#F8FAFC'}
                      />
                    </Field>
                  </div>
                </div>
              ))}
            </div>
            <Button
              type="button"
              onClick={() =>
                setForm((f) => ({
                  ...f,
                  brand_promises: [
                    ...(f.brand_promises || []),
                    { id: `bp-${Date.now()}`, icon: 'Truck', title: '', desc: '', color: '#0078D4', bg: '#DEECF9', image: '' },
                  ],
                }))
              }
              variant="outline"
              className="mt-4 rounded-full border-dashed border-2"
              data-testid="add-promise-btn"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Add a brand promise tile
            </Button>
            {(form.brand_promises || []).length === 0 && (
              <Button
                type="button"
                onClick={() =>
                  setForm((f) => ({
                    ...f,
                    brand_promises: [
                      { id: 'p1', icon: 'Truck', title: 'Scheduled delivery', desc: 'Weekly or on-demand routes across Muscat', color: '#0078D4', bg: '#DEECF9', image: '' },
                      { id: 'p2', icon: 'FileText', title: 'Tax invoices ready', desc: 'VAT-compliant invoices on every order', color: '#7C3AED', bg: '#EDE9FE', image: '' },
                      { id: 'p3', icon: 'Wallet', title: 'Trade pricing', desc: 'Volume discounts for verified businesses', color: '#10B981', bg: '#D1FAE5', image: '' },
                      { id: 'p4', icon: 'Calendar', title: 'Same-day dispatch', desc: 'Orders placed before 14:00 ship today', color: '#0EA5E9', bg: '#E0F2FE', image: '' },
                    ],
                  }))
                }
                variant="outline"
                className="mt-4 ml-2 rounded-full border-dashed border-2"
                data-testid="load-default-promises-btn"
              >
                <Download className="w-4 h-4 mr-1.5" /> Load default tiles to edit
              </Button>
            )}
          </Section>

          <Section
            title="Trade account CTA"
            desc="Background image for the dark CTA banner at the bottom of the homepage."
          >
            <Field label="CTA decorative image (optional)" full>
              <ImageUpload
                value={form.b2b_cta_image}
                onChange={(url) => update({ b2b_cta_image: url })}
                bg="#0F172A"
                testId="b2b-cta-image"
              />
            </Field>
          </Section>
          </>
          )}
          {tab === 'hero' && (
          <Section
            title="Hero — main panel"
            desc="The big blue panel on the left side of your homepage."
          >
            <div className="grid grid-cols-2 gap-4">
              <Field label="Eyebrow tag" full hint="Short label above the headline">
                <Input
                  value={form.hero_eyebrow}
                  onChange={(e) => update({ hero_eyebrow: e.target.value })}
                  data-testid="hero-eyebrow"
                />
              </Field>
              <Field label="Headline (start)">
                <Input
                  value={form.hero_title_lead}
                  onChange={(e) => update({ hero_title_lead: e.target.value })}
                  data-testid="hero-title-lead"
                />
              </Field>
              <Field label="Headline (highlight)" hint="Shown in light cyan colour">
                <Input
                  value={form.hero_title_highlight}
                  onChange={(e) => update({ hero_title_highlight: e.target.value })}
                  data-testid="hero-title-highlight"
                />
              </Field>
              <Field label="Subtitle / description" full>
                <Textarea
                  rows={3}
                  value={form.hero_subtitle}
                  onChange={(e) => update({ hero_subtitle: e.target.value })}
                  data-testid="hero-subtitle"
                />
              </Field>
              <Field label="Primary button label">
                <Input
                  value={form.hero_primary_cta_label}
                  onChange={(e) => update({ hero_primary_cta_label: e.target.value })}
                />
              </Field>
              <Field label="Primary button link" hint="Use #anchor, /path or https://…">
                <Input
                  value={form.hero_primary_cta_link}
                  onChange={(e) => update({ hero_primary_cta_link: e.target.value })}
                />
              </Field>
              <Field label="Secondary link label" hint="Leave empty to hide">
                <Input
                  value={form.hero_secondary_cta_label}
                  onChange={(e) => update({ hero_secondary_cta_label: e.target.value })}
                />
              </Field>
              <Field label="Secondary link target">
                <Input
                  value={form.hero_secondary_cta_link}
                  onChange={(e) => update({ hero_secondary_cta_link: e.target.value })}
                />
              </Field>
            </div>
          </Section>
          )}

          {/* Featured TV slideshow — multi-slide editor */}
          {tab === 'slideshow' && (
          <>
          <Section
            title="Display mode"
            desc="Choose how the featured slides appear on your homepage."
          >
            <div className="grid sm:grid-cols-2 gap-3">
              <label
                className={`cursor-pointer rounded-2xl border-2 p-4 transition ${
                  (form.featured_display_mode || 'slideshow') === 'slideshow'
                    ? 'border-[#0078D4] bg-[#EFF7FF] ring-2 ring-[#0078D4]/20'
                    : 'border-neutral-200 hover:border-neutral-300'
                }`}
                data-testid="display-mode-slideshow"
              >
                <input
                  type="radio"
                  name="display_mode"
                  value="slideshow"
                  checked={(form.featured_display_mode || 'slideshow') === 'slideshow'}
                  onChange={(e) => update({ featured_display_mode: e.target.value })}
                  className="sr-only"
                />
                <div className="font-display font-bold text-[#0F172A]">Slideshow card</div>
                <p className="text-xs text-neutral-500 mt-1 leading-relaxed">
                  Square hero card on the right side that auto-rotates with transitions.
                </p>
              </label>
              <label
                className={`cursor-pointer rounded-2xl border-2 p-4 transition ${
                  form.featured_display_mode === 'ticker'
                    ? 'border-[#0078D4] bg-[#EFF7FF] ring-2 ring-[#0078D4]/20'
                    : 'border-neutral-200 hover:border-neutral-300'
                }`}
                data-testid="display-mode-ticker"
              >
                <input
                  type="radio"
                  name="display_mode"
                  value="ticker"
                  checked={form.featured_display_mode === 'ticker'}
                  onChange={(e) => update({ featured_display_mode: e.target.value })}
                  className="sr-only"
                />
                <div className="font-display font-bold text-[#0F172A]">Full-width ticker</div>
                <p className="text-xs text-neutral-500 mt-1 leading-relaxed">
                  Continuous horizontal scroll band under the hero with speed & direction control.
                </p>
              </label>
            </div>
          </Section>

          {form.featured_display_mode === 'ticker' && (
            <Section
              title="Ticker controls"
              desc="Tune how the ticker moves. Changes apply instantly on the homepage."
            >
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Field label="Direction">
                  <select
                    value={form.ticker_direction || 'left'}
                    onChange={(e) => update({ ticker_direction: e.target.value })}
                    className="h-10 px-3 rounded-md border border-neutral-300 bg-white text-sm w-full focus:outline-none focus:ring-2 focus:ring-[#0078D4]"
                    data-testid="ticker-direction-select"
                  >
                    <option value="left">← Right to left (default)</option>
                    <option value="right">Left to right →</option>
                  </select>
                </Field>
                <Field label="Speed (px / second)">
                  <Input
                    type="number"
                    min={10}
                    max={400}
                    value={form.ticker_speed ?? 80}
                    onChange={(e) =>
                      update({ ticker_speed: Math.max(10, parseInt(e.target.value || '80', 10)) })
                    }
                    data-testid="ticker-speed-input"
                  />
                </Field>
                <Field label="Height (px)">
                  <Input
                    type="number"
                    min={80}
                    max={400}
                    value={form.ticker_height ?? 180}
                    onChange={(e) =>
                      update({ ticker_height: Math.max(80, parseInt(e.target.value || '180', 10)) })
                    }
                    data-testid="ticker-height-input"
                  />
                </Field>
                <Field label="Item width (px)">
                  <Input
                    type="number"
                    min={120}
                    max={600}
                    value={form.ticker_item_width ?? 320}
                    onChange={(e) =>
                      update({ ticker_item_width: Math.max(120, parseInt(e.target.value || '320', 10)) })
                    }
                    data-testid="ticker-item-width-input"
                  />
                </Field>
                <Field label="Margin top (px)">
                  <Input
                    type="number"
                    min={0}
                    max={200}
                    value={form.ticker_margin_top ?? 24}
                    onChange={(e) =>
                      update({ ticker_margin_top: Math.max(0, parseInt(e.target.value || '0', 10)) })
                    }
                    data-testid="ticker-margin-top-input"
                  />
                </Field>
                <Field label="Margin bottom (px)">
                  <Input
                    type="number"
                    min={0}
                    max={200}
                    value={form.ticker_margin_bottom ?? 24}
                    onChange={(e) =>
                      update({ ticker_margin_bottom: Math.max(0, parseInt(e.target.value || '0', 10)) })
                    }
                    data-testid="ticker-margin-bottom-input"
                  />
                </Field>
                <Field label="Background colour">
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={form.ticker_background || '#ffffff'}
                      onChange={(e) => update({ ticker_background: e.target.value })}
                      className="h-10 w-12 rounded-md border border-neutral-300 cursor-pointer"
                      data-testid="ticker-background-color"
                    />
                    <Input
                      type="text"
                      value={form.ticker_background || '#ffffff'}
                      onChange={(e) => update({ ticker_background: e.target.value })}
                      placeholder="#ffffff"
                      className="flex-1 font-mono"
                      data-testid="ticker-background-input"
                    />
                  </div>
                </Field>
              </div>
              <div className="mt-3 rounded-xl bg-amber-50 border border-amber-100 px-4 py-2.5 text-xs text-amber-800">
                Tip: 60-100 px/s feels calm; 150+ px/s is fast and attention-grabbing. Hover the ticker to pause it.
              </div>
            </Section>
          )}

          <Section
            title="Featured screen · Slideshow"
            desc="The big card on the right side of your homepage hero acts like a TV screen. Add as many slides as you want — they rotate automatically. Leave empty to use the single-slide fallback below."
          >
            <div className="flex items-center gap-3 flex-wrap mb-3">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-900 text-white text-xs font-semibold">
                <Tv className="w-3.5 h-3.5" />
                {(form.featured_slides || []).length} slide{(form.featured_slides || []).length === 1 ? '' : 's'}
              </div>
              <Field label="Auto-rotate every">
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={0}
                    max={60}
                    value={form.featured_autoplay_seconds ?? 5}
                    onChange={(e) =>
                      update({ featured_autoplay_seconds: Math.max(0, parseInt(e.target.value || '0', 10)) })
                    }
                    className="w-24"
                    data-testid="featured-autoplay-input"
                  />
                  <span className="text-sm text-neutral-500">seconds (0 = no autoplay)</span>
                </div>
              </Field>
              <Field label="Transition effect">
                <select
                  value={form.featured_transition || 'kenburns'}
                  onChange={(e) => update({ featured_transition: e.target.value })}
                  className="h-10 px-3 rounded-md border border-neutral-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0078D4] focus:border-transparent"
                  data-testid="featured-transition-select"
                >
                  <option value="none">(None) — instant cut</option>
                  <option value="kenburns">Ken Burns (cinematic zoom)</option>
                  <option value="fade">Cross-fade</option>
                  <option value="slide">Slide-in from right</option>
                  <option value="zoom">Zoom &amp; fade</option>
                  <option value="flip">3D flip</option>
                  <option value="blur">Blur dissolve</option>
                </select>
              </Field>
            </div>

            {(form.featured_slides || []).length === 0 && (
              <div className="rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50 p-8 text-center text-sm text-slate-500">
                No slides yet. The storefront will fall back to the single-slide card below.
              </div>
            )}

            <div className="space-y-4" data-testid="featured-slides-list">
              {(form.featured_slides || []).map((s, idx) => (
                <div
                  key={s.id || idx}
                  className="border border-neutral-200 rounded-2xl p-5 bg-neutral-50/40"
                  data-testid={`featured-slide-row-${idx}`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-white text-sm shadow-sm"
                        style={{ background: s.color || '#0EA5E9' }}
                      >
                        {idx + 1}
                      </div>
                      <div className="font-semibold text-[#0F172A] text-sm truncate max-w-[260px]">
                        {s.title || <span className="text-neutral-400 italic">Untitled slide</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => moveSlide(idx, -1)}
                        disabled={idx === 0}
                        className="w-8 h-8 rounded-md hover:bg-slate-200 disabled:opacity-30 flex items-center justify-center"
                        aria-label="Move up"
                        data-testid={`slide-up-${idx}`}
                      >
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveSlide(idx, 1)}
                        disabled={idx === form.featured_slides.length - 1}
                        className="w-8 h-8 rounded-md hover:bg-slate-200 disabled:opacity-30 flex items-center justify-center"
                        aria-label="Move down"
                        data-testid={`slide-down-${idx}`}
                      >
                        <ChevronDown className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm('Delete this slide?')) removeSlide(idx);
                        }}
                        className="w-8 h-8 rounded-md hover:bg-red-50 text-red-600 flex items-center justify-center"
                        aria-label="Delete slide"
                        data-testid={`slide-delete-${idx}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Eyebrow / label">
                      <Input
                        value={s.eyebrow}
                        onChange={(e) => updateSlide(idx, { eyebrow: e.target.value })}
                        data-testid={`slide-eyebrow-${idx}`}
                      />
                    </Field>
                    <Field label="Badge" hint='e.g. "Trade pack", "New"'>
                      <Input
                        value={s.badge}
                        onChange={(e) => updateSlide(idx, { badge: e.target.value })}
                        data-testid={`slide-badge-${idx}`}
                      />
                    </Field>
                    <Field label="Title" full>
                      <Input
                        value={s.title}
                        onChange={(e) => updateSlide(idx, { title: e.target.value })}
                        data-testid={`slide-title-${idx}`}
                      />
                    </Field>
                    <Field label="Price label">
                      <Input
                        value={s.price_label}
                        onChange={(e) => updateSlide(idx, { price_label: e.target.value })}
                      />
                    </Field>
                    <Field label="Price value">
                      <Input
                        value={s.price}
                        onChange={(e) => updateSlide(idx, { price: e.target.value })}
                      />
                    </Field>
                    <Field label="CTA text">
                      <Input
                        value={s.cta_label}
                        onChange={(e) => updateSlide(idx, { cta_label: e.target.value })}
                      />
                    </Field>
                    <Field label="Click link" hint="e.g. /category/water">
                      <Input
                        value={s.link}
                        onChange={(e) => updateSlide(idx, { link: e.target.value })}
                        data-testid={`slide-link-${idx}`}
                      />
                    </Field>
                    <Field label="Accent colour">
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={s.color}
                          onChange={(e) => updateSlide(idx, { color: e.target.value })}
                          className="h-10 w-12 rounded-md border border-neutral-300 cursor-pointer"
                        />
                        <Input
                          value={s.color}
                          onChange={(e) => updateSlide(idx, { color: e.target.value })}
                          className="font-mono text-sm"
                        />
                      </div>
                    </Field>
                    <Field label="Background colour">
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={s.bg}
                          onChange={(e) => updateSlide(idx, { bg: e.target.value })}
                          className="h-10 w-12 rounded-md border border-neutral-300 cursor-pointer"
                        />
                        <Input
                          value={s.bg}
                          onChange={(e) => updateSlide(idx, { bg: e.target.value })}
                          className="font-mono text-sm"
                        />
                      </div>
                    </Field>
                    <Field label="Text colour" hint="Colour of the title shown over the image">
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={s.text_color || '#FFFFFF'}
                          onChange={(e) => updateSlide(idx, { text_color: e.target.value })}
                          className="h-10 w-12 rounded-md border border-neutral-300 cursor-pointer"
                          data-testid={`slide-text-color-${idx}`}
                        />
                        <Input
                          value={s.text_color || '#FFFFFF'}
                          onChange={(e) => updateSlide(idx, { text_color: e.target.value })}
                          className="font-mono text-sm"
                        />
                      </div>
                    </Field>
                    <Field label="Image" full hint="Leave empty to use the linked division's image">
                      <ImageUpload
                        value={s.image}
                        onChange={(url) => updateSlide(idx, { image: url })}
                        bg={s.bg}
                        testId={`slide-image-${idx}`}
                      />
                    </Field>
                  </div>
                </div>
              ))}
            </div>

            <Button
              type="button"
              onClick={addSlide}
              variant="outline"
              className="mt-4 rounded-full border-dashed border-2"
              data-testid="add-slide-btn"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Add a slide
            </Button>
          </Section>

          {/* Featured card (legacy single-slide fallback) */}
          <Section
            title="Single-slide fallback"
            desc="These values are used only when the slideshow above has no slides. Safe to leave as-is."
          >
            <div className="grid grid-cols-2 gap-4">
              <Field label="Eyebrow / division label">
                <Input
                  value={form.featured_eyebrow}
                  onChange={(e) => update({ featured_eyebrow: e.target.value })}
                />
              </Field>
              <Field label="Top-right badge" hint='Small pill, e.g. "Trade pack"'>
                <Input
                  value={form.featured_badge}
                  onChange={(e) => update({ featured_badge: e.target.value })}
                />
              </Field>
              <Field label="Product title" full>
                <Input
                  value={form.featured_title}
                  onChange={(e) => update({ featured_title: e.target.value })}
                />
              </Field>
              <Field label="Price label" hint='e.g. "From", "Bulk price"'>
                <Input
                  value={form.featured_price_label}
                  onChange={(e) => update({ featured_price_label: e.target.value })}
                />
              </Field>
              <Field label="Price value">
                <Input
                  value={form.featured_price}
                  onChange={(e) => update({ featured_price: e.target.value })}
                />
              </Field>
              <Field label="Card link" full hint="Where clicking the card takes the user">
                <Input
                  value={form.featured_link}
                  onChange={(e) => update({ featured_link: e.target.value })}
                  placeholder="/category/water"
                />
              </Field>
              <Field label="CTA text">
                <Input
                  value={form.featured_cta_label}
                  onChange={(e) => update({ featured_cta_label: e.target.value })}
                />
              </Field>
              <Field label="Accent colour" hint="Background gradient + CTA tint">
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={form.featured_color}
                    onChange={(e) => update({ featured_color: e.target.value })}
                    className="h-10 w-12 rounded-md border border-neutral-300 cursor-pointer"
                  />
                  <Input
                    value={form.featured_color}
                    onChange={(e) => update({ featured_color: e.target.value })}
                    className="font-mono text-sm"
                  />
                </div>
              </Field>
              <Field label="Background colour" hint="Light tone for the card backdrop">
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={form.featured_bg}
                    onChange={(e) => update({ featured_bg: e.target.value })}
                    className="h-10 w-12 rounded-md border border-neutral-300 cursor-pointer"
                  />
                  <Input
                    value={form.featured_bg}
                    onChange={(e) => update({ featured_bg: e.target.value })}
                    className="font-mono text-sm"
                  />
                </div>
              </Field>
              <Field
                label="Featured image"
                full
                hint="Leave empty to use the image of the linked division"
              >
                <ImageUpload
                  value={form.featured_image}
                  onChange={(url) => update({ featured_image: url })}
                  bg={form.featured_bg}
                  testId="featured-image"
                />
              </Field>
            </div>
          </Section>
          </>
          )}

          {/* Stats — part of Hero tab */}
          {tab === 'hero' && (
          <Section
            title="Stat tiles"
            desc="The 4 small tiles shown below the hero (e.g. 500+ Trade partners)."
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {form.stats.map((s, i) => (
                <div key={i} className="p-4 bg-neutral-50 rounded-lg space-y-3">
                  <div className="text-[11px] font-bold tracking-widest text-neutral-500 uppercase">
                    Tile {i + 1}
                  </div>
                  <Field label="Value">
                    <Input
                      value={s.value}
                      onChange={(e) => updateStat(i, { value: e.target.value })}
                      data-testid={`stat-${i}-value`}
                    />
                  </Field>
                  <Field label="Label">
                    <Input
                      value={s.label}
                      onChange={(e) => updateStat(i, { label: e.target.value })}
                      data-testid={`stat-${i}-label`}
                    />
                  </Field>
                </div>
              ))}
            </div>
          </Section>
          )}

          {/* Live pill — part of Hero tab */}
          {tab === 'hero' && (
          <Section
            title="Top pill"
            desc="The small green dot label at the very top of your homepage."
          >
            <Field label="Pill text">
              <Input
                value={form.live_pill}
                onChange={(e) => update({ live_pill: e.target.value })}
                data-testid="live-pill"
              />
            </Field>
          </Section>
          )}

          {/* Catalogue section — part of Sections tab */}
          {tab === 'sections' && (
          <>
          <Section
            title="Catalogue overview section"
            desc="The heading block above the four division cards."
          >
            <div className="grid grid-cols-2 gap-4">
              <Field label="Eyebrow">
                <Input value={form.categories_eyebrow} onChange={(e) => update({ categories_eyebrow: e.target.value })} />
              </Field>
              <Field label="Heading">
                <Input value={form.categories_heading} onChange={(e) => update({ categories_heading: e.target.value })} />
              </Field>
              <Field label="Description" full>
                <Textarea rows={2} value={form.categories_desc} onChange={(e) => update({ categories_desc: e.target.value })} />
              </Field>
            </div>
          </Section>

          {/* Bestsellers section */}
          <Section
            title="Bestsellers section"
            desc="The heading above the Top moving SKUs grid."
          >
            <div className="grid grid-cols-2 gap-4">
              <Field label="Eyebrow">
                <Input
                  value={form.featured_section_eyebrow}
                  onChange={(e) => update({ featured_section_eyebrow: e.target.value })}
                />
              </Field>
              <Field label="Heading">
                <Input
                  value={form.featured_section_heading}
                  onChange={(e) => update({ featured_section_heading: e.target.value })}
                />
              </Field>
            </div>
          </Section>

          {/* Why mrpco section */}
          <Section
            title="Why-us section (Brand promises)"
            desc="The heading above the four promise cards (Delivery, VAT invoices, Trade pricing, Dispatch)."
          >
            <div className="grid grid-cols-2 gap-4">
              <Field label="Eyebrow">
                <Input
                  value={form.promises_eyebrow}
                  onChange={(e) => update({ promises_eyebrow: e.target.value })}
                />
              </Field>
              <Field label="Heading">
                <Input
                  value={form.promises_heading}
                  onChange={(e) => update({ promises_heading: e.target.value })}
                />
              </Field>
            </div>
          </Section>

          {/* B2B CTA section */}
          <Section
            title="Trade account CTA section"
            desc="The dark banner inviting retailers to open a trade account."
          >
            <div className="grid grid-cols-2 gap-4">
              <Field label="Eyebrow">
                <Input value={form.b2b_eyebrow} onChange={(e) => update({ b2b_eyebrow: e.target.value })} />
              </Field>
              <Field label="Heading" full>
                <Input value={form.b2b_heading} onChange={(e) => update({ b2b_heading: e.target.value })} />
              </Field>
              <Field label="Description" full>
                <Textarea rows={3} value={form.b2b_desc} onChange={(e) => update({ b2b_desc: e.target.value })} />
              </Field>
            </div>
          </Section>
          </>
          )}

          {/* Footer */}
          {tab === 'footer' && (
          <Section
            title="Footer"
            desc="Company info shown at the bottom of every page."
          >
            <div className="grid grid-cols-2 gap-4">
              <Field label="Brand description" full>
                <Textarea
                  rows={3}
                  value={form.footer_description}
                  onChange={(e) => update({ footer_description: e.target.value })}
                  data-testid="footer-description"
                />
              </Field>
              <Field label="Address">
                <Input value={form.footer_address} onChange={(e) => update({ footer_address: e.target.value })} />
              </Field>
              <Field label="Phone">
                <Input value={form.footer_phone} onChange={(e) => update({ footer_phone: e.target.value })} />
              </Field>
              <Field label="Email">
                <Input value={form.footer_email} onChange={(e) => update({ footer_email: e.target.value })} />
              </Field>
              <Field label="Copyright line">
                <Input value={form.footer_copyright} onChange={(e) => update({ footer_copyright: e.target.value })} />
              </Field>
            </div>
          </Section>
          )}

          {/* Typography (storefront / driver portal fonts & sizes) */}
          {tab === 'typography' && (
          <Section
            title="Typography"
            desc="Pick the fonts, size scale, and letter spacing for your storefront. Changes apply instantly in the live preview — Save to roll them out for every customer."
          >
            <TypographyPanel form={form} update={update} />
          </Section>
          )}

          {/* Documents (PDF customization) */}
          {tab === 'documents' && (
          <Section
            title="PDF documents"
            desc="Customize every generated PDF — invoices, receipts, admin reports and driver manifests. Save your changes, then preview to see them rendered."
          >
            <DocumentsPanel form={form} update={update} authHeaders={authHeaders} />
          </Section>
          )}

          {/* Notifications (Email + SMS) */}
          {tab === 'notifications' && (
            <NotificationsPanel
              form={form}
              update={update}
              authHeaders={authHeaders}
              apiBase={API}
            />
          )}

          {/* Warehouse — super admin only (Branch FEFO WMS controls) */}
          {tab === 'warehouse' && isSuper && (
            <Section
              title="Branch FEFO Warehouse System"
              desc="Controls for the branch-side warehouse automation. Turn these on to sync branch receipts with your central WMS in real time."
            >
              <div className="rounded-xl border border-neutral-200 bg-white p-5 mb-4 flex flex-wrap items-center justify-between gap-4">
                <div className="flex-1 min-w-[16rem]">
                  <div className="text-sm font-bold text-neutral-900">
                    Auto-ASN on Order Receipt
                  </div>
                  <div className="text-xs text-neutral-500 mt-0.5 leading-relaxed">
                    When ON, confirming a branch receipt automatically generates an Inbound ASN for the branch's own warehouse — carrying over pallet IDs, quantities, production/expiry dates and the Vehicle #. When OFF, branch users continue to raise ASNs manually.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => update({ auto_asn_on_receipt: !form.auto_asn_on_receipt })}
                  data-testid="auto-asn-toggle"
                  aria-pressed={!!form.auto_asn_on_receipt}
                  className={`relative inline-flex h-7 w-14 shrink-0 rounded-full transition ${
                    form.auto_asn_on_receipt ? 'bg-emerald-500' : 'bg-neutral-300'
                  } cursor-pointer`}
                >
                  <span
                    className={`inline-block h-5 w-5 rounded-full bg-white shadow-md transform transition my-1 ${
                      form.auto_asn_on_receipt ? 'translate-x-8' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              <p className="text-[11px] text-neutral-500">
                Requires each branch to have a <b>Warehouse</b> assigned in the Branch Accounts admin. Branches without a warehouse binding are silently skipped so the toggle is safe to enable partially.
              </p>
            </Section>
          )}

          {/* Maintenance — super admin only */}
          {tab === 'maintenance' && isSuper && (
            <Section
              title="Maintenance mode"
              hint="Temporarily take the public storefront offline (admin still works)"
            >
              <div className="rounded-xl border border-amber-200 bg-amber-50/40 p-5 mb-5 flex items-start gap-3">
                <Wrench className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
                <div className="text-sm text-amber-900 leading-relaxed">
                  When enabled, all storefront visitors see a polished &ldquo;We&apos;ll be back shortly&rdquo; page.
                  Admin URLs (<code className="px-1 py-0.5 rounded bg-amber-100 text-[12px]">/admin/...</code>) and
                  driver app (<code className="px-1 py-0.5 rounded bg-amber-100 text-[12px]">/driver/...</code>)
                  continue to work so you can flip it back off.
                </div>
              </div>

              <MaintenanceToolsIndexBlock />
              <div className="rounded-xl border border-neutral-200 bg-white p-5 mb-4 flex items-center justify-between">
                <div>
                  <div className="text-sm font-bold text-neutral-900">
                    Storefront status: <span className={maint.enabled ? 'text-rose-600' : 'text-emerald-600'}>
                      {maint.enabled ? 'OFFLINE — maintenance mode' : 'LIVE'}
                    </span>
                  </div>
                  <div className="text-xs text-neutral-500 mt-0.5">
                    {maint.enabled ? 'Visitors are seeing the maintenance screen.' : 'Visitors see the normal store.'}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => saveMaintenance({ enabled: !maint.enabled })}
                  disabled={maintSaving}
                  data-testid="maintenance-toggle"
                  className={`relative inline-flex h-7 w-14 shrink-0 rounded-full transition ${
                    maint.enabled ? 'bg-rose-500' : 'bg-neutral-300'
                  } ${maintSaving ? 'opacity-60 cursor-wait' : 'cursor-pointer'}`}
                  aria-pressed={maint.enabled}
                  aria-label="Toggle storefront maintenance mode"
                >
                  <span
                    className={`inline-block h-5 w-5 rounded-full bg-white shadow-md transform transition my-1 ${
                      maint.enabled ? 'translate-x-8' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <label className="block text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1.5">
                Custom message <span className="text-neutral-400 font-normal lowercase tracking-normal">— optional</span>
              </label>
              <Textarea
                value={maint.message}
                onChange={(e) => setMaint({ ...maint, message: e.target.value })}
                placeholder="The store is offline for scheduled maintenance. We'll be back in 30 minutes."
                rows={3}
                className="font-mono text-sm"
                data-testid="maintenance-message-input"
              />
              <p className="text-[11px] text-neutral-500 mt-2">
                Shown to visitors on the maintenance screen. Leave blank to use the default copy.
              </p>
              <div className="mt-4 flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => saveMaintenance({ message: maint.message })}
                  disabled={maintSaving}
                  data-testid="maintenance-save-message"
                >
                  {maintSaving ? 'Saving…' : 'Save message'}
                </Button>
                <a
                  href="/"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-[#0078D4] hover:text-[#005A9E] px-3 py-2 rounded-md hover:bg-[#0078D4]/5"
                >
                  <ExternalLink className="w-4 h-4" /> Preview storefront
                </a>
              </div>

              {/* Branch portal — INDEPENDENT lock (added 2026-07-19) */}
              <div className="mt-8 pt-6 border-t border-neutral-200">
                <div className="text-sm font-bold text-neutral-900 mb-1">Branch portal maintenance</div>
                <div className="text-xs text-neutral-500 mb-4">
                  Independent of the storefront lock. Locking one has zero effect on the other.
                  Admin (<code className="px-1 py-0.5 rounded bg-neutral-100 text-[11px]">/admin/...</code>) and driver
                  (<code className="px-1 py-0.5 rounded bg-neutral-100 text-[11px]">/driver/...</code>) always stay online.
                </div>

                <div className="rounded-xl border border-neutral-200 bg-white p-5 mb-4 flex items-center justify-between">
                  <div>
                    <div className="text-sm font-bold text-neutral-900">
                      Branch portal status: <span className={maint.branch_enabled ? 'text-rose-600' : 'text-emerald-600'}>
                        {maint.branch_enabled ? 'OFFLINE — maintenance mode' : 'LIVE'}
                      </span>
                    </div>
                    <div className="text-xs text-neutral-500 mt-0.5">
                      {maint.branch_enabled
                        ? 'Branch staff on /branch are seeing the maintenance screen.'
                        : 'Branch staff see the normal /branch portal.'}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => saveMaintenance({ branch_enabled: !maint.branch_enabled })}
                    disabled={maintSaving}
                    data-testid="branch-maintenance-toggle"
                    className={`relative inline-flex h-7 w-14 shrink-0 rounded-full transition ${
                      maint.branch_enabled ? 'bg-rose-500' : 'bg-neutral-300'
                    } ${maintSaving ? 'opacity-60 cursor-wait' : 'cursor-pointer'}`}
                    aria-pressed={maint.branch_enabled}
                    aria-label="Toggle branch portal maintenance mode"
                  >
                    <span
                      className={`inline-block h-5 w-5 rounded-full bg-white shadow-md transform transition my-1 ${
                        maint.branch_enabled ? 'translate-x-8' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                <label className="block text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1.5">
                  Branch custom message <span className="text-neutral-400 font-normal lowercase tracking-normal">— optional</span>
                </label>
                <Textarea
                  value={maint.branch_message}
                  onChange={(e) => setMaint({ ...maint, branch_message: e.target.value })}
                  placeholder="The branch portal is temporarily offline. We'll be back in 30 minutes."
                  rows={3}
                  className="font-mono text-sm"
                  data-testid="branch-maintenance-message-input"
                />
                <p className="text-[11px] text-neutral-500 mt-2">
                  Shown to branch staff on the maintenance screen. Leave blank to use the default copy.
                </p>
                <div className="mt-4 flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => saveMaintenance({ branch_message: maint.branch_message })}
                    disabled={maintSaving}
                    data-testid="branch-maintenance-save-message"
                  >
                    {maintSaving ? 'Saving…' : 'Save branch message'}
                  </Button>
                  <a
                    href="/branch/login"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-[#0078D4] hover:text-[#005A9E] px-3 py-2 rounded-md hover:bg-[#0078D4]/5"
                  >
                    <ExternalLink className="w-4 h-4" /> Preview branch portal
                  </a>
                </div>
              </div>

              {/* Data tools — super admin only. Feb 2026 */}
              <ReconcileAllocationsBlock API={API} authHeaders={authHeaders} />
              <ReconcilePlacementsBlock API={API} authHeaders={authHeaders} />
              <RecalcOpenDispatchBlock API={API} authHeaders={authHeaders} />
              {/* Feb 2026 (post-IFG0012) — three recovery blocks */}
              <QtyVsShippedStateReconcilerBlock API={API} authHeaders={authHeaders} />
              <BatchLedgerNetReconcilerBlock API={API} authHeaders={authHeaders} />
              <NegativeLedgerBucketsScanBlock API={API} authHeaders={authHeaders} />
              <SurgicalPlacementTaskCleanupBlock API={API} authHeaders={authHeaders} />
              <CloseStalePickTasksBlock API={API} authHeaders={authHeaders} />
              <CategoryLocationRulesBlock API={API} authHeaders={authHeaders} />
              <LocationRuleViolationsReportBlock API={API} authHeaders={authHeaders} />
              {/* Feb 2026 — Three dispatch-lock recovery tools, Dry run → Commit */}
              <ReleaseStaleDispatchLocksToolBlock API={API} authHeaders={authHeaders} />
              <RestoreOrderReservationsToolBlock API={API} authHeaders={authHeaders} />
              <AllocateShortfallToolBlock API={API} authHeaders={authHeaders} />
              {/* Feb 2026 — generic stale/false shipped flag clearer (super-admin) */}
              <ClearStaleShippedFlagBlock API={API} authHeaders={authHeaders} />
              {/* Feb 2026 — Fix dispatch lock quantities (super-admin, Preview → Apply) */}
              <FixDispatchLockQuantitiesBlock API={API} authHeaders={authHeaders} />
              {/* Feb 2026 — Archived one-off tools, collapsed by default. */}
              <ArchivedOneOffToolsSection>
                <Mr495CleanupBlock API={API} authHeaders={authHeaders} />
                <Phase2RepairBlock API={API} authHeaders={authHeaders} />
                <IFG0012ASN149ReversalBlock API={API} authHeaders={authHeaders} />
                <Mr785L12CorrectionBlock API={API} authHeaders={authHeaders} />
              </ArchivedOneOffToolsSection>
            </Section>
          )}
        </div>

        {/* Sticky save bar */}
        {dirty && (
          <div className="fixed bottom-0 left-0 md:left-64 right-0 z-40 bg-white border-t border-neutral-200 px-4 md:px-6 py-3 md:py-4 shadow-lg pb-[max(env(safe-area-inset-bottom,0),12px)] md:pb-4">
            <div className="max-w-4xl flex items-center justify-between gap-3">
              <div className="hidden md:block text-sm text-neutral-600">
                <span className="font-semibold text-[#0F172A]">Unsaved changes</span> —
                review and click save to push to the storefront.
              </div>
              <div className="md:hidden text-[11px] font-bold text-[#0F172A] shrink-0">
                Unsaved
              </div>
              <div className="flex items-center gap-2 flex-1 md:flex-none justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={revert}
                  className="h-12 md:h-10 rounded-full flex-1 md:flex-none"
                  data-testid="revert-settings"
                >
                  <RotateCcw className="w-4 h-4 mr-1.5" /> Revert
                </Button>
                <Button
                  type="button"
                  onClick={save}
                  disabled={saving}
                  className="h-12 md:h-10 rounded-full bg-[#0078D4] hover:bg-[#005A9E] text-white font-semibold flex-1 md:flex-none"
                  data-testid="save-settings"
                >
                  <Save className="w-4 h-4 mr-1.5" />
                  {saving ? 'Saving…' : 'Save changes'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminSettings;

// Feb 2026 — Super-admin only "Reconcile Placement Quantities" block.
// Wraps the shared /admin/data-tools/reconcile-placement-quantities
// endpoint so ops can heal drift without shelling into a pod. Two
// buttons: Preview (dry-run) and Apply (commit). Both stream a
// spinner while the request is in flight and display the summary
// stats returned by the server on completion.
const ReconcilePlacementsBlock = ({ API, authHeaders }) => {
  const [running, setRunning] = React.useState(false);
  const [mode, setMode] = React.useState('');       // '' | 'preview' | 'commit'
  const [result, setResult] = React.useState(null); // stats or null
  const [error, setError] = React.useState('');
  const [confirmOpen, setConfirmOpen] = React.useState(false);

  const trigger = async (commit) => {
    setRunning(true);
    setMode(commit ? 'commit' : 'preview');
    setError('');
    setResult(null);
    setConfirmOpen(false);
    try {
      const r = await axios.post(
        `${API}/admin/data-tools/reconcile-placement-quantities`,
        { commit, verbose: false },
        { headers: authHeaders },
      );
      setResult(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Reconciliation failed.');
    } finally {
      setRunning(false);
    }
  };

  const summary = result || {};
  const totalChanged = summary.placements_touched || 0;
  const totalRemoved = summary.qty_removed_total || 0;

  return (
    <div
      className="mt-8 pt-6 border-t border-neutral-200"
      data-testid="data-tools-reconcile-placements-block"
    >
      <div className="flex items-center gap-2 mb-1">
        <Database className="w-4 h-4 text-[#0078D4]" />
        <div className="text-sm font-bold text-neutral-900">
          Reconcile Placement Quantities
        </div>
      </div>
      <div className="text-xs text-neutral-500 mb-4 leading-relaxed">
        Walks every non-stage warehouse placement grouped by
        <code className="mx-1 px-1 py-0.5 rounded bg-neutral-100 text-[11px]">product · production_date</code>
        and reconciles the group sum to the authoritative
        <code className="mx-1 px-1 py-0.5 rounded bg-neutral-100 text-[11px]">products.batches[].qty</code>.
        Inflated groups are scaled proportionally; groups whose product
        HAS a ledger but no matching batch (or a zero-qty match) are
        zeroed. Products with no batch ledger at all are skipped and
        reported — never zeroed. ⚠️ This tool changes physical stock
        quantities; for wrong "Allocated" numbers use Reconcile
        Allocations above instead. Under-placement is left alone.
      </div>

      <div
        className="flex flex-wrap items-center gap-2 mb-3"
        data-testid="reconcile-placements-actions"
      >
        <Button
          type="button"
          variant="outline"
          onClick={() => trigger(false)}
          disabled={running}
          data-testid="reconcile-placements-preview-btn"
        >
          {running && mode === 'preview' ? (
            <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
          ) : (
            <Eye className="w-4 h-4 mr-1.5" />
          )}
          {running && mode === 'preview' ? 'Scanning…' : 'Preview (dry-run)'}
        </Button>
        <Button
          type="button"
          onClick={() => setConfirmOpen(true)}
          disabled={running}
          className="bg-rose-600 hover:bg-rose-700 text-white"
          data-testid="reconcile-placements-apply-btn"
        >
          {running && mode === 'commit' ? (
            <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
          ) : (
            <Wrench className="w-4 h-4 mr-1.5" />
          )}
          {running && mode === 'commit' ? 'Reconciling…' : 'Reconcile Placement Quantities'}
        </Button>
      </div>

      {confirmOpen && (
        <div
          className="rounded-xl border border-rose-200 bg-rose-50 p-4 mb-3"
          data-testid="reconcile-placements-confirm"
        >
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-rose-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="text-sm font-bold text-rose-900">
                Apply reconciliation to the production database?
              </div>
              <div className="text-xs text-rose-800 mt-0.5">
                This writes directly to every affected warehouse_placement row.
                We recommend running Preview first to confirm the scope.
              </div>
              <div className="mt-3 flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmOpen(false)}
                  data-testid="reconcile-placements-cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => trigger(true)}
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                  data-testid="reconcile-placements-confirm-btn"
                >
                  Yes, apply
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {running && (
        <div
          className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 mb-3 flex items-center gap-2 text-sm text-blue-900"
          data-testid="reconcile-placements-progress"
        >
          <Loader2 className="w-4 h-4 animate-spin" />
          Scanning placements and reconciling to batch totals — this can take a moment on large datasets…
        </div>
      )}

      {error && (
        <div
          className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 mb-3 text-sm text-rose-900"
          data-testid="reconcile-placements-error"
        >
          {error}
        </div>
      )}

      {result && !running && (
        <div
          className="rounded-xl border border-emerald-200 bg-emerald-50 p-4"
          data-testid="reconcile-placements-result"
        >
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            <div className="text-sm font-bold text-emerald-900">
              {result.commit ? 'Reconciliation applied' : 'Preview complete (no writes)'}
            </div>
            <div className="text-[11px] text-emerald-700 ml-auto tabular-nums">
              {result.duration_ms ?? 0} ms
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
            <StatCard label="Placements corrected" value={totalChanged} testid="reconcile-stat-placements-touched" />
            <StatCard label="Excess qty removed" value={totalRemoved} testid="reconcile-stat-qty-removed" />
            <StatCard label="Groups scanned" value={result.groups_scanned || 0} testid="reconcile-stat-groups-scanned" />
            <StatCard label="Groups healthy" value={result.groups_ok || 0} testid="reconcile-stat-groups-ok" />
            <StatCard label="Groups scaled" value={result.groups_inflated_scaled || 0} testid="reconcile-stat-groups-scaled" />
            <StatCard label="Groups wiped (no batch)" value={result.groups_zero_batch_wiped || 0} testid="reconcile-stat-groups-wiped" />
            <StatCard label="Groups skipped (no ledger)" value={result.groups_skipped_no_batch_ledger || 0} testid="reconcile-stat-groups-skipped" />
          </div>
          {result.finished_at && (
            <div className="mt-3 text-[11px] text-emerald-700 tabular-nums">
              Finished {formatDateTime(result.finished_at)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, testid }) => (
  <div
    className="rounded-lg border border-emerald-200 bg-white px-3 py-2"
    data-testid={testid}
  >
    <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">{label}</div>
    <div className="text-lg font-bold text-neutral-900 tabular-nums">{value}</div>
  </div>
);

// Aug 2026 — Super-admin only "Reconcile Allocations" block (ticket
// #230339). Wraps POST /admin/warehouses/reconcile-allocations — the
// SAFE counter reconcile that rebuilds every placement's allocated_qty
// from live open orders. It NEVER touches physical quantities, so it
// is the correct tool for inflated-Allocated drift. Preview posts
// dry_run=true (zero writes); Apply confirms, then posts dry_run=false
// with auto_replan off (pick lists are never rewritten silently).
const ReconcileAllocationsBlock = ({ API, authHeaders }) => {
  const [running, setRunning] = React.useState(false);
  const [mode, setMode] = React.useState('');       // '' | 'preview' | 'commit'
  const [result, setResult] = React.useState(null); // summary or null
  const [error, setError] = React.useState('');
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  // Feb 2026 (v3) — Optional pallet-ID scope. Comma-separated input;
  // when non-empty, both Preview and Reconcile only check/correct
  // placements with those pallet IDs.
  const [palletIdsText, setPalletIdsText] = React.useState('');

  const parsedPalletIds = React.useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const raw of (palletIdsText || '').split(/[\n,]+/)) {
      const s = (raw || '').trim();
      if (!s || seen.has(s)) continue;
      seen.add(s);
      out.push(s);
    }
    return out;
  }, [palletIdsText]);

  const trigger = async (dryRun) => {
    setRunning(true);
    setMode(dryRun ? 'preview' : 'commit');
    setError('');
    setResult(null);
    setConfirmOpen(false);
    try {
      const body = parsedPalletIds.length > 0 ? { pallet_ids: parsedPalletIds } : {};
      const r = await axios.post(
        `${API}/admin/warehouses/reconcile-allocations?dry_run=${dryRun}&auto_replan=false`,
        body,
        { headers: authHeaders },
      );
      setResult(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Allocation reconcile failed.');
    } finally {
      setRunning(false);
    }
  };

  const corrected = result?.corrected || 0;
  const scanned = result?.scanned || 0;
  const replanCandidates = (result?.candidate_replan_orders || []).length;
  const corrections = Array.isArray(result?.corrections) ? result.corrections : [];

  return (
    <div
      className="mt-8 pt-6 border-t border-neutral-200"
      data-testid="data-tools-reconcile-allocations-block"
    >
      <div className="flex items-center gap-2 mb-1">
        <Database className="w-4 h-4 text-emerald-600" />
        <div className="text-sm font-bold text-neutral-900">
          Reconcile Allocations (safe — fixes the Allocated counters)
        </div>
      </div>
      <div className="text-xs text-neutral-500 mb-4 leading-relaxed">
        Rebuilds every placement's <code className="mx-1 px-1 py-0.5 rounded bg-neutral-100 text-[11px]">allocated_qty</code>
        from the live set of open orders — the fix for inflated
        "Allocated" figures. Physical stock quantities are never
        changed. Run Preview first, review, then Apply.
      </div>

      <div className="mb-3" data-testid="alloc-reconcile-pallet-ids-wrap">
        <label
          className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 block mb-1"
          htmlFor="alloc-reconcile-pallet-ids"
        >
          Pallet IDs (optional, comma separated)
        </label>
        <Input
          id="alloc-reconcile-pallet-ids"
          value={palletIdsText}
          onChange={(e) => setPalletIdsText(e.target.value)}
          placeholder="e.g. FA22095, FA22101, FA22233"
          className="font-mono text-xs"
          data-testid="alloc-reconcile-pallet-ids"
        />
        <div className="mt-1 text-[11px] text-neutral-500">
          {parsedPalletIds.length === 0
            ? 'Leave blank to scan EVERY placement.'
            : <>Will scan <strong>{parsedPalletIds.length}</strong> pallet id{parsedPalletIds.length === 1 ? '' : 's'} only.</>}
        </div>
      </div>

      <div
        className="flex flex-wrap items-center gap-2 mb-3"
        data-testid="alloc-reconcile-actions"
      >
        <Button
          type="button"
          variant="outline"
          onClick={() => trigger(true)}
          disabled={running}
          data-testid="alloc-reconcile-preview-btn"
        >
          {running && mode === 'preview' ? (
            <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
          ) : (
            <Eye className="w-4 h-4 mr-1.5" />
          )}
          {running && mode === 'preview' ? 'Scanning…' : 'Preview (dry-run)'}
        </Button>
        <Button
          type="button"
          onClick={() => setConfirmOpen(true)}
          disabled={running}
          className="bg-[#0078D4] hover:bg-[#006aBE] text-white"
          data-testid="alloc-reconcile-apply-btn"
        >
          {running && mode === 'commit' ? (
            <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
          ) : (
            <Wrench className="w-4 h-4 mr-1.5" />
          )}
          {running && mode === 'commit' ? 'Reconciling…' : 'Reconcile Allocations'}
        </Button>
      </div>

      {confirmOpen && (
        <div
          className="rounded-xl border border-blue-200 bg-blue-50 p-4 mb-3"
          data-testid="alloc-reconcile-confirm"
        >
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-blue-700 mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="text-sm font-bold text-blue-900">
                Apply the corrected allocation counters?
              </div>
              <div className="text-xs text-blue-800 mt-0.5">
                Only allocated_qty values are rewritten — physical stock is
                untouched. Run Preview first and confirm the numbers look right.
              </div>
              <div className="mt-3 flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmOpen(false)}
                  data-testid="alloc-reconcile-cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => trigger(false)}
                  className="bg-[#0078D4] hover:bg-[#006aBE] text-white"
                  data-testid="alloc-reconcile-confirm-btn"
                >
                  Yes, apply
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {running && (
        <div
          className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 mb-3 flex items-center gap-2 text-sm text-blue-900"
          data-testid="alloc-reconcile-progress"
        >
          <Loader2 className="w-4 h-4 animate-spin" />
          Rebuilding allocated counters from open orders — this can take a moment…
        </div>
      )}

      {error && (
        <div
          className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 mb-3 text-sm text-rose-900"
          data-testid="alloc-reconcile-error"
        >
          {error}
        </div>
      )}

      {result && !running && (
        <div
          className="rounded-xl border border-emerald-200 bg-emerald-50 p-4"
          data-testid="alloc-reconcile-result"
        >
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            <div className="text-sm font-bold text-emerald-900">
              {result.dry_run ? 'Preview complete (no writes)' : 'Allocation counters reconciled'}
            </div>
            <div className="text-[11px] text-emerald-700 ml-auto tabular-nums">
              run by {result.run_by || '—'}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
            <StatCard label="Placements scanned" value={scanned} testid="alloc-reconcile-stat-scanned" />
            <StatCard label="Allocations corrected" value={corrected} testid="alloc-reconcile-stat-corrected" />
            <StatCard label="Replan candidates" value={replanCandidates} testid="alloc-reconcile-stat-replan" />
          </div>
          {(result.top_deltas || []).length > 0 && (
            <div className="mt-3 text-[11px] text-emerald-700">
              Largest correction: −{(result.top_deltas[0] || {}).delta ?? 0} allocated
              on placement {(result.top_deltas[0] || {}).placement_id || ''}.
            </div>
          )}
          {corrections.length > 0 && (
            <div
              className="mt-3 rounded-md border border-emerald-200 bg-white"
              data-testid="alloc-reconcile-corrections"
            >
              <div className="px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-neutral-600 border-b border-emerald-100 flex items-center justify-between">
                <span>
                  Corrected pallets ({corrections.length})
                </span>
                {(result.pallet_ids_filter || []).length > 0 && (
                  <span className="text-neutral-500 font-normal normal-case tracking-normal">
                    scoped to {result.pallet_ids_filter.length} pallet id{result.pallet_ids_filter.length === 1 ? '' : 's'}
                  </span>
                )}
              </div>
              <div className="max-h-64 overflow-y-auto">
                <table className="w-full text-[11px]">
                  <thead className="bg-neutral-50 text-neutral-600 uppercase tracking-wider sticky top-0">
                    <tr>
                      <th className="px-3 py-1.5 text-left">Pallet</th>
                      <th className="px-3 py-1.5 text-left">Location</th>
                      <th className="px-3 py-1.5 text-right">Before</th>
                      <th className="px-3 py-1.5 text-right">After</th>
                      <th className="px-3 py-1.5 text-right">Δ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {corrections.map((c, i) => (
                      <tr
                        key={`${c.placement_id}-${i}`}
                        className="border-t border-neutral-100"
                        data-testid={`alloc-reconcile-correction-row-${i}`}
                      >
                        <td className="px-3 py-1.5 font-mono">
                          {c.pallet_id || <em className="text-neutral-400">—</em>}
                        </td>
                        <td className="px-3 py-1.5 font-mono text-neutral-600">
                          {c.zone || '—'} / {c.location_id || '—'}
                        </td>
                        <td className="px-3 py-1.5 text-right font-mono text-neutral-500">
                          {c.before}
                        </td>
                        <td className="px-3 py-1.5 text-right font-mono font-bold text-emerald-700">
                          {c.after}
                        </td>
                        <td className="px-3 py-1.5 text-right font-mono text-neutral-500">
                          {c.before > c.after ? '−' : '+'}{c.delta}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          {result.run_at && (
            <div className="mt-1 text-[11px] text-emerald-700 tabular-nums">
              Finished {formatDateTime(result.run_at)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Feb 2026 — Super-admin only "Recalculate Open Dispatch Plans" block.
// Wraps /admin/data-tools/recalculate-open-dispatch-plans so ops can
// rebuild every open order's pick_lines against the current placement
// state (run this straight after Reconcile Placement Quantities to
// clear stale allocations pointing at now-dead qty).
const RecalcOpenDispatchBlock = ({ API, authHeaders }) => {
  const [running, setRunning] = React.useState(false);
  const [mode, setMode] = React.useState('');       // '' | 'preview' | 'commit'
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState('');
  const [confirmOpen, setConfirmOpen] = React.useState(false);

  const trigger = async (dryRun) => {
    setRunning(true);
    setMode(dryRun ? 'preview' : 'commit');
    setError('');
    setResult(null);
    setConfirmOpen(false);
    try {
      const r = await axios.post(
        `${API}/admin/data-tools/recalculate-open-dispatch-plans`,
        { dry_run: !!dryRun },
        { headers: authHeaders, timeout: 300000 },
      );
      setResult(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Recalculation failed.');
    } finally {
      setRunning(false);
    }
  };

  const summary = result || {};
  const changedOrders = (summary.orders || []).filter((o) => o.changed);

  return (
    <div
      className="mt-8 pt-6 border-t border-neutral-200"
      data-testid="data-tools-recalc-dispatch-block"
    >
      <div className="flex items-center gap-2 mb-1">
        <Wrench className="w-4 h-4 text-[#0078D4]" />
        <div className="text-sm font-bold text-neutral-900">
          Recalculate Open Dispatch Plans
        </div>
      </div>
      <div className="text-xs text-neutral-500 mb-4 leading-relaxed">
        Reruns the FEFO planner on every order that has an active pick
        plan and has not yet shipped. Use this straight after
        <span className="mx-1 font-semibold text-neutral-700">Reconcile Placement Quantities</span>
        so pick lines are rebuilt from the corrected placement data —
        stale allocations pointing at now-dead pallet qty are cleared
        and shortfalls surface honestly.
      </div>

      <div
        className="flex flex-wrap items-center gap-2 mb-3"
        data-testid="recalc-dispatch-actions"
      >
        <Button
          type="button"
          variant="outline"
          onClick={() => trigger(true)}
          disabled={running}
          data-testid="recalc-dispatch-preview-btn"
        >
          {running && mode === 'preview' ? (
            <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
          ) : (
            <Eye className="w-4 h-4 mr-1.5" />
          )}
          {running && mode === 'preview' ? 'Counting…' : 'Preview candidates'}
        </Button>
        <Button
          type="button"
          onClick={() => setConfirmOpen(true)}
          disabled={running}
          className="bg-[#0078D4] hover:bg-[#005A9E] text-white"
          data-testid="recalc-dispatch-apply-btn"
        >
          {running && mode === 'commit' ? (
            <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
          ) : (
            <Wrench className="w-4 h-4 mr-1.5" />
          )}
          {running && mode === 'commit' ? 'Recalculating…' : 'Recalculate all open plans'}
        </Button>
      </div>

      {confirmOpen && (
        <div
          className="rounded-xl border border-amber-200 bg-amber-50 p-4 mb-3"
          data-testid="recalc-dispatch-confirm"
        >
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="text-sm font-bold text-amber-900">
                Rebuild every open order&apos;s pick plan?
              </div>
              <div className="text-xs text-amber-800 mt-0.5">
                Each order&apos;s placement allocation is released and re-locked
                against the freshest FEFO source. Not reversible in one click.
              </div>
              <div className="mt-3 flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmOpen(false)}
                  data-testid="recalc-dispatch-cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => trigger(false)}
                  className="bg-[#0078D4] hover:bg-[#005A9E] text-white"
                  data-testid="recalc-dispatch-confirm-btn"
                >
                  Yes, recalculate
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {running && (
        <div
          className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 mb-3 flex items-center gap-2 text-sm text-blue-900"
          data-testid="recalc-dispatch-progress"
        >
          <Loader2 className="w-4 h-4 animate-spin" />
          Rebuilding pick lines for every open order — this can take a moment on large batches…
        </div>
      )}

      {error && (
        <div
          className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 mb-3 text-sm text-rose-900"
          data-testid="recalc-dispatch-error"
        >
          {error}
        </div>
      )}

      {result && !running && (
        <div
          className="rounded-xl border border-emerald-200 bg-emerald-50 p-4"
          data-testid="recalc-dispatch-result"
        >
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            <div className="text-sm font-bold text-emerald-900">
              {result.dry_run ? 'Preview complete (no writes)' : 'Recalculation applied'}
            </div>
            <div className="text-[11px] text-emerald-700 ml-auto tabular-nums">
              {result.duration_ms ?? 0} ms
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
            <StatCard label="Open orders scanned" value={result.orders_scanned || 0} testid="recalc-stat-scanned" />
            <StatCard label="Plans recalculated" value={result.orders_recalculated || 0} testid="recalc-stat-recalculated" />
            <StatCard label="Plans changed" value={result.orders_changed || 0} testid="recalc-stat-changed" />
            <StatCard label="Plans unchanged" value={result.orders_unchanged || 0} testid="recalc-stat-unchanged" />
            <StatCard label="Shortfall (before)" value={result.shortfall_orders_before || 0} testid="recalc-stat-shortfall-before" />
            <StatCard label="Shortfall (after)" value={result.shortfall_orders_after || 0} testid="recalc-stat-shortfall-after" />
          </div>
          {!result.dry_run && result.orders_failed > 0 && (
            <div className="mt-3 text-xs text-rose-800 font-semibold" data-testid="recalc-dispatch-failed-count">
              {result.orders_failed} order(s) failed to recalculate — see server logs.
            </div>
          )}
          {!result.dry_run && changedOrders.length > 0 && (
            <details className="mt-3 rounded-lg bg-white border border-emerald-200 p-3" data-testid="recalc-changed-list">
              <summary className="text-xs font-bold text-emerald-800 cursor-pointer">
                {changedOrders.length} plan{changedOrders.length === 1 ? '' : 's'} changed — show list
              </summary>
              <ul className="mt-2 max-h-48 overflow-y-auto text-xs text-neutral-800 divide-y divide-neutral-100">
                {changedOrders.map((o) => (
                  <li key={o.order_id} className="py-1 flex items-center gap-2 tabular-nums">
                    <span className="font-mono text-[11px]">{o.order_number || o.order_id}</span>
                    <span className="text-[10px] uppercase tracking-wider text-neutral-500">{o.status}</span>
                    {o.shortfall_after && (
                      <span className="text-[10px] font-semibold text-amber-700">shortfall</span>
                    )}
                  </li>
                ))}
              </ul>
            </details>
          )}
          {result.finished_at && (
            <div className="mt-3 text-[11px] text-emerald-700 tabular-nums">
              Finished {formatDateTime(result.finished_at)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// 2026-10 — Super-admin only. One-off cleanup button for the orphan
// pending picking tasks on order MR-00000495 assigned to
// rafiqueabdullah339@gmail.com. The backend endpoint has the filter
// hard-coded (order ref + assignee + status=pending), so this block
// just confirms, calls POST, and surfaces the deleted count.
// Completed tasks are protected server-side by the `status=pending`
// filter + a redundant `$ne: completed` clause.
const Mr495CleanupBlock = ({ API, authHeaders }) => {
  const [running, setRunning] = React.useState(false);
  const [report, setReport] = React.useState(null); // { deleted_count, deleted_tasks[] }
  const [error, setError] = React.useState('');
  const [confirmOpen, setConfirmOpen] = React.useState(false);

  const runCleanup = async () => {
    setRunning(true);
    setError('');
    try {
      const r = await axios.post(
        `${API}/admin/system/one-off/mr495-delete-pending-tasks`,
        {},
        { headers: authHeaders },
      );
      setReport(r.data || null);
      setConfirmOpen(false);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Cleanup failed.');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div
      className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4"
      data-testid="mr495-cleanup-block"
    >
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <div className="text-sm font-semibold text-amber-900">
            Order MR-00000495 — Orphan pending-task cleanup
          </div>
          <div className="mt-1 text-[12.5px] text-amber-800 leading-relaxed">
            Deletes <b>pending</b> picking/putaway/loading tasks on order
            <code className="mx-1 px-1 bg-white/60 rounded border border-amber-200">
              MR-00000495
            </code>
            assigned to{' '}
            <code className="px-1 bg-white/60 rounded border border-amber-200">
              rafiqueabdullah339@gmail.com
            </code>
            . Completed tasks are protected — the server filter is
            hard-coded to <code>status=pending</code>. Safe to run multiple
            times; a second call returns 0.
          </div>
        </div>
        <button
          type="button"
          onClick={() => { setConfirmOpen(true); setError(''); }}
          disabled={running}
          className="shrink-0 inline-flex items-center gap-2 rounded-lg bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white font-semibold text-sm px-4 py-2 shadow-sm transition"
          data-testid="mr495-cleanup-open-btn"
        >
          {running ? 'Working…' : 'Run cleanup'}
        </button>
      </div>

      {confirmOpen && (
        <div
          className="mt-3 rounded-lg border border-amber-300 bg-white p-3"
          data-testid="mr495-cleanup-confirm"
        >
          <div className="text-[13px] text-neutral-800 font-medium">
            Confirm — delete all <b>pending</b> tasks on MR-00000495 for
            that picker?
          </div>
          <div className="mt-1 text-[11.5px] text-neutral-600">
            Completed tasks are not touched. The operation is logged
            server-side with your admin email.
          </div>
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={runCleanup}
              disabled={running}
              className="rounded-md bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white text-sm font-semibold px-3 py-1.5"
              data-testid="mr495-cleanup-confirm-btn"
            >
              {running ? 'Deleting…' : 'Yes, delete pending tasks'}
            </button>
            <button
              type="button"
              onClick={() => setConfirmOpen(false)}
              disabled={running}
              className="rounded-md border border-neutral-300 bg-white text-neutral-700 text-sm px-3 py-1.5"
              data-testid="mr495-cleanup-cancel-btn"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {error && (
        <div
          className="mt-3 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-[12.5px] text-red-800"
          data-testid="mr495-cleanup-error"
        >
          {error}
        </div>
      )}

      {report && (
        <div
          className="mt-3 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-[12.5px] text-emerald-900"
          data-testid="mr495-cleanup-report"
        >
          <div className="font-semibold">
            Deleted {report.deleted_count ?? 0} pending task
            {(report.deleted_count ?? 0) === 1 ? '' : 's'}.
          </div>
          {Array.isArray(report.deleted_tasks) && report.deleted_tasks.length > 0 && (
            <ul className="mt-1 list-disc pl-5 space-y-0.5">
              {report.deleted_tasks.slice(0, 20).map((t) => (
                <li key={t.id}>
                  <code className="text-[11px]">{t.id}</code> · pallet{' '}
                  {t.pallet_id || '—'} · loc {t.location_id || '—'}
                </li>
              ))}
              {report.deleted_tasks.length > 20 && (
                <li className="text-neutral-600">
                  … and {report.deleted_tasks.length - 20} more
                </li>
              )}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};



// Feb 2026 — Super-admin only "Phase 2 · Historical data repair" block.
// Wraps the /admin/data-tools/phase2-* endpoints. Three actions:
//   • Dry-run: preview the per-SKU plan, no writes.
//   • Apply: requires an active dry-run < 30 min old.
//   • Rollback: reverse one applied run_id.
// Backup collections are created automatically; every row inserted /
// changed is tagged with the run_id so a rollback is a one-click.
const Phase2RepairBlock = ({ API, authHeaders }) => {
  const [running, setRunning] = React.useState(false);
  const [mode, setMode] = React.useState(''); // '' | 'dry_run' | 'apply' | 'rollback'
  const [dryReport, setDryReport] = React.useState(null); // last dry-run report
  const [applyReport, setApplyReport] = React.useState(null); // last apply/rollback report
  const [error, setError] = React.useState('');
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [rollbackRunId, setRollbackRunId] = React.useState('');
  const [recentRuns, setRecentRuns] = React.useState([]);

  // Feb 2026 — Scope filter. Empty state == run against every SKU.
  const [filterSkus, setFilterSkus] = React.useState('');
  const [filterCategories, setFilterCategories] = React.useState([]);
  const [filterDivisions, setFilterDivisions] = React.useState([]);
  const [filterOptions, setFilterOptions] = React.useState({ categories: [], divisions: [] });

  const loadFilterOptions = React.useCallback(async () => {
    try {
      const r = await axios.get(
        `${API}/admin/data-tools/phase2-filter-options`,
        { headers: authHeaders },
      );
      setFilterOptions({
        categories: r.data?.categories || [],
        divisions: r.data?.divisions || [],
      });
    } catch (err) {
      // Non-fatal: super-admin gate might not be satisfied yet.
    }
  }, [API, authHeaders]);

  const buildFilterPayload = () => {
    const skus = filterSkus
      .split(/[\s,;]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (!skus.length && !filterCategories.length && !filterDivisions.length) {
      return null;
    }
    return {
      product_ids: [],
      skus,
      categories: filterCategories,
      divisions: filterDivisions,
    };
  };

  const clearFilter = () => {
    setFilterSkus('');
    setFilterCategories([]);
    setFilterDivisions([]);
  };

  const filterActive = React.useMemo(() => {
    return !!(
      filterSkus.trim() || filterCategories.length || filterDivisions.length
    );
  }, [filterSkus, filterCategories, filterDivisions]);

  const loadRecent = React.useCallback(async () => {
    try {
      const r = await axios.get(`${API}/admin/data-tools/phase2-runs`, {
        headers: authHeaders,
      });
      setRecentRuns(r.data?.runs || []);
    } catch (err) {
      // Non-fatal: super-admin gate might not be satisfied yet.
    }
  }, [API, authHeaders]);

  React.useEffect(() => { loadRecent(); loadFilterOptions(); }, [loadRecent, loadFilterOptions]);

  const triggerDryRun = async () => {
    setRunning(true); setMode('dry_run'); setError('');
    setDryReport(null); setApplyReport(null);
    try {
      const filter = buildFilterPayload();
      const r = await axios.post(
        `${API}/admin/data-tools/phase2-repair`,
        { mode: 'dry_run', filter },
        { headers: authHeaders },
      );
      setDryReport(r.data);
      await loadRecent();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Dry-run failed.');
    } finally {
      setRunning(false);
    }
  };

  const triggerApply = async () => {
    if (!dryReport?.run_id) return;
    setRunning(true); setMode('apply'); setError('');
    setConfirmOpen(false);
    try {
      const r = await axios.post(
        `${API}/admin/data-tools/phase2-repair`,
        { mode: 'apply', run_id: dryReport.run_id },
        { headers: authHeaders },
      );
      setApplyReport(r.data);
      await loadRecent();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Apply failed.');
    } finally {
      setRunning(false);
    }
  };

  const triggerRollback = async () => {
    const rid = (rollbackRunId || '').trim();
    if (!rid) { setError('Enter a run_id to roll back.'); return; }
    setRunning(true); setMode('rollback'); setError('');
    try {
      const r = await axios.post(
        `${API}/admin/data-tools/phase2-rollback`,
        { run_id: rid },
        { headers: authHeaders },
      );
      setApplyReport(r.data);
      setRollbackRunId('');
      await loadRecent();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Rollback failed.');
    } finally {
      setRunning(false);
    }
  };

  const step_d = dryReport?.step_d || {};
  const step_e = dryReport?.step_e || {};
  const large = step_d.large_adjustments || [];
  const orphans = step_d.orphan_no_placements || [];

  return (
    <div
      className="mt-8 pt-6 border-t border-neutral-200"
      data-testid="data-tools-phase2-repair-block"
    >
      <div className="flex items-center gap-2 mb-1">
        <Wrench className="w-4 h-4 text-[#B45309]" />
        <div className="text-sm font-bold text-neutral-900">
          Phase 2 · Historical Data Repair
        </div>
      </div>
      <div className="text-xs text-neutral-500 mb-4 leading-relaxed">
        In-app repair of the historical ledger drift: backfill
        <code className="mx-1 px-1 py-0.5 rounded bg-neutral-100 text-[11px]">production_date</code>
        on legacy IN events, insert
        <code className="mx-1 px-1 py-0.5 rounded bg-neutral-100 text-[11px]">reconciliation_adjustment</code>
        rows per production_date so each SKU&apos;s ledger nets to its true physical
        placement total, and clean up blank-date entries inside
        <code className="mx-1 px-1 py-0.5 rounded bg-neutral-100 text-[11px]">products.batches</code>.
        Always run
        <span className="font-semibold text-neutral-700 mx-1">Dry-run</span>
        first — review the plan, then apply within 30 minutes. Every apply
        writes a backup keyed on its
        <code className="mx-1 px-1 py-0.5 rounded bg-neutral-100 text-[11px]">run_id</code>
        so a rollback is a one-click. ⚠️ Super-admin only.
      </div>

      {/* Feb 2026 — Scope filter. Empty state == run against every
          SKU. Any non-empty field narrows the plan; multiple fields
          are OR-ed by the backend. */}
      <div
        className="rounded-xl border border-neutral-200 bg-neutral-50 p-3 mb-3"
        data-testid="phase2-scope-filter"
      >
        <div className="flex items-center gap-2 mb-2">
          <div className="text-xs font-bold text-neutral-800">Scope filter</div>
          <span className={
            filterActive
              ? "text-[10px] font-bold uppercase text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded"
              : "text-[10px] uppercase text-neutral-500 bg-white border border-neutral-200 px-1.5 py-0.5 rounded"
          } data-testid="phase2-scope-status">
            {filterActive ? 'Scoped' : 'All SKUs'}
          </span>
          {filterActive && (
            <button
              type="button"
              onClick={clearFilter}
              className="ml-auto text-[11px] text-neutral-600 hover:text-neutral-900 underline"
              data-testid="phase2-scope-clear"
            >
              Clear filter
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <div>
            <label className="block text-[11px] font-semibold text-neutral-600 mb-1">
              SKUs
            </label>
            <textarea
              value={filterSkus}
              onChange={(e) => setFilterSkus(e.target.value)}
              placeholder="One or more SKUs, separated by comma / space / newline"
              rows={2}
              className="w-full text-xs font-mono border border-neutral-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-amber-500"
              data-testid="phase2-scope-skus"
            />
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-neutral-600 mb-1">
              Categories
            </label>
            <select
              multiple
              value={filterCategories}
              onChange={(e) => setFilterCategories(
                Array.from(e.target.selectedOptions).map((o) => o.value),
              )}
              className="w-full text-xs border border-neutral-300 rounded px-2 py-1 h-16 focus:outline-none focus:ring-2 focus:ring-amber-500"
              data-testid="phase2-scope-categories"
            >
              {filterOptions.categories.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            <div className="text-[10px] text-neutral-500 mt-0.5">
              Ctrl/⌘-click for multiple
            </div>
          </div>
          <div>
            <label className="block text-[11px] font-semibold text-neutral-600 mb-1">
              Divisions
            </label>
            <select
              multiple
              value={filterDivisions}
              onChange={(e) => setFilterDivisions(
                Array.from(e.target.selectedOptions).map((o) => o.value),
              )}
              className="w-full text-xs border border-neutral-300 rounded px-2 py-1 h-16 focus:outline-none focus:ring-2 focus:ring-amber-500"
              data-testid="phase2-scope-divisions"
            >
              {filterOptions.divisions.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
            <div className="text-[10px] text-neutral-500 mt-0.5">
              Ctrl/⌘-click for multiple
            </div>
          </div>
        </div>
        <div className="text-[11px] text-neutral-500 mt-2 leading-relaxed">
          Leave all three fields empty to run against every product. When any
          field is populated, only matching products are touched — an apply
          reuses the exact filter recorded on the dry-run so nothing outside
          the scope can be modified.
        </div>
      </div>

      <div
        className="flex flex-wrap items-center gap-2 mb-3"
        data-testid="phase2-repair-actions"
      >
        <Button
          type="button"
          variant="outline"
          onClick={triggerDryRun}
          disabled={running}
          data-testid="phase2-dry-run-btn"
        >
          {running && mode === 'dry_run' ? (
            <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
          ) : (
            <Eye className="w-4 h-4 mr-1.5" />
          )}
          {running && mode === 'dry_run' ? 'Scanning…' : 'Dry-run (preview)'}
        </Button>
        <Button
          type="button"
          onClick={() => setConfirmOpen(true)}
          disabled={running || !dryReport?.run_id}
          className="bg-[#B45309] hover:bg-[#92400E] text-white disabled:bg-neutral-300"
          data-testid="phase2-apply-btn"
        >
          {running && mode === 'apply' ? (
            <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
          ) : (
            <Wrench className="w-4 h-4 mr-1.5" />
          )}
          {running && mode === 'apply'
            ? 'Applying…'
            : `Apply${dryReport?.run_id ? ` ${dryReport.run_id}` : ''}`}
        </Button>
      </div>

      {/* Rollback row */}
      <div
        className="flex flex-wrap items-center gap-2 mb-3"
        data-testid="phase2-rollback-actions"
      >
        <Input
          type="text"
          value={rollbackRunId}
          onChange={(e) => setRollbackRunId(e.target.value)}
          placeholder="PH2-XXXXXXXX to roll back"
          className="max-w-[240px] font-mono text-xs"
          data-testid="phase2-rollback-input"
        />
        <Button
          type="button"
          variant="outline"
          onClick={triggerRollback}
          disabled={running || !rollbackRunId.trim()}
          data-testid="phase2-rollback-btn"
        >
          {running && mode === 'rollback' ? (
            <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
          ) : (
            <RotateCcw className="w-4 h-4 mr-1.5" />
          )}
          Rollback
        </Button>
      </div>

      {confirmOpen && (
        <div
          className="rounded-xl border border-amber-200 bg-amber-50 p-4 mb-3"
          data-testid="phase2-apply-confirm"
        >
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="text-sm font-bold text-amber-900">
                Apply phase 2 repair to production data?
              </div>
              <div className="text-xs text-amber-800 mt-0.5">
                Run <code className="font-mono">{dryReport?.run_id}</code> —
                {step_d.skus_needing_adjustment || 0} SKU(s),
                {' '}{step_d.total_adjustment_rows || 0} adjustment row(s),
                {' '}{step_e.batches_to_fix_count || 0} batch fix(es),
                {' '}{step_e.batches_to_remove_count || 0} batch removal(s).
                {dryReport?.filter_summary?.scoped && (
                  <span className="block mt-1 font-semibold text-amber-900" data-testid="phase2-apply-confirm-scope">
                    🔒 Scoped run — only the {dryReport.filter_summary.matched_product_count || 0}
                    {' '}filter-matched product(s) will be touched.
                  </span>
                )}
                {large.length > 0 && (
                  <span className="block mt-1 font-semibold text-amber-900">
                    ⚠ {large.length} SKU(s) with adjustment &gt; 1,000 units.
                    Verify against a floor count before applying.
                  </span>
                )}
              </div>
              <div className="mt-3 flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setConfirmOpen(false)}
                  data-testid="phase2-apply-cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={triggerApply}
                  className="bg-[#B45309] hover:bg-[#92400E] text-white"
                  data-testid="phase2-apply-confirm-btn"
                >
                  Yes, apply {dryReport?.run_id}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {error && (
        <div
          className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 mb-3 text-sm text-rose-900"
          data-testid="phase2-repair-error"
        >
          {error}
        </div>
      )}

      {/* Dry-run summary */}
      {dryReport && (
        <div
          className="rounded-xl border border-neutral-200 bg-white p-4 mb-3"
          data-testid="phase2-dry-run-result"
        >
          <div className="flex items-center gap-2 mb-3">
            <Eye className="w-5 h-5 text-[#0078D4]" />
            <div className="text-sm font-bold text-neutral-900">
              Dry-run plan · <span className="font-mono">{dryReport.run_id}</span>
            </div>
            {dryReport.filter_summary?.scoped && (
              <span
                className="text-[10px] font-bold uppercase text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded"
                data-testid="phase2-dry-run-scope-badge"
                title="Filter recorded on this dry-run"
              >
                🔒 Scoped · {dryReport.filter_summary.matched_product_count || 0} SKU(s)
              </span>
            )}
            <div className="text-[11px] text-neutral-500 ml-auto tabular-nums">
              {dryReport.finished_at ? formatDateTime(dryReport.finished_at) : ''}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm mb-3">
            <StatCard
              label="IN events w/o date"
              value={dryReport.step_c?.scanned_in_events_without_date || 0}
              testid="phase2-stat-c-scanned"
            />
            <StatCard
              label="Stampable"
              value={dryReport.step_c?.stampable_estimate || 0}
              testid="phase2-stat-c-stampable"
            />
            <StatCard
              label="SKUs needing adj."
              value={step_d.skus_needing_adjustment || 0}
              testid="phase2-stat-d-skus"
            />
            <StatCard
              label="Adj. rows"
              value={step_d.total_adjustment_rows || 0}
              testid="phase2-stat-d-rows"
            />
            <StatCard
              label="Blank batches to fix"
              value={step_e.batches_to_fix_count || 0}
              testid="phase2-stat-e-fix"
            />
            <StatCard
              label="Blank batches to remove"
              value={step_e.batches_to_remove_count || 0}
              testid="phase2-stat-e-remove"
            />
            <StatCard
              label="No-placement SKUs"
              value={orphans.length}
              testid="phase2-stat-orphans"
            />
            <StatCard
              label="Large (>1000) SKUs"
              value={large.length}
              testid="phase2-stat-large"
            />
          </div>

          {large.length > 0 && (
            <details
              className="mt-3 rounded-lg bg-amber-50 border border-amber-200 p-3"
              data-testid="phase2-large-list"
              open
            >
              <summary className="text-xs font-bold text-amber-900 cursor-pointer">
                ⚠ {large.length} SKU(s) with |adjustment| &gt; 1,000 — verify before apply
              </summary>
              <ul className="mt-2 max-h-40 overflow-y-auto text-xs text-amber-900 divide-y divide-amber-200">
                {large.map((s) => (
                  <li key={s.product_id} className="py-1 flex items-center gap-2 tabular-nums font-mono">
                    <span className="font-semibold">{s.sku || s.product_id}</span>
                    <span className="truncate">{s.name}</span>
                    <span className="ml-auto">
                      {s.total_adjustment > 0 ? '+' : ''}{s.total_adjustment}
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          )}

          {orphans.length > 0 && (
            <details
              className="mt-3 rounded-lg bg-neutral-50 border border-neutral-200 p-3"
              data-testid="phase2-orphan-list"
            >
              <summary className="text-xs font-bold text-neutral-800 cursor-pointer">
                {orphans.length} SKU(s) with no physical placements — will be zeroed
              </summary>
              <ul className="mt-2 max-h-40 overflow-y-auto text-xs text-neutral-800 divide-y divide-neutral-100">
                {orphans.map((s) => (
                  <li key={s.product_id} className="py-1 flex items-center gap-2 tabular-nums font-mono">
                    <span className="font-semibold">{s.sku || s.product_id}</span>
                    <span className="truncate">{s.name}</span>
                    <span className="ml-auto">ledger {s.ledger_net_before}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}

          {(step_d.per_sku || []).length > 0 && (
            <details
              className="mt-3 rounded-lg bg-white border border-neutral-200 p-3"
              data-testid="phase2-per-sku-list"
            >
              <summary className="text-xs font-bold text-neutral-800 cursor-pointer">
                Full per-SKU plan ({(step_d.per_sku || []).length})
              </summary>
              <div className="mt-2 max-h-64 overflow-y-auto text-xs">
                <table className="w-full">
                  <thead className="text-[10px] uppercase tracking-wider text-neutral-500">
                    <tr>
                      <th className="text-left py-1">SKU</th>
                      <th className="text-right py-1">Ledger</th>
                      <th className="text-right py-1">Placements</th>
                      <th className="text-right py-1">Δ</th>
                      <th className="text-right py-1">Dates</th>
                    </tr>
                  </thead>
                  <tbody className="tabular-nums">
                    {(step_d.per_sku || []).slice(0, 500).map((s) => (
                      <tr key={s.product_id} className="border-t border-neutral-100">
                        <td className="py-1 font-mono">{s.sku || s.product_id}</td>
                        <td className="py-1 text-right">{s.ledger_net_before}</td>
                        <td className="py-1 text-right">{s.placement_total}</td>
                        <td className="py-1 text-right font-bold">
                          {s.total_adjustment > 0 ? '+' : ''}{s.total_adjustment}
                        </td>
                        <td className="py-1 text-right text-neutral-500">
                          {(s.adjustments || []).length}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}
        </div>
      )}

      {/* Apply/rollback outcome */}
      {applyReport && (
        <div
          className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 mb-3"
          data-testid="phase2-apply-result"
        >
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            <div className="text-sm font-bold text-emerald-900">
              {applyReport.mode === 'rollback' ? 'Rollback complete' : 'Apply complete'} ·{' '}
              <span className="font-mono">{applyReport.run_id}</span>
            </div>
            <div className="text-[11px] text-emerald-700 ml-auto tabular-nums">
              {applyReport.duration_ms ?? 0} ms
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            {applyReport.mode === 'apply' ? (
              <>
                <StatCard
                  label="C: dates stamped"
                  value={applyReport.step_c?.stamped || 0}
                  testid="phase2-apply-stat-c-stamped"
                />
                <StatCard
                  label="D: ledger rows"
                  value={applyReport.step_d?.ledger_rows_inserted || 0}
                  testid="phase2-apply-stat-d-rows"
                />
                <StatCard
                  label="E: batches fixed"
                  value={applyReport.step_e?.batches_fixed || 0}
                  testid="phase2-apply-stat-e-fixed"
                />
                <StatCard
                  label="E: batches removed"
                  value={applyReport.step_e?.batches_removed || 0}
                  testid="phase2-apply-stat-e-removed"
                />
                <StatCard
                  label="F: verified"
                  value={applyReport.step_f?.verified_count || 0}
                  testid="phase2-apply-stat-f-verified"
                />
                <StatCard
                  label="F: still negative"
                  value={(applyReport.step_f?.still_negative || []).length}
                  testid="phase2-apply-stat-f-negative"
                />
                <StatCard
                  label="F: mismatches"
                  value={(applyReport.step_f?.mismatches || []).length}
                  testid="phase2-apply-stat-f-mismatches"
                />
                <StatCard
                  label="Plans recalc"
                  value={applyReport.recalc_open_dispatch?.orders_recalculated || 0}
                  testid="phase2-apply-stat-recalc"
                />
              </>
            ) : (
              <>
                <StatCard
                  label="Ledger rows deleted"
                  value={applyReport.deleted_ledger_rows || 0}
                  testid="phase2-rb-stat-ledger"
                />
                <StatCard
                  label="Events restored"
                  value={applyReport.restored_stock_events || 0}
                  testid="phase2-rb-stat-events"
                />
                <StatCard
                  label="Products restored"
                  value={applyReport.restored_products || 0}
                  testid="phase2-rb-stat-products"
                />
                <StatCard
                  label="Recomputed"
                  value={applyReport.products_recomputed || 0}
                  testid="phase2-rb-stat-recompute"
                />
              </>
            )}
          </div>
          {(applyReport.step_f?.still_negative || []).length > 0 && (
            <div className="mt-3 text-xs font-semibold text-rose-800" data-testid="phase2-still-negative-warn">
              ⚠ {(applyReport.step_f.still_negative).length} SKU(s) still net negative —
              review server logs and stock_ledger_write_failures collection.
            </div>
          )}
        </div>
      )}

      {/* Recent runs */}
      {recentRuns.length > 0 && (
        <details
          className="rounded-xl border border-neutral-200 bg-white p-3"
          data-testid="phase2-recent-runs"
        >
          <summary className="text-xs font-bold text-neutral-700 cursor-pointer">
            Recent phase 2 runs ({recentRuns.length})
          </summary>
          <ul className="mt-2 max-h-64 overflow-y-auto text-xs text-neutral-800 divide-y divide-neutral-100">
            {recentRuns.map((r) => (
              <li
                key={`${r.run_id}-${r.mode}`}
                className="py-1.5 flex items-center gap-2 tabular-nums font-mono"
              >
                <span className="font-semibold">{r.run_id}</span>
                <span className={
                  r.mode === 'apply' ? 'text-emerald-700 text-[10px] uppercase font-bold' :
                  r.mode === 'rollback' ? 'text-rose-700 text-[10px] uppercase font-bold' :
                  'text-neutral-600 text-[10px] uppercase'
                }>
                  {r.mode}
                </span>
                <span className="text-neutral-500 text-[11px] truncate">
                  {r.email}
                </span>
                <span className="ml-auto text-neutral-500 text-[11px]">
                  {r.started_at ? formatDateTime(r.started_at) : ''}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};


// Feb 2026 (post-IFG0012) — Three super-admin recovery blocks for
// the ship-state / batch-ledger drift class the IFG0012 FA23101
// incident exposed. All three live in the Maintenance tab beside
// the existing data-tools blocks.

// 1. Clear false `shipped_state` on placements that still have qty > 0.
const QtyVsShippedStateReconcilerBlock = ({ API, authHeaders }) => {
  const [running, setRunning] = React.useState(false);
  const [mode, setMode] = React.useState('');
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState('');

  const trigger = async (commit) => {
    setRunning(true);
    setMode(commit ? 'commit' : 'preview');
    setError('');
    setResult(null);
    try {
      const r = await axios.post(
        `${API}/admin/data-tools/reconcile-qty-vs-shipped-state`,
        { commit },
        { headers: authHeaders },
      );
      setResult(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Scan failed.');
    } finally {
      setRunning(false);
    }
  };

  const found = result?.found || 0;
  const cleared = result?.cleared || 0;
  const rows = result?.placements || [];

  return (
    <div
      className="mt-8 pt-6 border-t border-neutral-200"
      data-testid="data-tools-qty-vs-shipped-state-block"
    >
      <div className="flex items-center gap-2 mb-1">
        <AlertTriangle className="w-4 h-4 text-amber-600" />
        <div className="text-sm font-bold text-neutral-900">
          Clear False Shipped Flags (qty &gt; 0)
        </div>
      </div>
      <div className="text-xs text-neutral-500 mb-4 leading-relaxed">
        Scans every <code className="mx-1 px-1 py-0.5 rounded bg-neutral-100 text-[11px]">warehouse_placements</code>
        row where <code className="mx-1 px-1 py-0.5 rounded bg-neutral-100 text-[11px]">qty &gt; 0</code>
        AND <code className="mx-1 px-1 py-0.5 rounded bg-neutral-100 text-[11px]">shipped_state ∈ (shipped, consumed)</code>.
        These are mutually exclusive by design (post-IFG0012) &mdash; `shipped_state`
        is strictly post-decrement. Rows that match are proof of past drift
        (interrupted ship pipeline / legacy pre-decrement stamping). The tool
        clears the false flag and leaves qty intact so the pallet surfaces again
        on Live Stock.
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Button
          type="button"
          variant="outline"
          onClick={() => trigger(false)}
          disabled={running}
          data-testid="qty-vs-shipped-preview-btn"
        >
          {running && mode === 'preview' ? (
            <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
          ) : (
            <Eye className="w-4 h-4 mr-1.5" />
          )}
          {running && mode === 'preview' ? 'Scanning…' : 'Preview'}
        </Button>
        <Button
          type="button"
          onClick={() => trigger(true)}
          disabled={running || !found}
          className="bg-rose-600 hover:bg-rose-700 text-white"
          data-testid="qty-vs-shipped-apply-btn"
        >
          {running && mode === 'commit' ? (
            <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
          ) : (
            <Wrench className="w-4 h-4 mr-1.5" />
          )}
          {running && mode === 'commit' ? 'Clearing…' : `Clear ${found || ''} false flag${found === 1 ? '' : 's'}`.trim()}
        </Button>
      </div>
      {error && (
        <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2 mb-2">
          {error}
        </div>
      )}
      {result && (
        <div
          className="text-xs text-neutral-700 bg-neutral-50 border border-neutral-200 rounded-md p-3"
          data-testid="qty-vs-shipped-result"
        >
          <div className="font-semibold text-neutral-900 mb-1">
            {mode === 'commit' ? 'Applied' : 'Preview'} — {found} drifted row{found === 1 ? '' : 's'}
            {mode === 'commit' && `, ${cleared} cleared`}
          </div>
          {rows.length > 0 && (
            <details>
              <summary className="cursor-pointer text-[#0078D4] hover:underline">
                Show {rows.length} affected placement{rows.length === 1 ? '' : 's'}
              </summary>
              <ul className="mt-2 space-y-1 max-h-72 overflow-auto">
                {rows.map((r) => (
                  <li key={r.id} className="font-mono text-[11px]">
                    <span className="text-neutral-500">{r.pallet_id || r.id}</span>
                    {' / '}
                    <span className="text-neutral-900">qty={r.qty}</span>
                    {' · '}
                    <span className="text-rose-700">{r.shipped_state}</span>
                    {r.shipped_order_id ? (
                      <span className="text-neutral-500"> ({r.shipped_order_id})</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}
    </div>
  );
};

// 2. Generic (product_id, batch_ref, expiry, prod_date) ledger reconciler.
const BatchLedgerNetReconcilerBlock = ({ API, authHeaders }) => {
  const [form, setForm] = React.useState({
    product_id: '',
    batch_ref: '',
    expiry_date: '',
    production_date: '',
    target_qty: '',
    note: '',
    clear_shipped_placement_ids: '',
  });
  const [running, setRunning] = React.useState(false);
  const [mode, setMode] = React.useState('');
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState('');

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const trigger = async (commit) => {
    setRunning(true);
    setMode(commit ? 'commit' : 'preview');
    setError('');
    setResult(null);
    try {
      const payload = {
        product_id: form.product_id.trim(),
        batch_ref: form.batch_ref.trim(),
        expiry_date: form.expiry_date.trim(),
        production_date: form.production_date.trim(),
        target_qty: parseInt(form.target_qty, 10),
        note: form.note.trim(),
        commit,
        clear_shipped_placement_ids: form.clear_shipped_placement_ids
          .split(/[,\n\s]+/)
          .map((s) => s.trim())
          .filter(Boolean),
      };
      const r = await axios.post(
        `${API}/admin/data-tools/reconcile-batch-ledger-net`,
        payload,
        { headers: authHeaders },
      );
      setResult(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Reconciliation failed.');
    } finally {
      setRunning(false);
    }
  };

  const ready =
    form.product_id.trim() &&
    form.expiry_date.trim() &&
    form.production_date.trim() &&
    form.target_qty !== '' &&
    !Number.isNaN(parseInt(form.target_qty, 10));

  return (
    <div
      className="mt-8 pt-6 border-t border-neutral-200"
      data-testid="data-tools-batch-ledger-reconcile-block"
    >
      <div className="flex items-center gap-2 mb-1">
        <Database className="w-4 h-4 text-[#0078D4]" />
        <div className="text-sm font-bold text-neutral-900">
          Reconcile Batch Ledger Net
        </div>
      </div>
      <div className="text-xs text-neutral-500 mb-4 leading-relaxed">
        Idempotent target-based ledger reconciliation for a single
        (product, batch, expiry, prod-date) bucket. Computes the current
        recompute-visible net at run time and posts the delta as a single
        <code className="mx-1 px-1 py-0.5 rounded bg-neutral-100 text-[11px]">reconciliation_adjustment</code>
        / <code className="mx-1 px-1 py-0.5 rounded bg-neutral-100 text-[11px]">reconciliation_adjustment_out</code>
        event, then runs the ledger-driven product summary. Re-running after a
        successful commit yields a zero-delta no-op. Optional placement-id list
        clears false shipped / dispatch flags on those rows at the same time
        (use for the IFG0012 FA23101 case).
      </div>
      <div className="grid gap-3 md:grid-cols-2 mb-3" data-testid="batch-ledger-form">
        <label className="block">
          <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-1">Product ID</div>
          <Input
            value={form.product_id}
            onChange={(e) => update({ product_id: e.target.value })}
            placeholder="4a72a2d6-ab26-4948-955f-e4268d578c5d"
            className="font-mono text-xs"
            data-testid="batch-ledger-product-id"
          />
        </label>
        <label className="block">
          <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-1">Batch Ref</div>
          <Input
            value={form.batch_ref}
            onChange={(e) => update({ batch_ref: e.target.value })}
            placeholder="ASN-000046"
            className="font-mono text-xs"
            data-testid="batch-ledger-batch-ref"
          />
        </label>
        <label className="block">
          <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-1">Expiry Date</div>
          <Input
            type="date"
            value={form.expiry_date}
            onChange={(e) => update({ expiry_date: e.target.value })}
            className="font-mono text-xs"
            data-testid="batch-ledger-expiry"
          />
        </label>
        <label className="block">
          <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-1">Production Date</div>
          <Input
            type="date"
            value={form.production_date}
            onChange={(e) => update({ production_date: e.target.value })}
            className="font-mono text-xs"
            data-testid="batch-ledger-prod-date"
          />
        </label>
        <label className="block">
          <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-1">Target On-Hand Qty</div>
          <Input
            type="number"
            min="0"
            value={form.target_qty}
            onChange={(e) => update({ target_qty: e.target.value })}
            placeholder="256"
            className="font-mono text-xs"
            data-testid="batch-ledger-target-qty"
          />
        </label>
        <label className="block">
          <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-1">
            Clear shipped flags on these placement IDs (optional)
          </div>
          <Input
            value={form.clear_shipped_placement_ids}
            onChange={(e) => update({ clear_shipped_placement_ids: e.target.value })}
            placeholder="60b61f0c-6da1-47a8-b325-244e2a0277cd, …"
            className="font-mono text-xs"
            data-testid="batch-ledger-clear-shipped"
          />
        </label>
        <label className="block md:col-span-2">
          <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-1">Note (optional)</div>
          <Input
            value={form.note}
            onChange={(e) => update({ note: e.target.value })}
            placeholder="physical count reconciliation for IFG0012 / ASN-000046"
            className="text-xs"
            data-testid="batch-ledger-note"
          />
        </label>
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Button
          type="button"
          variant="outline"
          onClick={() => trigger(false)}
          disabled={running || !ready}
          data-testid="batch-ledger-preview-btn"
        >
          {running && mode === 'preview' ? (
            <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
          ) : (
            <Eye className="w-4 h-4 mr-1.5" />
          )}
          {running && mode === 'preview' ? 'Computing…' : 'Preview delta'}
        </Button>
        <Button
          type="button"
          onClick={() => trigger(true)}
          disabled={running || !ready}
          className="bg-rose-600 hover:bg-rose-700 text-white"
          data-testid="batch-ledger-apply-btn"
        >
          {running && mode === 'commit' ? (
            <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
          ) : (
            <Wrench className="w-4 h-4 mr-1.5" />
          )}
          {running && mode === 'commit' ? 'Reconciling…' : 'Apply reconciliation'}
        </Button>
      </div>
      {error && (
        <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2 mb-2">
          {error}
        </div>
      )}
      {result && (
        <div
          className="text-xs text-neutral-700 bg-neutral-50 border border-neutral-200 rounded-md p-3"
          data-testid="batch-ledger-result"
        >
          <div className="font-semibold text-neutral-900 mb-1 flex items-center gap-1">
            {result.commit ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <Eye className="w-3.5 h-3.5 text-neutral-500" />}
            {result.commit ? 'Applied' : 'Preview'} · delta {result.delta_computed > 0 ? '+' : ''}{result.delta_computed}
          </div>
          <div className="grid grid-cols-2 gap-2 mt-2">
            <div className="rounded-md border border-neutral-200 bg-white p-2">
              <div className="text-[10px] uppercase tracking-wider text-neutral-400 font-bold">Before</div>
              <div className="font-mono text-xs">
                ledger_net = <b>{result.before?.ledger_net}</b>
                <br />placement_sum = <b>{result.before?.placement_sum}</b>
              </div>
            </div>
            <div className="rounded-md border border-neutral-200 bg-white p-2">
              <div className="text-[10px] uppercase tracking-wider text-neutral-400 font-bold">After</div>
              <div className="font-mono text-xs">
                ledger_net = <b>{result.after?.ledger_net}</b>
                <br />placement_sum = <b>{result.after?.placement_sum}</b>
              </div>
            </div>
          </div>
          {result.cleared_shipped?.length > 0 && (
            <div className="mt-2 text-[11px] text-emerald-700">
              Cleared shipped flag on {result.cleared_shipped.length} placement{result.cleared_shipped.length === 1 ? '' : 's'}.
            </div>
          )}
          {result.event_id && (
            <div className="mt-1 text-[11px] text-neutral-500 font-mono truncate">
              event_id = {result.event_id}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// 3. Scan for negative-net / outbound-only ledger buckets.
const NegativeLedgerBucketsScanBlock = ({ API, authHeaders }) => {
  const [running, setRunning] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState('');

  const trigger = async () => {
    setRunning(true);
    setError('');
    setResult(null);
    try {
      const r = await axios.post(
        `${API}/admin/data-tools/scan-negative-ledger-buckets`,
        { limit: 500 },
        { headers: authHeaders },
      );
      setResult(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Scan failed.');
    } finally {
      setRunning(false);
    }
  };

  const buckets = result?.buckets || [];
  const total = result?.total_flagged || 0;

  return (
    <div
      className="mt-8 pt-6 border-t border-neutral-200"
      data-testid="data-tools-neg-ledger-scan-block"
    >
      <div className="flex items-center gap-2 mb-1">
        <AlertTriangle className="w-4 h-4 text-amber-600" />
        <div className="text-sm font-bold text-neutral-900">
          Scan Negative Ledger Buckets
        </div>
      </div>
      <div className="text-xs text-neutral-500 mb-4 leading-relaxed">
        Flags every <code className="mx-1 px-1 py-0.5 rounded bg-neutral-100 text-[11px]">(product, batch_ref, expiry_date)</code>
        bucket whose recompute-visible net is <b>negative</b> OR which has outbound
        events with no inbound. Both patterns are definitionally wrong — receipt
        must precede ship — and reproduce the IFG0012 silent-divergence pattern
        on other SKUs. Report-only; use the batch reconciler above to fix.
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Button
          type="button"
          variant="outline"
          onClick={trigger}
          disabled={running}
          data-testid="neg-ledger-scan-btn"
        >
          {running ? (
            <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
          ) : (
            <Eye className="w-4 h-4 mr-1.5" />
          )}
          {running ? 'Scanning…' : 'Scan'}
        </Button>
      </div>
      {error && (
        <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2 mb-2">
          {error}
        </div>
      )}
      {result && (
        <div
          className="text-xs text-neutral-700 bg-neutral-50 border border-neutral-200 rounded-md p-3"
          data-testid="neg-ledger-scan-result"
        >
          <div className="font-semibold text-neutral-900 mb-1">
            {total === 0 ? 'No flagged buckets — ledger is clean.' : `${total} bucket${total === 1 ? '' : 's'} flagged`}
          </div>
          {buckets.length > 0 && (
            <div className="mt-2 max-h-96 overflow-auto">
              <table className="w-full text-[11px] font-mono">
                <thead className="bg-white sticky top-0">
                  <tr className="text-left border-b border-neutral-200">
                    <th className="py-1 pr-2">SKU</th>
                    <th className="py-1 pr-2">Batch</th>
                    <th className="py-1 pr-2">Expiry</th>
                    <th className="py-1 pr-2 text-right">In</th>
                    <th className="py-1 pr-2 text-right">Out</th>
                    <th className="py-1 pr-2 text-right">Net</th>
                    <th className="py-1 pr-2">Issue</th>
                  </tr>
                </thead>
                <tbody>
                  {buckets.map((b, i) => (
                    <tr key={i} className="border-b border-neutral-100">
                      <td className="py-1 pr-2">{b.sku || b.product_id?.slice(0, 8)}</td>
                      <td className="py-1 pr-2">{b.batch_ref || '—'}</td>
                      <td className="py-1 pr-2">{b.expiry_date || '—'}</td>
                      <td className="py-1 pr-2 text-right">{b.in_qty}</td>
                      <td className="py-1 pr-2 text-right">{b.out_qty}</td>
                      <td className={`py-1 pr-2 text-right font-bold ${b.net < 0 ? 'text-rose-600' : 'text-amber-600'}`}>{b.net}</td>
                      <td className="py-1 pr-2">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] ${b.issue === 'negative_net' ? 'bg-rose-100 text-rose-800' : 'bg-amber-100 text-amber-800'}`}>
                          {b.issue}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// 2026-02 — Surgical two-ID cleanup: delete one empty placement AND
// close one stale pending picking task on an already-terminal order.
const SurgicalPlacementTaskCleanupBlock = ({ API, authHeaders }) => {
  const [form, setForm] = React.useState({
    placement_id: '',
    task_id: '',
    note: '',
  });
  const [running, setRunning] = React.useState(false);
  const [mode, setMode] = React.useState('');
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState('');

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const ready = form.placement_id.trim() && form.task_id.trim();

  const trigger = async (commit) => {
    setRunning(true);
    setMode(commit ? 'commit' : 'preview');
    setError('');
    setResult(null);
    try {
      const r = await axios.post(
        `${API}/admin/data-tools/surgical-delete-placement-and-close-task`,
        {
          placement_id: form.placement_id.trim(),
          task_id: form.task_id.trim(),
          note: form.note.trim(),
          commit,
        },
        { headers: authHeaders },
      );
      setResult(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Action failed.');
    } finally {
      setRunning(false);
    }
  };

  const skipLine = (section) => section?.skip_reason ? (
    <div className="text-[11px] text-amber-700 mt-1">
      Skipped: {section.skip_reason}
    </div>
  ) : null;

  return (
    <div
      className="mt-8 pt-6 border-t border-neutral-200"
      data-testid="data-tools-surgical-cleanup-block"
    >
      <div className="flex items-center gap-2 mb-1">
        <Wrench className="w-4 h-4 text-rose-600" />
        <div className="text-sm font-bold text-neutral-900">
          Surgical Cleanup: Delete Empty Placement + Close Stale Task
        </div>
      </div>
      <div className="text-xs text-neutral-500 mb-4 leading-relaxed">
        One-off super-admin action for the specific class of ask "delete this
        empty placement and close this stale pending task on an
        already-delivered order". Enforces strict preconditions so a mis-paste
        can't hit the wrong row: the placement must have
        <code className="mx-1 px-1 py-0.5 rounded bg-neutral-100 text-[11px]">qty = 0</code>
        and <code className="mx-1 px-1 py-0.5 rounded bg-neutral-100 text-[11px]">allocated_qty = 0</code>
        and NOT be a `superseded` merge-retired row; the task must be
        <code className="mx-1 px-1 py-0.5 rounded bg-neutral-100 text-[11px]">type = picking</code>
        AND <code className="mx-1 px-1 py-0.5 rounded bg-neutral-100 text-[11px]">status = pending</code>
        on an order already in a terminal state (delivered / shipped /
        cancelled / failed).
      </div>
      <div className="grid gap-3 md:grid-cols-2 mb-3">
        <label className="block">
          <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-1">Placement ID</div>
          <Input
            value={form.placement_id}
            onChange={(e) => update({ placement_id: e.target.value })}
            placeholder="abf1e475-518b-4e37-882b-9b199e0a0570"
            className="font-mono text-xs"
            data-testid="surgical-placement-id"
          />
        </label>
        <label className="block">
          <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-1">Task ID</div>
          <Input
            value={form.task_id}
            onChange={(e) => update({ task_id: e.target.value })}
            placeholder="56287098-6454-47f8-8ca0-a822c119a986"
            className="font-mono text-xs"
            data-testid="surgical-task-id"
          />
        </label>
        <label className="block md:col-span-2">
          <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-1">Note (optional)</div>
          <Input
            value={form.note}
            onChange={(e) => update({ note: e.target.value })}
            placeholder="MR-00000511 FA23077 F1FD12C20 cleanup"
            className="text-xs"
            data-testid="surgical-note"
          />
        </label>
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Button
          type="button"
          variant="outline"
          onClick={() => trigger(false)}
          disabled={running || !ready}
          data-testid="surgical-preview-btn"
        >
          {running && mode === 'preview' ? (
            <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
          ) : (
            <Eye className="w-4 h-4 mr-1.5" />
          )}
          {running && mode === 'preview' ? 'Checking…' : 'Preview'}
        </Button>
        <Button
          type="button"
          onClick={() => trigger(true)}
          disabled={running || !ready}
          className="bg-rose-600 hover:bg-rose-700 text-white"
          data-testid="surgical-apply-btn"
        >
          {running && mode === 'commit' ? (
            <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
          ) : (
            <Wrench className="w-4 h-4 mr-1.5" />
          )}
          {running && mode === 'commit' ? 'Applying…' : 'Apply'}
        </Button>
      </div>
      {error && (
        <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2 mb-2">
          {error}
        </div>
      )}
      {result && (
        <div
          className="text-xs text-neutral-700 bg-neutral-50 border border-neutral-200 rounded-md p-3"
          data-testid="surgical-result"
        >
          <div className="font-semibold text-neutral-900 mb-2 flex items-center gap-1">
            {result.commit ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <Eye className="w-3.5 h-3.5 text-neutral-500" />}
            {result.commit ? 'Applied' : 'Preview'}
            {result.commit && ` · placement ${result.deleted_placement ? 'deleted' : 'skipped'} · task ${result.closed_task ? 'closed' : 'skipped'}`}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-md border border-neutral-200 bg-white p-2">
              <div className="text-[10px] uppercase tracking-wider text-neutral-400 font-bold">Placement</div>
              <div className="font-mono text-[11px] break-all">{result.placement?.placement_id}</div>
              <div className="mt-1 text-[11px]">
                {result.placement?.found ? (
                  <>
                    pallet=<b>{result.placement?.pallet_id || '—'}</b> · loc=<b>{result.placement?.location_id || '—'}</b> · qty=<b>{result.placement?.qty ?? '—'}</b>
                  </>
                ) : <span className="text-neutral-500">not found</span>}
              </div>
              {skipLine(result.placement)}
            </div>
            <div className="rounded-md border border-neutral-200 bg-white p-2">
              <div className="text-[10px] uppercase tracking-wider text-neutral-400 font-bold">Task</div>
              <div className="font-mono text-[11px] break-all">{result.task?.task_id}</div>
              <div className="mt-1 text-[11px]">
                {result.task?.found ? (
                  <>
                    order=<b>{result.task?.order_number || result.task?.order_id}</b> · order_status=<b>{result.task?.order_status}</b> · task_status=<b>{result.task?.status}</b>
                  </>
                ) : <span className="text-neutral-500">not found</span>}
              </div>
              {skipLine(result.task)}
            </div>
          </div>
          {result.actions_planned?.length > 0 && !result.commit && (
            <div className="mt-2 text-[11px] text-neutral-500">
              Would run {result.actions_planned.length} action{result.actions_planned.length === 1 ? '' : 's'}.
            </div>
          )}
        </div>
      )}
    </div>
  );
};


// =====================================================================
// 2026-02 — Bulk cleanup for stale pending pick tasks on orders that
// already reached a terminal state (delivered / received / completed /
// shipped). Historical pending tasks (e.g. MR-00000021 … MR-00000026)
// accumulated before the auto-close hook on delivery existed. This
// block offers a dry-run preview (lists candidates per order) followed
// by a stock-neutral commit that retires the pending tasks + writes
// audit rows. Only touches tasks with `picked_qty` empty/zero.
// =====================================================================
const CloseStalePickTasksBlock = ({ API, authHeaders }) => {
  const [running, setRunning] = React.useState(false);
  const [mode, setMode] = React.useState('');
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState('');

  const trigger = async (commit) => {
    setRunning(true);
    setMode(commit ? 'commit' : 'preview');
    setError('');
    setResult(null);
    try {
      const r = await axios.post(
        `${API}/admin/data-tools/close-stale-pick-tasks-on-delivered-orders`,
        { commit },
        { headers: authHeaders },
      );
      setResult(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Action failed.');
    } finally {
      setRunning(false);
    }
  };

  // Group candidates by order for a cleaner render.
  const byOrder = React.useMemo(() => {
    const m = new Map();
    (result?.candidates || []).forEach((c) => {
      const key = c.order_number || c.order_id;
      if (!m.has(key)) m.set(key, { order: c, items: [] });
      m.get(key).items.push(c);
    });
    return Array.from(m.values());
  }, [result]);

  return (
    <div
      className="mt-8 pt-6 border-t border-neutral-200"
      data-testid="data-tools-close-stale-pick-tasks-block"
    >
      <div className="flex items-center gap-2 mb-1">
        <Wrench className="w-4 h-4 text-rose-600" />
        <div className="text-sm font-bold text-neutral-900">
          Close Stale Pick Tasks on Delivered Orders
        </div>
      </div>
      <div className="text-xs text-neutral-500 mb-4 leading-relaxed">
        Retires any <code className="mx-1 px-1 py-0.5 rounded bg-neutral-100 text-[11px]">pending</code>
        picking task whose order has already reached a terminal state
        (delivered / received / completed / shipped) AND whose
        <code className="mx-1 px-1 py-0.5 rounded bg-neutral-100 text-[11px]">picked_qty</code> is empty
        or zero. Stock has already been deducted at delivery, so this
        close is <b>stock-neutral</b> — only
        <code className="mx-1 px-1 py-0.5 rounded bg-neutral-100 text-[11px]">warehouse_tasks</code>
        rows are removed. Tasks on open orders and tasks that have already
        pulled stock off the shelf are left alone. Review the preview
        before committing.
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Button
          type="button"
          variant="outline"
          onClick={() => trigger(false)}
          disabled={running}
          data-testid="close-stale-picks-preview-btn"
        >
          {running && mode === 'preview' ? (
            <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
          ) : (
            <Eye className="w-4 h-4 mr-1.5" />
          )}
          {running && mode === 'preview' ? 'Scanning…' : 'Preview'}
        </Button>
        <Button
          type="button"
          onClick={() => trigger(true)}
          disabled={running || !result?.candidate_count}
          className="bg-rose-600 hover:bg-rose-700 text-white"
          data-testid="close-stale-picks-apply-btn"
        >
          {running && mode === 'commit' ? (
            <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
          ) : (
            <Wrench className="w-4 h-4 mr-1.5" />
          )}
          {running && mode === 'commit' ? 'Closing…' : `Close ${result?.candidate_count || 0}`}
        </Button>
      </div>
      {error && (
        <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2 mb-2">
          {error}
        </div>
      )}
      {result && (
        <div
          className="text-xs text-neutral-700 bg-neutral-50 border border-neutral-200 rounded-md p-3"
          data-testid="close-stale-picks-result"
        >
          <div className="font-semibold text-neutral-900 mb-2 flex items-center gap-1">
            {result.commit ? (
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
            ) : (
              <Eye className="w-3.5 h-3.5 text-neutral-500" />
            )}
            {result.commit
              ? `Closed ${result.retired_count} task(s) · ${result.audit_rows_written} audit row(s)`
              : `Preview — ${result.candidate_count} candidate(s) · ${result.skipped_count} skipped`}
          </div>
          {byOrder.length > 0 && (
            <div className="space-y-2 max-h-80 overflow-auto">
              {byOrder.map(({ order, items }) => (
                <div
                  key={order.order_id}
                  className="rounded-md border border-neutral-200 bg-white p-2"
                >
                  <div className="font-mono text-[11px] font-bold">
                    {order.order_number}{' '}
                    <span className="text-neutral-400 font-normal">
                      · {order.order_status} · {items.length} task{items.length === 1 ? '' : 's'}
                    </span>
                  </div>
                  {items.slice(0, 10).map((it) => (
                    <div key={it.task_id} className="text-[11px] text-neutral-600 mt-1">
                      <span className="font-mono">{it.task_id.slice(0, 8)}</span> ·{' '}
                      pallet=<b>{it.pallet_id || '—'}</b> · loc=<b>{it.location_id || '—'}</b> ·{' '}
                      qty=<b>{it.qty}</b>
                    </div>
                  ))}
                  {items.length > 10 && (
                    <div className="text-[10px] text-neutral-400 mt-1">
                      …and {items.length - 10} more
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          {result.skipped_sample?.length > 0 && !result.commit && (
            <div className="mt-2 text-[11px] text-amber-700">
              {result.skipped_count} task(s) skipped (orders not terminal, picked_qty &gt; 0, or missing order).
            </div>
          )}
        </div>
      )}
    </div>
  );
};



// =====================================================================
// 2026-02 — One-time surgical tool: reverse ASN-000149 duplicate
// receipt for IFG0012. Two-step Preview → Apply flow with typed
// confirmation. Mirrors the backend endpoint shape 1:1.
// =====================================================================
const IFG0012ASN149ReversalBlock = ({ API, authHeaders }) => {
  const [running, setRunning] = React.useState(false);
  const [mode, setMode] = React.useState('');
  const [preview, setPreview] = React.useState(null);
  const [applyResult, setApplyResult] = React.useState(null);
  const [error, setError] = React.useState('');
  const [typed, setTyped] = React.useState('');

  const runPreview = async () => {
    setRunning(true);
    setMode('preview');
    setError('');
    setApplyResult(null);
    try {
      const r = await axios.post(
        `${API}/admin/data-tools/ifg0012-asn149-reversal/preview`,
        {},
        { headers: authHeaders },
      );
      setPreview(r.data);
      setTyped('');
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Preview failed.');
    } finally {
      setRunning(false);
    }
  };

  const runApply = async () => {
    if (!preview?.preview_token) return;
    const expectedPhrase = preview.apply_confirmation_phrase || 'REVERSE ASN-000149';
    if (typed !== expectedPhrase) {
      setError(`Type exactly: ${expectedPhrase}`);
      return;
    }
    setRunning(true);
    setMode('apply');
    setError('');
    try {
      const r = await axios.post(
        `${API}/admin/data-tools/ifg0012-asn149-reversal/apply`,
        {
          preview_token: preview.preview_token,
          typed_confirmation: typed,
          product_total_now: preview.product_total_now,
          product_total_after_apply: preview.product_total_after_apply,
        },
        { headers: authHeaders },
      );
      setApplyResult(r.data);
      setPreview(null);
      setTyped('');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === 'string'
          ? detail
          : detail?.message || err?.message || 'Apply failed.',
      );
    } finally {
      setRunning(false);
    }
  };

  const totalsOk =
    preview &&
    preview.cartons_reduced === preview.expected_cartons_reduced &&
    preview.cartons_removed === preview.expected_cartons_removed;
  const expectedPhrase = preview?.apply_confirmation_phrase || 'REVERSE ASN-000149';

  return (
    <div
      className="mt-8 pt-6 border-t border-neutral-200"
      data-testid="data-tools-ifg0012-asn149-reversal-block"
    >
      <div className="flex items-center gap-2 mb-1">
        <Wrench className="w-4 h-4 text-rose-700" />
        <div className="text-sm font-bold text-neutral-900">
          Reverse ASN-000149 Duplicate Receipt (IFG0012)
        </div>
      </div>
      <div className="text-xs text-neutral-500 mb-4 leading-relaxed">
        One-time surgical reversal of the duplicate ASN-000149 receipt for
        product IFG0012. Reduces 3 merged placements by 288 each
        (<b>864 cartons reduced</b>) and retires 4 ASN-000149-only placements
        at 288 each (<b>1,152 cartons removed</b>). Uses the per-placement
        stock-adjustment path so each change writes an audit entry + keeps
        batch and product totals consistent. Also voids the completed
        put-away tasks on ASN-000149 for those pallets and cleans
        stale placement references on the ASN record. Picking tasks are
        never touched. ASN-000038 stock, pallet FA23084, and the ASN-000149
        hold flag are explicitly preserved. No override — if any
        precondition fails, Apply is blocked.
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Button
          type="button"
          variant="outline"
          onClick={runPreview}
          disabled={running}
          data-testid="ifg0012-asn149-preview-btn"
        >
          {running && mode === 'preview' ? (
            <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
          ) : (
            <Eye className="w-4 h-4 mr-1.5" />
          )}
          {running && mode === 'preview' ? 'Previewing…' : 'Preview'}
        </Button>
      </div>
      {error && (
        <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2 mb-2">
          {error}
        </div>
      )}

      {preview && (
        <div
          className="text-xs bg-neutral-50 border border-neutral-200 rounded-md p-3 space-y-3"
          data-testid="ifg0012-asn149-preview-result"
        >
          {/* Totals card */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
            <div className="rounded-md border border-neutral-200 bg-white p-2">
              <div className="text-[10px] uppercase text-neutral-500">Current total ({preview.product_sku})</div>
              <div className="text-sm font-mono font-bold text-neutral-900">
                {preview.product_total_now} {preview.product_uom}
              </div>
            </div>
            <div className="rounded-md border border-neutral-200 bg-white p-2">
              <div className="text-[10px] uppercase text-neutral-500">After apply (projected)</div>
              <div className="text-sm font-mono font-bold text-emerald-700">
                {preview.product_total_after_apply} {preview.product_uom}
              </div>
            </div>
            <div className="rounded-md border border-neutral-200 bg-white p-2">
              <div className="text-[10px] uppercase text-neutral-500">Reduced (864 expected)</div>
              <div
                className={`text-sm font-mono font-bold ${
                  preview.cartons_reduced === preview.expected_cartons_reduced
                    ? 'text-emerald-700'
                    : 'text-rose-700'
                }`}
                data-testid="ifg0012-cartons-reduced"
              >
                {preview.cartons_reduced}
              </div>
            </div>
            <div className="rounded-md border border-neutral-200 bg-white p-2">
              <div className="text-[10px] uppercase text-neutral-500">Removed (1,152 expected)</div>
              <div
                className={`text-sm font-mono font-bold ${
                  preview.cartons_removed === preview.expected_cartons_removed
                    ? 'text-emerald-700'
                    : 'text-rose-700'
                }`}
                data-testid="ifg0012-cartons-removed"
              >
                {preview.cartons_removed}
              </div>
            </div>
          </div>

          {/* Part A rows */}
          <div>
            <div className="font-semibold text-neutral-900 mb-1">
              Part A — reduce by 288 each (keep rows)
            </div>
            <div className="space-y-1">
              {preview.part_a.map((r) => (
                <div
                  key={r.placement_id}
                  className={`rounded-md border p-2 ${
                    r.ok
                      ? 'bg-white border-neutral-200'
                      : 'bg-rose-50 border-rose-200'
                  }`}
                >
                  <div className="font-mono text-[11px]">
                    <b>{r.pallet_id}</b> @ {r.location_id} ·{' '}
                    <span className="text-neutral-500">{r.placement_id}</span>
                  </div>
                  <div className="text-[11px] text-neutral-700">
                    qty <b>{r.qty_now}</b> → <b>{r.qty_after}</b> (delta {r.delta})
                  </div>
                  {r.issue && (
                    <div className="text-[11px] text-rose-700 mt-1">{r.issue}</div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Part B rows */}
          <div>
            <div className="font-semibold text-neutral-900 mb-1">
              Part B — retire 288 ctn each (remove)
            </div>
            <div className="space-y-1">
              {preview.part_b.map((r) => (
                <div
                  key={r.id_prefix}
                  className={`rounded-md border p-2 ${
                    r.ok
                      ? 'bg-white border-neutral-200'
                      : 'bg-rose-50 border-rose-200'
                  }`}
                >
                  <div className="font-mono text-[11px]">
                    <b>{r.pallet_id || r.expected_pallet_id}</b> @{' '}
                    {r.location_id || r.expected_location_id} ·{' '}
                    <span className="text-neutral-500">
                      {r.placement_id || `prefix ${r.id_prefix}…`}
                    </span>
                  </div>
                  <div className="text-[11px] text-neutral-700">
                    qty <b>{r.qty_now ?? r.expected_qty_now}</b> → <b>0 (retired)</b>
                  </div>
                  {r.issue && (
                    <div className="text-[11px] text-rose-700 mt-1">{r.issue}</div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Tasks */}
          <div>
            <div className="font-semibold text-neutral-900 mb-1">
              Part C — put-away tasks to void (
              {(preview.tasks_to_void || []).filter((t) => t.action === 'void').length})
            </div>
            {(preview.tasks_to_void || []).length === 0 ? (
              <div className="text-[11px] text-neutral-500">
                No completed put-away tasks on ASN-000149 for these placements.
              </div>
            ) : (
              <div className="space-y-1">
                {(preview.tasks_to_void || []).map((t) => (
                  <div
                    key={t.id}
                    className="rounded-md border border-neutral-200 bg-white p-2 text-[11px] font-mono"
                  >
                    <b>{t.pallet_id || '—'}</b> @ {t.location_id || '—'} · task{' '}
                    {t.id?.slice(0, 8)} · {t.status}
                    {t.action === 'skip_non_completed' && (
                      <span className="text-amber-700 ml-2">(will skip — not completed)</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Guardrails */}
          <div>
            <div className="font-semibold text-neutral-900 mb-1">Guardrails — untouched</div>
            <div className="space-y-0.5 text-[11px] text-neutral-600">
              {(preview.guardrail_untouched || []).map((g, i) => (
                <div key={i}>
                  •{' '}
                  {g.placement_id_prefix ? (
                    <>
                      <span className="font-mono">{g.placement_id_prefix}…</span>{' '}
                      ({g.pallet_id}
                      {g.location_id ? `, ${g.location_id}` : ''}
                      {g.qty ? `, ${g.qty} ctn` : ''}) — {g.reason}
                    </>
                  ) : (
                    <>
                      <b>{g.asn_number}</b> hold flag — {g.hold}
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Apply */}
          <div className="pt-2 border-t border-neutral-200">
            {!preview.ready_to_apply ? (
              <div className="text-[12px] text-rose-700 font-semibold">
                Apply blocked — one or more preconditions failed. Fix the
                issues above and run Preview again.
              </div>
            ) : (
              <>
                <label className="block text-[11px] font-semibold text-neutral-800 mb-1">
                  Type <code className="px-1 bg-neutral-100 rounded">{expectedPhrase}</code> to enable Apply:
                </label>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="text"
                    value={typed}
                    onChange={(e) => setTyped(e.target.value)}
                    placeholder={expectedPhrase}
                    className="flex-1 min-w-[220px] px-2 py-1.5 text-xs font-mono border border-neutral-300 rounded-md focus:outline-none focus:border-rose-500"
                    data-testid="ifg0012-asn149-confirm-input"
                  />
                  <Button
                    type="button"
                    onClick={runApply}
                    disabled={running || typed !== expectedPhrase || !totalsOk}
                    className="bg-rose-600 hover:bg-rose-700 text-white"
                    data-testid="ifg0012-asn149-apply-btn"
                  >
                    {running && mode === 'apply' ? (
                      <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                    ) : (
                      <Wrench className="w-4 h-4 mr-1.5" />
                    )}
                    {running && mode === 'apply' ? 'Applying…' : 'Apply'}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {applyResult && (
        <div
          className="mt-3 text-xs bg-emerald-50 border border-emerald-200 rounded-md p-3"
          data-testid="ifg0012-asn149-apply-result"
        >
          <div className="font-semibold text-emerald-800 mb-2 flex items-center gap-1">
            <CheckCircle2 className="w-3.5 h-3.5" />
            Applied — adjustment {applyResult.adjustment_no}
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 text-neutral-700">
            <div>
              <div className="text-[10px] uppercase text-neutral-500">Reduced</div>
              <div className="font-mono font-bold">{applyResult.cartons_reduced}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-neutral-500">Removed</div>
              <div className="font-mono font-bold">{applyResult.cartons_removed}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-neutral-500">Total before</div>
              <div className="font-mono font-bold">{applyResult.product_total_before}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase text-neutral-500">Total after (recomputed)</div>
              <div
                className={`font-mono font-bold ${
                  applyResult.product_total_after === applyResult.product_total_projected
                    ? 'text-emerald-700'
                    : 'text-rose-700'
                }`}
                data-testid="ifg0012-asn149-total-after"
              >
                {applyResult.product_total_after}
              </div>
            </div>
          </div>
          <div className="text-[11px] text-neutral-600 mt-2">
            Tasks voided: <b>{applyResult.tasks_voided}</b> · ASN record cleaned:{' '}
            <b>{applyResult.asn_record_cleaned ? 'yes' : 'no'}</b>
          </div>
        </div>
      )}
    </div>
  );
};

// =====================================================================
// 2026-02 — Super-admin: Location rules by category.
// Reads/writes the narrow `category_location_rules` section of the
// settings doc. Values: "single_pallet" | "multi_pallet". Default is
// multi_pallet so nothing changes until a super-admin flips a row.
// =====================================================================
const CategoryLocationRulesBlock = ({ API, authHeaders }) => {
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [data, setData] = React.useState(null);
  const [dirty, setDirty] = React.useState({});
  const [error, setError] = React.useState('');
  const [notice, setNotice] = React.useState('');

  const load = React.useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const r = await axios.get(
        `${API}/admin/settings/category-location-rules`,
        { headers: authHeaders },
      );
      setData(r.data);
      setDirty({});
    } catch (err) {
      // 403 → hide the whole block. Non-super-admins never see it.
      if (err?.response?.status === 403) {
        setData({ forbidden: true });
      } else {
        setError(err?.response?.data?.detail || err?.message || 'Load failed.');
      }
    } finally {
      setLoading(false);
    }
  }, [API, authHeaders]);

  React.useEffect(() => { load(); }, [load]);

  if (data?.forbidden) return null;

  const setValue = (slug, value) => {
    setDirty((d) => ({ ...d, [slug]: value }));
  };

  const effective = (slug, saved) => (dirty[slug] !== undefined ? dirty[slug] : saved);

  const save = async () => {
    if (!data) return;
    // Build the full map (merge saved + dirty); unchanged keys included
    // so the backend diff produces accurate audit entries.
    const rules = {};
    (data.categories || []).forEach((c) => {
      rules[c.slug] = effective(c.slug, c.rule);
    });
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const r = await axios.put(
        `${API}/admin/settings/category-location-rules`,
        { rules },
        { headers: authHeaders },
      );
      setDirty({});
      setNotice(
        r.data?.changes?.length
          ? `Saved. ${r.data.changes.length} category rule(s) changed.`
          : 'Saved — no changes.',
      );
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const dirtyCount = Object.keys(dirty).filter(
    (slug) => {
      const saved = (data?.categories || []).find((c) => c.slug === slug)?.rule;
      return dirty[slug] !== saved;
    },
  ).length;

  return (
    <div
      className="mt-8 pt-6 border-t border-neutral-200"
      data-testid="data-tools-category-location-rules-block"
    >
      <div className="flex items-center gap-2 mb-1">
        <Settings className="w-4 h-4 text-sky-700" />
        <div className="text-sm font-bold text-neutral-900">
          Location rules by category
        </div>
      </div>
      <div className="text-xs text-neutral-500 mb-3 leading-relaxed">
        For each product category, choose whether a bin can hold one pallet
        at a time, or multiple pallets. The default is{' '}
        <b>One location, multiple pallets</b>; nothing changes until you
        flip a row. The rule is enforced on put-away, super-admin
        placement edits, and warehouse transfers — never on picking,
        shipping, staging or returns. Merging into the same pallet
        number already in a bin stays allowed in every mode. Every
        change is written to the warehouse audit log.
      </div>
      {loading && (
        <div className="text-xs text-neutral-500 flex items-center gap-2">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading…
        </div>
      )}
      {error && (
        <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2 mb-2">
          {error}
        </div>
      )}
      {notice && (
        <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 mb-2">
          {notice}
        </div>
      )}
      {data?.categories && (
        <>
          <div className="rounded-lg border border-neutral-200 overflow-hidden">
            <div className="hidden md:grid grid-cols-[1fr_auto_auto] gap-3 bg-neutral-50 px-3 py-2 text-[11px] uppercase font-semibold text-neutral-500">
              <div>Category</div>
              <div>One location, one pallet</div>
              <div>One location, multiple pallets</div>
            </div>
            {(data.categories || []).map((c) => {
              const val = effective(c.slug, c.rule);
              const isChanged = dirty[c.slug] !== undefined && dirty[c.slug] !== c.rule;
              return (
                <div
                  key={c.slug}
                  className={`grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-3 px-3 py-2 border-t border-neutral-100 items-center ${
                    isChanged ? 'bg-amber-50' : ''
                  }`}
                  data-testid={`category-rule-row-${c.slug}`}
                >
                  <div className="text-sm">
                    <div className="font-semibold text-neutral-900">{c.name}</div>
                    <div className="font-mono text-[10px] text-neutral-500">{c.slug}</div>
                  </div>
                  <label className="inline-flex items-center gap-2 text-xs">
                    <input
                      type="radio"
                      name={`rule-${c.slug}`}
                      checked={val === 'single_pallet'}
                      onChange={() => setValue(c.slug, 'single_pallet')}
                      data-testid={`category-rule-${c.slug}-single`}
                    />
                    <span className="md:hidden">One location, one pallet</span>
                  </label>
                  <label className="inline-flex items-center gap-2 text-xs">
                    <input
                      type="radio"
                      name={`rule-${c.slug}`}
                      checked={val === 'multi_pallet'}
                      onChange={() => setValue(c.slug, 'multi_pallet')}
                      data-testid={`category-rule-${c.slug}-multi`}
                    />
                    <span className="md:hidden">One location, multiple pallets</span>
                  </label>
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-3 mt-3">
            <Button
              type="button"
              onClick={save}
              disabled={saving || dirtyCount === 0}
              className="bg-sky-600 hover:bg-sky-700 text-white"
              data-testid="category-rules-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
              {saving ? 'Saving…' : `Save${dirtyCount ? ` (${dirtyCount})` : ''}`}
            </Button>
            {dirtyCount > 0 && (
              <button
                type="button"
                onClick={() => setDirty({})}
                className="text-xs text-neutral-500 hover:text-neutral-800"
              >
                Discard changes
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};


// =====================================================================
// 2026-02 — Super-admin: read-only report listing locations that
// currently violate the single_pallet rule.
// =====================================================================
const LocationRuleViolationsReportBlock = ({ API, authHeaders }) => {
  const [loading, setLoading] = React.useState(false);
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await axios.get(
        `${API}/admin/reports/location-rule-violations`,
        { headers: authHeaders },
      );
      setData(r.data);
    } catch (err) {
      if (err?.response?.status === 403) {
        // Hide the block for non-super-admins; they won't see any violations anyway.
        setData({ forbidden: true });
      } else {
        setError(err?.response?.data?.detail || err?.message || 'Load failed.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (data?.forbidden) return null;

  return (
    <div
      className="mt-8 pt-6 border-t border-neutral-200"
      data-testid="data-tools-location-rule-violations-block"
    >
      <div className="flex items-center gap-2 mb-1">
        <AlertTriangle className="w-4 h-4 text-amber-600" />
        <div className="text-sm font-bold text-neutral-900">
          Location rule violations (read-only)
        </div>
      </div>
      <div className="text-xs text-neutral-500 mb-3 leading-relaxed">
        Lists non-staging bins that currently hold two or more different
        pallets with stock where at least one of those pallets is in a
        category set to <b>One location, one pallet</b>. Read-only —
        this report does <b>not</b> change any placement.
      </div>
      <Button
        type="button"
        variant="outline"
        onClick={load}
        disabled={loading}
        data-testid="location-rule-violations-run-btn"
      >
        {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
        {loading ? 'Scanning…' : 'Run report'}
      </Button>
      {error && (
        <div className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2 mt-2">
          {error}
        </div>
      )}
      {data && !data.forbidden && (
        <div
          className="text-xs bg-neutral-50 border border-neutral-200 rounded-md p-3 mt-3"
          data-testid="location-rule-violations-result"
        >
          <div className="font-semibold text-neutral-900 mb-2 flex items-center gap-2">
            {data.violation_count === 0 ? (
              <>
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                No violations — every bin complies with the current rules.
              </>
            ) : (
              <>
                <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                {data.violation_count} bin(s) violate the single-pallet rule.
              </>
            )}
          </div>
          {data.violation_count > 0 && (
            <div className="space-y-2 max-h-96 overflow-auto">
              {(data.violations || []).map((v) => (
                <div
                  key={`${v.warehouse_id}:${v.location_id}`}
                  className="rounded-md border border-neutral-200 bg-white p-2"
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="font-mono text-[11px] font-bold">
                      {v.location_id}
                      <span className="text-neutral-400 font-normal">
                        {' '}· {v.warehouse_name}
                      </span>
                    </div>
                    <div className="text-[10px] text-rose-700">
                      flagged: {(v.flagged_category_slugs || []).join(', ')}
                    </div>
                  </div>
                  <div className="space-y-0.5">
                    {(v.pallets || []).map((p) => (
                      <div
                        key={p.pallet_id}
                        className="text-[11px] font-mono text-neutral-700"
                      >
                        <b>{p.pallet_id}</b> · {p.product_name || p.product_id} ·{' '}
                        <span
                          className={
                            p.rule === 'single_pallet'
                              ? 'text-rose-700 font-semibold'
                              : 'text-neutral-500'
                          }
                        >
                          {p.category_slug || '—'} ({p.rule})
                        </span>{' '}
                        · {p.qty} ctn
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};


// =====================================================================
// Feb 2026 — Three super-admin dispatch-lock recovery tools, each with
// a two-step Dry run → Commit flow. Each block posts to its own
// endpoint and renders the raw server response in a scrollable code
// block so ops can audit every placement / order before committing.
// Commit is disabled until a successful dry run has returned.
// =====================================================================
const _TwoStepToolBlock = ({
  title,
  testIdSlug,
  description,
  buildPayload,
  endpoint,
  method,
  renderInputs,
  renderSummary,
}) => {
  const [running, setRunning] = React.useState(false);
  const [mode, setMode] = React.useState('');
  const [dryResult, setDryResult] = React.useState(null);
  const [commitResult, setCommitResult] = React.useState(null);
  const [error, setError] = React.useState('');

  const trigger = async (commit) => {
    setRunning(true);
    setMode(commit ? 'commit' : 'preview');
    setError('');
    try {
      const payload = buildPayload({ commit });
      if (payload === null) {
        setError('Please fill the required fields above.');
        return;
      }
      const url = typeof endpoint === 'function' ? endpoint() : endpoint;
      const config = { headers: authHeadersCache };
      const r = (method || 'post').toLowerCase() === 'get'
        ? await axios.get(url, config)
        : await axios.post(url, payload, config);
      if (commit) {
        setCommitResult(r.data);
      } else {
        setDryResult(r.data);
        setCommitResult(null);     // force a fresh dry run after inputs change
      }
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Request failed.');
    } finally {
      setRunning(false);
    }
  };

  // `authHeaders` is injected via closure capture at usage site — see
  // the three concrete blocks below. Keep this helper block generic.
  const authHeadersCache = _TwoStepToolBlock._latestAuthHeaders || {};

  const canCommit = !!dryResult && !running;

  return (
    <div
      className="mt-8 pt-6 border-t border-neutral-200"
      data-testid={`data-tools-${testIdSlug}-block`}
    >
      <div className="flex items-center gap-2 mb-1">
        <Database className="w-4 h-4 text-[#0078D4]" />
        <div className="text-sm font-bold text-neutral-900">{title}</div>
      </div>
      <div className="text-xs text-neutral-500 mb-4 leading-relaxed">{description}</div>
      {renderInputs ? (
        <div className="grid gap-3 md:grid-cols-2 mb-3">
          {renderInputs({ onInputsChanged: () => { setDryResult(null); setCommitResult(null); } })}
        </div>
      ) : null}
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => trigger(false)}
          disabled={running}
          data-testid={`${testIdSlug}-dryrun-btn`}
        >
          {running && mode === 'preview' ? 'Running…' : 'Dry run'}
        </Button>
        <Button
          size="sm"
          onClick={() => trigger(true)}
          disabled={!canCommit}
          className="bg-[#0078D4] hover:bg-[#005A9E]"
          data-testid={`${testIdSlug}-commit-btn`}
        >
          {running && mode === 'commit' ? 'Committing…' : 'Commit'}
        </Button>
        {!canCommit && !running && (
          <span className="text-[11px] text-neutral-500">
            Run a dry run first to enable Commit.
          </span>
        )}
      </div>
      {error && (
        <div
          className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
          data-testid={`${testIdSlug}-error`}
        >
          {error}
        </div>
      )}
      {dryResult && (
        <div className="mt-4" data-testid={`${testIdSlug}-dry-result`}>
          <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1">
            Dry run result
          </div>
          {renderSummary ? renderSummary(dryResult, false) : null}
          <pre className="mt-2 max-h-72 overflow-auto text-[11px] bg-neutral-900 text-neutral-100 rounded-md p-3">
            {JSON.stringify(dryResult, null, 2)}
          </pre>
        </div>
      )}
      {commitResult && (
        <div className="mt-4" data-testid={`${testIdSlug}-commit-result`}>
          <div className="text-xs font-semibold uppercase tracking-wider text-emerald-700 mb-1">
            Commit result
          </div>
          {renderSummary ? renderSummary(commitResult, true) : null}
          <pre className="mt-2 max-h-72 overflow-auto text-[11px] bg-emerald-950 text-emerald-50 rounded-md p-3">
            {JSON.stringify(commitResult, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

// Static field used by `_TwoStepToolBlock` so the three concrete blocks
// below can inject the latest `authHeaders` without wrapping every
// render in a Context. Set at render-time by each concrete block.
_TwoStepToolBlock._latestAuthHeaders = {};


const ReleaseStaleDispatchLocksToolBlock = ({ API, authHeaders }) => {
  // Keep the latest auth headers reachable by the shared helper.
  _TwoStepToolBlock._latestAuthHeaders = authHeaders;
  return (
    <_TwoStepToolBlock
      title="Release stale dispatch locks (terminal orders)"
      testIdSlug="release-stale-dispatch-locks"
      description={
        <>
          Scans every placement whose <code className="mx-1 px-1 py-0.5 rounded bg-neutral-100 text-[11px]">dispatch_locked_order_id</code>
          points at an order already in a terminal state (cancelled / failed /
          returned / delivered / rejected) and releases the lock. The dry run
          lists the candidate placements and their owning orders; commit
          applies the release and writes a <code className="mx-1 px-1 py-0.5 rounded bg-neutral-100 text-[11px]">warehouse_audit_log</code>
          entry per placement.
        </>
      }
      buildPayload={({ commit }) => ({ commit })}
      endpoint={`${API}/admin/data-tools/release-stale-dispatch-locks`}
      method="post"
      renderSummary={(r, isCommit) => (
        <div className="text-xs text-neutral-700">
          Candidates: <strong>{r?.candidate_count ?? 0}</strong>
          {isCommit && (
            <> · Released: <strong className="text-emerald-700">{r?.released_count ?? 0}</strong></>
          )}
        </div>
      )}
    />
  );
};


const RestoreOrderReservationsToolBlock = ({ API, authHeaders }) => {
  _TwoStepToolBlock._latestAuthHeaders = authHeaders;
  const [orderId, setOrderId] = React.useState('30ad89c5-57af-4865-ac0b-3095d326da09');
  return (
    <_TwoStepToolBlock
      title="Restore order reservations (allocated_qty)"
      testIdSlug="restore-order-reservations"
      description={
        <>
          Rebuilds the correct <code className="mx-1 px-1 py-0.5 rounded bg-neutral-100 text-[11px]">allocated_qty</code>
          on every placement referenced by an order's dispatch plan by summing
          <code className="mx-1 px-1 py-0.5 rounded bg-neutral-100 text-[11px]">qty_to_pick</code> across every active,
          unshipped order. Skips any placement where the correct total exceeds the pallet's
          physical qty (flagged for review). On commit, flips
          <code className="mx-1 px-1 py-0.5 rounded bg-neutral-100 text-[11px]">dispatch_plan.consumed</code>
          back to <code className="mx-1 px-1 py-0.5 rounded bg-neutral-100 text-[11px]">false</code> so normal shipping deducts stock.
          Pre-filled with order MR-00000683 — change the Order ID below to target another order.
        </>
      }
      renderInputs={({ onInputsChanged }) => (
        <label className="block md:col-span-2">
          <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-1">
            Order ID
          </div>
          <Input
            value={orderId}
            onChange={(e) => { setOrderId(e.target.value); onInputsChanged(); }}
            placeholder="30ad89c5-57af-4865-ac0b-3095d326da09"
            className="font-mono text-xs"
            data-testid="restore-order-reservations-order-id"
          />
        </label>
      )}
      buildPayload={({ commit }) => {
        if (!orderId.trim()) return null;
        return { commit };
      }}
      endpoint={() => `${API}/admin/data-tools/restore-order-reservations/${encodeURIComponent(orderId.trim())}`}
      method="post"
      renderSummary={(r, isCommit) => (
        <div className="text-xs text-neutral-700">
          Order: <strong className="font-mono">{r?.order_number || r?.order_id || '—'}</strong>
          {' · '}Placements in plan: <strong>{(r?.placements || []).length}</strong>
          {' · '}Flagged over-booked:{' '}
          <strong className={(r?.placements_flagged ?? 0) > 0 ? 'text-red-700' : ''}>
            {r?.placements_flagged ?? 0}
          </strong>
          {isCommit && (
            <> · Updated: <strong className="text-emerald-700">{r?.placements_updated ?? 0}</strong></>
          )}
        </div>
      )}
    />
  );
};


const AllocateShortfallToolBlock = ({ API, authHeaders }) => {
  _TwoStepToolBlock._latestAuthHeaders = authHeaders;
  const [orderId, setOrderId] = React.useState('');
  const [orderLineId, setOrderLineId] = React.useState('');
  const [allocationsText, setAllocationsText] = React.useState('');
  const parsedAllocations = React.useMemo(() => {
    // Lines look like `placement_id:qty` — one per line.
    const out = [];
    for (const raw of allocationsText.split(/\n+/)) {
      const line = raw.trim();
      if (!line) continue;
      const [plc, qty] = line.split(/[:,\s]+/);
      const q = parseInt(qty, 10);
      if (!plc || !Number.isFinite(q) || q <= 0) continue;
      out.push({ placement_id: plc.trim(), qty: q });
    }
    return out;
  }, [allocationsText]);

  return (
    <div
      className="mt-8 pt-6 border-t border-neutral-200"
      data-testid="data-tools-allocate-shortfall-block"
    >
      <div className="flex items-center gap-2 mb-1">
        <Database className="w-4 h-4 text-[#0078D4]" />
        <div className="text-sm font-bold text-neutral-900">Allocate shortfall (per-line)</div>
      </div>
      <div className="text-xs text-neutral-500 mb-4 leading-relaxed">
        Additive top-up for a SINGLE shortfall / unassigned pick line on an order.
        Dry run calls <code className="mx-1 px-1 py-0.5 rounded bg-neutral-100 text-[11px]">GET /shortfall-candidates/&lt;line&gt;</code>
        to show the free pallets of this product (FEFO), so you know which placement IDs to type below.
        Commit calls <code className="mx-1 px-1 py-0.5 rounded bg-neutral-100 text-[11px]">POST /dispatch/fill-shortfall</code>.
        Every other line and pallet on the order stays byte-identical. Prefer the inline
        &ldquo;Allocate shortfall&rdquo; button on the order's Allocation Details table for
        day-to-day use — this block is here for ops / scripting.
      </div>
      <_AllocateShortfallInner
        API={API}
        authHeaders={authHeaders}
        orderId={orderId}
        setOrderId={setOrderId}
        orderLineId={orderLineId}
        setOrderLineId={setOrderLineId}
        allocationsText={allocationsText}
        setAllocationsText={setAllocationsText}
        parsedAllocations={parsedAllocations}
      />
    </div>
  );
};

const _AllocateShortfallInner = ({
  API, authHeaders, orderId, setOrderId, orderLineId, setOrderLineId,
  allocationsText, setAllocationsText, parsedAllocations,
}) => {
  const [running, setRunning] = React.useState(false);
  const [mode, setMode] = React.useState('');
  const [dryResult, setDryResult] = React.useState(null);
  const [commitResult, setCommitResult] = React.useState(null);
  const [error, setError] = React.useState('');

  const triggerDry = async () => {
    setRunning(true);
    setMode('preview');
    setError('');
    setCommitResult(null);
    try {
      if (!orderId.trim() || !orderLineId.trim()) {
        setError('Enter an Order ID and Order Line ID to run a dry run.');
        return;
      }
      const r = await axios.get(
        `${API}/admin/orders/${encodeURIComponent(orderId.trim())}/dispatch/shortfall-candidates/${encodeURIComponent(orderLineId.trim())}`,
        { headers: authHeaders },
      );
      setDryResult(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Dry run failed.');
    } finally {
      setRunning(false);
    }
  };

  const triggerCommit = async () => {
    setRunning(true);
    setMode('commit');
    setError('');
    try {
      if (!parsedAllocations.length) {
        setError('Add at least one placement line (format: placement_id:qty) before committing.');
        return;
      }
      const r = await axios.post(
        `${API}/admin/orders/${encodeURIComponent(orderId.trim())}/dispatch/fill-shortfall`,
        { order_line_id: orderLineId.trim(), allocations: parsedAllocations },
        { headers: authHeaders },
      );
      setCommitResult(r.data);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Commit failed.');
    } finally {
      setRunning(false);
    }
  };

  const canCommit = !!dryResult && !running && parsedAllocations.length > 0;

  return (
    <>
      <div className="grid gap-3 md:grid-cols-2 mb-3">
        <label className="block">
          <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-1">
            Order ID
          </div>
          <Input
            value={orderId}
            onChange={(e) => { setOrderId(e.target.value); setDryResult(null); setCommitResult(null); }}
            placeholder="30ad89c5-57af-4865-ac0b-3095d326da09"
            className="font-mono text-xs"
            data-testid="allocate-shortfall-order-id"
          />
        </label>
        <label className="block">
          <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-1">
            Order Line ID
          </div>
          <Input
            value={orderLineId}
            onChange={(e) => { setOrderLineId(e.target.value); setDryResult(null); setCommitResult(null); }}
            placeholder="L12"
            className="font-mono text-xs"
            data-testid="allocate-shortfall-line-id"
          />
        </label>
        <label className="block md:col-span-2">
          <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-1">
            Allocations (one per line, format <code>placement_id:qty</code>)
          </div>
          <textarea
            value={allocationsText}
            onChange={(e) => setAllocationsText(e.target.value)}
            placeholder="2d8a…:50&#10;7f3c…:25"
            className="w-full font-mono text-xs rounded-md border border-neutral-300 px-2 py-1.5 min-h-[72px]"
            data-testid="allocate-shortfall-allocations"
          />
          <div className="mt-1 text-[11px] text-neutral-500">
            Parsed: <strong>{parsedAllocations.length}</strong> allocation(s), total qty{' '}
            <strong>{parsedAllocations.reduce((s, a) => s + a.qty, 0)}</strong>
          </div>
        </label>
      </div>
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={triggerDry}
          disabled={running}
          data-testid="allocate-shortfall-dryrun-btn"
        >
          {running && mode === 'preview' ? 'Running…' : 'Dry run'}
        </Button>
        <Button
          size="sm"
          onClick={triggerCommit}
          disabled={!canCommit}
          className="bg-[#0078D4] hover:bg-[#005A9E]"
          data-testid="allocate-shortfall-commit-btn"
        >
          {running && mode === 'commit' ? 'Committing…' : 'Commit'}
        </Button>
        {!canCommit && !running && (
          <span className="text-[11px] text-neutral-500">
            Run a dry run first, then type the allocations to enable Commit.
          </span>
        )}
      </div>
      {error && (
        <div
          className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
          data-testid="allocate-shortfall-error"
        >
          {error}
        </div>
      )}
      {dryResult && (
        <div className="mt-4" data-testid="allocate-shortfall-dry-result">
          <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1">
            Dry run result (candidate pallets)
          </div>
          <div className="text-xs text-neutral-700 mb-2">
            Shortfall on this line: <strong>{dryResult?.shortfall_qty ?? 0}</strong> ctn ·
            Candidates: <strong>{(dryResult?.candidates || []).length}</strong>
          </div>
          <pre className="max-h-72 overflow-auto text-[11px] bg-neutral-900 text-neutral-100 rounded-md p-3">
            {JSON.stringify(dryResult, null, 2)}
          </pre>
        </div>
      )}
      {commitResult && (
        <div className="mt-4" data-testid="allocate-shortfall-commit-result">
          <div className="text-xs font-semibold uppercase tracking-wider text-emerald-700 mb-1">
            Commit result
          </div>
          <div className="text-xs text-neutral-700 mb-2">
            Filled <strong className="text-emerald-700">{commitResult?.total_qty_filled ?? 0}</strong> ctn
            across <strong>{commitResult?.placements_filled ?? 0}</strong> pallet(s).
            Shortfall: <strong>{commitResult?.shortfall_before}</strong> → <strong>{commitResult?.shortfall_after}</strong>
          </div>
          <pre className="max-h-72 overflow-auto text-[11px] bg-emerald-950 text-emerald-50 rounded-md p-3">
            {JSON.stringify(commitResult, null, 2)}
          </pre>
        </div>
      )}
    </>
  );
};


// ---------------------------------------------------------------------------
// MR-00000785 / L12 / FA22095 — one-off correction block (Feb 2026)
// Super-admin-only. Rendered inside the Maintenance tab, which is itself
// gated by `isSuper` upstream. Dry Run shows the before/after JSON diff;
// Commit stays disabled until a dry run has succeeded AND the reason has
// not changed since. The underlying endpoint (/admin/_correction/mr785-l12-fa22095)
// is NOT modified — only its UI entry point is added here.
// ---------------------------------------------------------------------------
const Mr785L12CorrectionBlock = ({ API, authHeaders }) => {
  const [reason, setReason] = React.useState('');
  const [committableReason, setCommittableReason] = React.useState(null);
  const [running, setRunning] = React.useState(false);
  const [mode, setMode] = React.useState('');
  const [dryResult, setDryResult] = React.useState(null);
  const [commitResult, setCommitResult] = React.useState(null);
  const [error, setError] = React.useState('');

  const reasonTrim = reason.trim();

  const trigger = async (commit) => {
    setRunning(true);
    setMode(commit ? 'commit' : 'preview');
    setError('');
    try {
      const r = await axios.post(
        `${API}/admin/_correction/mr785-l12-fa22095`,
        { commit, reason: reasonTrim },
        { headers: authHeaders },
      );
      if (commit) {
        setCommitResult(r.data);
      } else {
        setDryResult(r.data);
        setCommitResult(null);
        // Only enable commit when the dry run shows a real, actionable diff.
        if (r.data?.preconditions_ok && !r.data?.already_applied) {
          setCommittableReason(reasonTrim);
        } else {
          setCommittableReason(null);
        }
      }
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg =
        (d && typeof d === 'object' && (d.message || JSON.stringify(d))) ||
        (typeof d === 'string' ? d : '') ||
        err?.message ||
        'Request failed.';
      setError(msg);
    } finally {
      setRunning(false);
    }
  };

  // Reason change invalidates the committable state (and clears any stale
  // dry/commit output so the UI stays honest about which reason is live).
  const onReasonChange = (v) => {
    setReason(v);
    if (committableReason !== null) {
      setCommittableReason(null);
    }
    if (commitResult) {
      setCommitResult(null);
    }
  };

  const canDry = !!reasonTrim && !running;
  const canCommit =
    !!dryResult
    && !running
    && !!committableReason
    && reasonTrim === committableReason;

  const b = dryResult?.before;
  const a = dryResult?.after;
  const hasBeforeAfter = !!(b || a);

  return (
    <div
      className="mt-8 pt-6 border-t border-neutral-200"
      data-testid="data-tools-mr785-l12-correction-block"
    >
      <div className="flex items-center gap-2 mb-1">
        <Database className="w-4 h-4 text-[#0078D4]" />
        <div className="text-sm font-bold text-neutral-900">
          MR-00000785 L12 Correction
        </div>
      </div>
      <div className="text-xs text-neutral-500 mb-4 leading-relaxed">
        One-off recovery for order <strong>MR-00000785</strong>, line{' '}
        <strong>L12</strong> (SKU <strong>MFG0015</strong>). Clears the false
        <code className="mx-1 px-1 py-0.5 rounded bg-neutral-100 text-[11px]">shipped_state</code>
        flag on pallet <strong>FA22095</strong>, re-points the pending unassigned
        91-ctn pick line + task to that pallet, and increments allocation through
        the canonical allocation helper. The completed 11-ctn FA22093 pick stays
        untouched. Super-admin only; dry run is read-only; a second commit is a
        safe no-op.
      </div>

      <label className="block mb-3">
        <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-1">
          Reason <span className="text-neutral-400 font-normal lowercase tracking-normal">— required, audited</span>
        </div>
        <Textarea
          value={reason}
          onChange={(e) => onReasonChange(e.target.value)}
          placeholder="e.g. live recovery after false-ship on FA22095 (ticket #…)"
          rows={2}
          className="font-mono text-xs"
          data-testid="mr785-l12-correction-reason"
        />
      </label>

      <div className="flex items-center gap-2 flex-wrap">
        <Button
          variant="outline"
          size="sm"
          onClick={() => trigger(false)}
          disabled={!canDry}
          data-testid="mr785-l12-correction-dryrun-btn"
        >
          {running && mode === 'preview' ? 'Running…' : 'Dry run'}
        </Button>
        <Button
          size="sm"
          onClick={() => trigger(true)}
          disabled={!canCommit}
          className="bg-[#0078D4] hover:bg-[#005A9E]"
          data-testid="mr785-l12-correction-commit-btn"
        >
          {running && mode === 'commit' ? 'Committing…' : 'Commit'}
        </Button>
        {!reasonTrim && (
          <span
            className="text-[11px] text-neutral-500"
            data-testid="mr785-l12-correction-needs-reason-hint"
          >
            Enter a reason to enable Dry run.
          </span>
        )}
        {reasonTrim && !canCommit && !running && (
          <span
            className="text-[11px] text-neutral-500"
            data-testid="mr785-l12-correction-needs-dry-hint"
          >
            {dryResult
              ? (committableReason === null
                  ? 'This dry run cannot be committed (either nothing to apply, preconditions failed, or already applied).'
                  : 'Reason changed since the last dry run — run Dry run again to re-enable Commit.')
              : 'Run a dry run first to enable Commit.'}
          </span>
        )}
      </div>

      {error && (
        <div
          className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
          data-testid="mr785-l12-correction-error"
        >
          {error}
        </div>
      )}

      {dryResult && (
        <div className="mt-4" data-testid="mr785-l12-correction-dry-result">
          <div className="flex items-center gap-2 mb-2">
            <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
              Dry run result
            </div>
            {dryResult.already_applied && (
              <span className="text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                already applied
              </span>
            )}
            {!dryResult.already_applied && dryResult.preconditions_ok && (
              <span className="text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                preconditions ok
              </span>
            )}
            {!dryResult.already_applied && !dryResult.preconditions_ok && (
              <span className="text-[11px] font-bold text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                preconditions failed
              </span>
            )}
          </div>
          {(dryResult.reasons || []).length > 0 && (
            <ul
              className="mb-3 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 list-disc list-inside space-y-0.5"
              data-testid="mr785-l12-correction-reasons"
            >
              {(dryResult.reasons || []).map((rsn, i) => (
                <li key={i}>{rsn}</li>
              ))}
            </ul>
          )}
          {hasBeforeAfter && (
            <div className="grid md:grid-cols-2 gap-3 mb-3">
              <div data-testid="mr785-l12-correction-before">
                <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-1">
                  Before
                </div>
                <pre className="max-h-72 overflow-auto text-[11px] bg-neutral-900 text-neutral-100 rounded-md p-3">
                  {JSON.stringify(b || null, null, 2)}
                </pre>
              </div>
              <div data-testid="mr785-l12-correction-after">
                <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-1">
                  After (proposed)
                </div>
                <pre className="max-h-72 overflow-auto text-[11px] bg-neutral-900 text-neutral-100 rounded-md p-3">
                  {JSON.stringify(a || null, null, 2)}
                </pre>
              </div>
            </div>
          )}
          {dryResult?.diagnostics && (
            <div className="mt-3" data-testid="mr785-l12-correction-diagnostics">
              <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-2">
                Diagnostics
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <div className="rounded-md border border-neutral-200 bg-white p-3">
                  <div className="text-[11px] font-bold text-neutral-700 mb-1">
                    Resolved product id (SKU MFG0015)
                  </div>
                  <div className="font-mono text-[11px] break-all">
                    {dryResult.diagnostics.resolved_product_id || <em className="text-red-700">unresolved</em>}
                  </div>
                  <div className="mt-2 text-[11px] text-neutral-500">
                    products.sku lookup →{' '}
                    <span className="font-mono">
                      {dryResult.diagnostics.mfg_products_sku_match_id || <em>not found</em>}
                    </span>
                  </div>
                </div>
                <div className="rounded-md border border-neutral-200 bg-white p-3">
                  <div className="text-[11px] font-bold text-neutral-700 mb-1">
                    L12 item on order
                  </div>
                  {dryResult.diagnostics.l12_item ? (
                    <div className="text-[11px] space-y-0.5">
                      <div>line_no: <strong>{dryResult.diagnostics.l12_item.line_no}</strong></div>
                      <div>qty: <strong>{dryResult.diagnostics.l12_item.qty}</strong> {dryResult.diagnostics.l12_item.uom || ''}</div>
                      <div>item.id: <span className="font-mono">{dryResult.diagnostics.l12_item.id || '—'}</span></div>
                      <div>product_id: <span className="font-mono">{dryResult.diagnostics.l12_item.product_id || '—'}</span></div>
                      <div>name: {dryResult.diagnostics.l12_item.name || '—'}</div>
                    </div>
                  ) : (
                    <div className="text-[11px] text-red-700">L12 item not found on order items.</div>
                  )}
                </div>
                <div className="rounded-md border border-neutral-200 bg-white p-3 md:col-span-2">
                  <div className="text-[11px] font-bold text-neutral-700 mb-1">
                    FA22095 (target pallet)
                  </div>
                  <div className="text-[11px] grid grid-cols-2 md:grid-cols-4 gap-y-0.5 gap-x-3 font-mono">
                    <div>qty: <strong>{dryResult.diagnostics.fa22095.qty}</strong></div>
                    <div>allocated: <strong>{dryResult.diagnostics.fa22095.allocated_qty}</strong></div>
                    <div>dispatch_locked_qty: <strong>{dryResult.diagnostics.fa22095.dispatch_locked_qty}</strong></div>
                    <div>shipped_state: <strong>{dryResult.diagnostics.fa22095.shipped_state || '(empty)'}</strong></div>
                    <div className="col-span-2">location: {dryResult.diagnostics.fa22095.zone || '—'} / {dryResult.diagnostics.fa22095.location_id || '—'}</div>
                    <div className="col-span-2">shipped_order_id: {dryResult.diagnostics.fa22095.shipped_order_id || '—'}</div>
                  </div>
                </div>
                <div className="rounded-md border border-neutral-200 bg-white p-3 md:col-span-2">
                  <div className="text-[11px] font-bold text-neutral-700 mb-1">
                    Every MFG0015 pick line on order ({dryResult.diagnostics.mfg_picks?.length || 0})
                  </div>
                  {(dryResult.diagnostics.mfg_picks || []).length === 0 ? (
                    <div className="text-[11px] text-neutral-500">No MFG0015 pick lines on this order.</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-[11px]">
                        <thead className="bg-neutral-50 text-neutral-600 uppercase tracking-wider">
                          <tr>
                            <th className="px-2 py-1 text-left">#</th>
                            <th className="px-2 py-1 text-right">qty</th>
                            <th className="px-2 py-1 text-left">pallet</th>
                            <th className="px-2 py-1 text-left">location</th>
                            <th className="px-2 py-1 text-left">unassigned</th>
                            <th className="px-2 py-1 text-right">shortfall</th>
                            <th className="px-2 py-1 text-left">corrected_by</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dryResult.diagnostics.mfg_picks.map((p, i) => (
                            <tr key={i} className="border-t border-neutral-100">
                              <td className="px-2 py-1 font-mono">{p.index}</td>
                              <td className="px-2 py-1 text-right font-mono">{p.qty_to_pick}</td>
                              <td className="px-2 py-1 font-mono">{p.pallet_id || <em className="text-neutral-400">—</em>}</td>
                              <td className="px-2 py-1 font-mono">{p.location_id || '—'}</td>
                              <td className="px-2 py-1">{p.unassigned ? <strong className="text-amber-800">yes</strong> : 'no'}</td>
                              <td className="px-2 py-1 text-right font-mono">{p.shortfall_qty}</td>
                              <td className="px-2 py-1 font-mono">{p.corrected_by || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                <div className="rounded-md border border-neutral-200 bg-white p-3">
                  <div className="text-[11px] font-bold text-neutral-700 mb-1">
                    FA22093 completed picking task
                  </div>
                  {dryResult.diagnostics.fa22093_completed_task ? (
                    <div className="text-[11px] space-y-0.5 font-mono">
                      <div>status: <strong>{dryResult.diagnostics.fa22093_completed_task.status}</strong></div>
                      <div>qty: <strong>{dryResult.diagnostics.fa22093_completed_task.qty}</strong> picked: <strong>{dryResult.diagnostics.fa22093_completed_task.picked_qty}</strong></div>
                      <div>task.id: {dryResult.diagnostics.fa22093_completed_task.id}</div>
                      <div>location: {dryResult.diagnostics.fa22093_completed_task.location_id || '—'}</div>
                    </div>
                  ) : (
                    <div className="text-[11px] text-red-700">No completed FA22093 task found.</div>
                  )}
                </div>
                <div className="rounded-md border border-neutral-200 bg-white p-3">
                  <div className="text-[11px] font-bold text-neutral-700 mb-1">
                    Pending MFG0015 picking task
                  </div>
                  {dryResult.diagnostics.pending_mfg_task ? (
                    <div className="text-[11px] space-y-0.5 font-mono">
                      <div>status: <strong>{dryResult.diagnostics.pending_mfg_task.status}</strong></div>
                      <div>qty: <strong>{dryResult.diagnostics.pending_mfg_task.qty}</strong> picked: <strong>{dryResult.diagnostics.pending_mfg_task.picked_qty}</strong></div>
                      <div>task.id: {dryResult.diagnostics.pending_mfg_task.id}</div>
                      <div>pallet: {dryResult.diagnostics.pending_mfg_task.pallet_id || <em className="text-neutral-400">(empty)</em>}</div>
                      <div>location: {dryResult.diagnostics.pending_mfg_task.location_id || <em className="text-neutral-400">(empty)</em>}</div>
                    </div>
                  ) : (
                    <div className="text-[11px] text-red-700">No pending MFG0015 picking task found.</div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {commitResult && (
        <div className="mt-4" data-testid="mr785-l12-correction-commit-result">
          <div className="flex items-center gap-2 mb-2">
            <div className="text-xs font-semibold uppercase tracking-wider text-emerald-700">
              Commit result
            </div>
            {commitResult.applied && (
              <span className="text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
                applied
              </span>
            )}
            {!commitResult.applied && commitResult.already_applied && (
              <span className="text-[11px] font-bold text-neutral-700 bg-neutral-100 border border-neutral-200 rounded-full px-2 py-0.5">
                no-op (already applied)
              </span>
            )}
          </div>
          <pre className="max-h-72 overflow-auto text-[11px] bg-emerald-950 text-emerald-50 rounded-md p-3">
            {JSON.stringify(commitResult, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Clear Stale Shipped Flag — generic super-admin tool (Feb 2026)
// Rendered inside the Maintenance tab (gated by `isSuper` upstream).
// Takes a list of pallet IDs + a reason, shows a per-pallet dry-run, then
// clears only shipped_state / shipped_at / shipped_order_id on eligible rows.
// Commit disabled until a successful dry run; re-disabled if pallet IDs or
// reason change after the dry run.
// ---------------------------------------------------------------------------
const ClearStaleShippedFlagBlock = ({ API, authHeaders }) => {
  const [palletText, setPalletText] = React.useState('');
  const [reason, setReason] = React.useState('');
  const [running, setRunning] = React.useState(false);
  const [mode, setMode] = React.useState('');
  const [dryResult, setDryResult] = React.useState(null);
  const [commitResult, setCommitResult] = React.useState(null);
  const [error, setError] = React.useState('');
  // Snapshot of the EXACT inputs the dry run was taken with. Commit stays
  // disabled unless the live inputs still match this snapshot verbatim.
  const [committableInputs, setCommittableInputs] = React.useState(null);

  const parsedPallets = React.useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const raw of palletText.split(/[\n,]+/)) {
      const s = (raw || '').trim();
      if (!s || seen.has(s)) continue;
      seen.add(s);
      out.push(s);
    }
    return out;
  }, [palletText]);

  const reasonTrim = reason.trim();
  const currentKey = JSON.stringify({ ids: parsedPallets, r: reasonTrim });

  const trigger = async (commit) => {
    setRunning(true);
    setMode(commit ? 'commit' : 'preview');
    setError('');
    try {
      const r = await axios.post(
        `${API}/admin/_correction/clear-stale-shipped-flag`,
        { commit, reason: reasonTrim, pallet_ids: parsedPallets },
        { headers: authHeaders },
      );
      if (commit) {
        setCommitResult(r.data);
      } else {
        setDryResult(r.data);
        setCommitResult(null);
        if ((r.data?.eligible_count ?? 0) > 0) {
          setCommittableInputs(currentKey);
        } else {
          setCommittableInputs(null);
        }
      }
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg =
        (d && typeof d === 'object' && (d.message || JSON.stringify(d))) ||
        (typeof d === 'string' ? d : '') ||
        err?.message ||
        'Request failed.';
      setError(msg);
    } finally {
      setRunning(false);
    }
  };

  const invalidateCommit = () => {
    if (committableInputs !== null) setCommittableInputs(null);
    if (commitResult) setCommitResult(null);
  };

  const onPalletsChange = (v) => {
    setPalletText(v);
    invalidateCommit();
  };
  const onReasonChange = (v) => {
    setReason(v);
    invalidateCommit();
  };

  const canDry = parsedPallets.length > 0 && !!reasonTrim && !running;
  const canCommit =
    !!dryResult
    && !running
    && !!committableInputs
    && currentKey === committableInputs;

  return (
    <div
      className="mt-8 pt-6 border-t border-neutral-200"
      data-testid="data-tools-clear-stale-shipped-flag-block"
    >
      <div className="flex items-center gap-2 mb-1">
        <Database className="w-4 h-4 text-[#0078D4]" />
        <div className="text-sm font-bold text-neutral-900">
          Clear stale shipped flag (per pallet)
        </div>
      </div>
      <div className="text-xs text-neutral-500 mb-4 leading-relaxed">
        Clears a stale/false <code className="mx-1 px-1 py-0.5 rounded bg-neutral-100 text-[11px]">shipped_state</code>
        flag on one or more pallets so the physical qty can be dispatched again.
        Only pallets with <code className="mx-1 px-1 py-0.5 rounded bg-neutral-100 text-[11px]">qty &gt; 0</code>
        and <code className="mx-1 px-1 py-0.5 rounded bg-neutral-100 text-[11px]">shipped_state ∈ {`{shipped, consumed}`}</code>
        are eligible. Clears <strong>only</strong>{' '}
        <code className="mx-1 px-1 py-0.5 rounded bg-neutral-100 text-[11px]">shipped_state</code>,
        <code className="mx-1 px-1 py-0.5 rounded bg-neutral-100 text-[11px]">shipped_at</code>, and
        <code className="mx-1 px-1 py-0.5 rounded bg-neutral-100 text-[11px]">shipped_order_id</code>.
        No qty, ledger entry, or non-listed pallet is touched.
      </div>

      <div className="grid gap-3 md:grid-cols-2 mb-3">
        <label className="block md:col-span-2">
          <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-1">
            Pallet IDs <span className="text-neutral-400 font-normal lowercase tracking-normal">— one per line or comma-separated</span>
          </div>
          <textarea
            value={palletText}
            onChange={(e) => onPalletsChange(e.target.value)}
            placeholder={'FA22095\nFA22101\nFA22233'}
            className="w-full font-mono text-xs rounded-md border border-neutral-300 px-2 py-1.5 min-h-[88px]"
            data-testid="clear-stale-shipped-pallet-ids"
          />
          <div className="mt-1 text-[11px] text-neutral-500">
            Parsed: <strong>{parsedPallets.length}</strong> unique pallet id(s).
          </div>
        </label>
        <label className="block md:col-span-2">
          <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-500 mb-1">
            Reason <span className="text-neutral-400 font-normal lowercase tracking-normal">— required, audited</span>
          </div>
          <Textarea
            value={reason}
            onChange={(e) => onReasonChange(e.target.value)}
            placeholder="e.g. false-ship after cancelled order X — physical stock still on floor"
            rows={2}
            className="font-mono text-xs"
            data-testid="clear-stale-shipped-reason"
          />
        </label>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Button
          variant="outline"
          size="sm"
          onClick={() => trigger(false)}
          disabled={!canDry}
          data-testid="clear-stale-shipped-dryrun-btn"
        >
          {running && mode === 'preview' ? 'Running…' : 'Dry run'}
        </Button>
        <Button
          size="sm"
          onClick={() => trigger(true)}
          disabled={!canCommit}
          className="bg-[#0078D4] hover:bg-[#005A9E]"
          data-testid="clear-stale-shipped-commit-btn"
        >
          {running && mode === 'commit' ? 'Committing…' : 'Commit'}
        </Button>
        {!canDry && !running && (
          <span
            className="text-[11px] text-neutral-500"
            data-testid="clear-stale-shipped-needs-inputs-hint"
          >
            Enter at least one pallet id and a reason to enable Dry run.
          </span>
        )}
        {canDry && !canCommit && !running && (
          <span
            className="text-[11px] text-neutral-500"
            data-testid="clear-stale-shipped-needs-dry-hint"
          >
            {dryResult
              ? (committableInputs === null
                  ? 'No eligible pallets in the last dry run — nothing to commit.'
                  : 'Pallet IDs or reason changed — run Dry run again to re-enable Commit.')
              : 'Run a dry run first to enable Commit.'}
          </span>
        )}
      </div>

      {error && (
        <div
          className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
          data-testid="clear-stale-shipped-error"
        >
          {error}
        </div>
      )}

      {dryResult && (
        <div className="mt-4" data-testid="clear-stale-shipped-dry-result">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
              Dry run result
            </div>
            <span className="text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
              Eligible: {dryResult.eligible_count ?? 0}
            </span>
            {(dryResult.skipped_pallet_ids || []).length > 0 && (
              <span className="text-[11px] font-bold text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                Skipped: {dryResult.skipped_pallet_ids.length}
              </span>
            )}
            {(dryResult.not_found_pallet_ids || []).length > 0 && (
              <span className="text-[11px] font-bold text-red-800 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">
                Not found: {dryResult.not_found_pallet_ids.length}
              </span>
            )}
          </div>
          <div className="overflow-x-auto rounded-md border border-neutral-200">
            <table className="w-full text-[11px]" data-testid="clear-stale-shipped-table">
              <thead className="bg-neutral-50 text-neutral-600 uppercase tracking-wider">
                <tr>
                  <th className="px-2 py-1.5 text-left">Pallet</th>
                  <th className="px-2 py-1.5 text-left">Product</th>
                  <th className="px-2 py-1.5 text-right">Qty</th>
                  <th className="px-2 py-1.5 text-right">Allocated</th>
                  <th className="px-2 py-1.5 text-left">Location</th>
                  <th className="px-2 py-1.5 text-left">Shipped state</th>
                  <th className="px-2 py-1.5 text-left">Shipped at</th>
                  <th className="px-2 py-1.5 text-left">Shipped order</th>
                  <th className="px-2 py-1.5 text-left">Eligible</th>
                </tr>
              </thead>
              <tbody>
                {(dryResult.report || []).map((r, i) => (
                  <tr key={`${r.pallet_id}-${i}`} className="border-t border-neutral-100">
                    <td className="px-2 py-1.5 font-mono">{r.pallet_id}</td>
                    <td className="px-2 py-1.5">
                      {r.found ? (
                        <>
                          <div className="font-semibold">{r.product_name || '—'}</div>
                          <div className="text-neutral-500 font-mono">{r.product_id || ''}</div>
                        </>
                      ) : (
                        <span className="text-red-700">not found</span>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono">{r.found ? r.qty : '—'}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{r.found ? r.allocated_qty : '—'}</td>
                    <td className="px-2 py-1.5">
                      {r.found ? (
                        <>
                          <div>{r.zone || '—'} / <span className="font-mono">{r.location_id || '—'}</span></div>
                          <div className="text-neutral-500 font-mono">{r.warehouse_id || ''}</div>
                        </>
                      ) : '—'}
                    </td>
                    <td className="px-2 py-1.5 font-mono">{r.shipped_state || (r.found ? '(empty)' : '—')}</td>
                    <td className="px-2 py-1.5 font-mono">{r.shipped_at || '—'}</td>
                    <td className="px-2 py-1.5">
                      {r.found ? (
                        r.shipped_order_id ? (
                          <>
                            <div className="font-mono">{r.shipped_order_number || r.shipped_order_id}</div>
                            <div className="text-neutral-500">
                              {r.shipped_order_found
                                ? (r.shipped_order_status || '—')
                                : 'order not found'}
                            </div>
                          </>
                        ) : <span className="text-neutral-400">—</span>
                      ) : '—'}
                    </td>
                    <td className="px-2 py-1.5">
                      {r.eligible ? (
                        <span className="text-emerald-700 font-bold">yes</span>
                      ) : (
                        <>
                          <span className="text-neutral-500 font-bold">no</span>
                          {r.reason && (
                            <div className="text-[10px] text-neutral-500">{r.reason}</div>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
                {(dryResult.report || []).length === 0 && (
                  <tr>
                    <td colSpan={9} className="px-2 py-3 text-center text-neutral-500">
                      No pallets to display.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {commitResult && (
        <div className="mt-4" data-testid="clear-stale-shipped-commit-result">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <div className="text-xs font-semibold uppercase tracking-wider text-emerald-700">
              Commit result
            </div>
            <span className="text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
              Cleared: {commitResult.cleared_count ?? 0}
            </span>
            {(commitResult.failed || []).length > 0 && (
              <span className="text-[11px] font-bold text-red-800 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">
                Failed: {commitResult.failed.length}
              </span>
            )}
          </div>
          <pre className="max-h-72 overflow-auto text-[11px] bg-emerald-950 text-emerald-50 rounded-md p-3">
            {JSON.stringify(commitResult, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};


// ---------------------------------------------------------------------------
// Fix dispatch lock quantities — super-admin only (Feb 2026)
// Wraps POST /admin/data-tools/backfill-dispatch-lock-qty.
//   Preview  → commit=false. Lists every candidate placement with its
//              pallet qty + the derived lock qty from the locking order's
//              dispatch plan, plus every skipped pallet with its reason.
//   Apply    → commit=true. Enabled ONLY after a successful preview.
// Endpoint logic is not changed by this block.
// ---------------------------------------------------------------------------
const FixDispatchLockQuantitiesBlock = ({ API, authHeaders }) => {
  const [running, setRunning] = React.useState(false);
  const [mode, setMode] = React.useState('');
  const [previewResult, setPreviewResult] = React.useState(null);
  const [applyResult, setApplyResult] = React.useState(null);
  const [error, setError] = React.useState('');
  const [canApply, setCanApply] = React.useState(false);

  const trigger = async (commit) => {
    setRunning(true);
    setMode(commit ? 'apply' : 'preview');
    setError('');
    try {
      const r = await axios.post(
        `${API}/admin/data-tools/backfill-dispatch-lock-qty`,
        { commit },
        { headers: authHeaders },
      );
      if (commit) {
        setApplyResult(r.data);
      } else {
        setPreviewResult(r.data);
        setApplyResult(null);
        setCanApply((r.data?.candidate_count ?? 0) > 0);
      }
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg =
        (d && typeof d === 'object' && (d.message || JSON.stringify(d))) ||
        (typeof d === 'string' ? d : '') ||
        err?.message ||
        'Request failed.';
      setError(msg);
    } finally {
      setRunning(false);
    }
  };

  const candidates = previewResult?.candidates || [];
  const skipped = previewResult?.skipped_sample || [];

  return (
    <div
      className="mt-8 pt-6 border-t border-neutral-200"
      data-testid="data-tools-fix-dispatch-lock-qty-block"
    >
      <div className="flex items-center gap-2 mb-1">
        <Database className="w-4 h-4 text-[#0078D4]" />
        <div className="text-sm font-bold text-neutral-900">
          Fix dispatch lock quantities
        </div>
      </div>
      <div className="text-xs text-neutral-500 mb-4 leading-relaxed">
        Walks every placement with the legacy boolean{' '}
        <code className="mx-1 px-1 py-0.5 rounded bg-neutral-100 text-[11px]">dispatch_locked=true</code>
        {' '}but no per-order quantity data, and derives the correct
        {' '}<code className="mx-1 px-1 py-0.5 rounded bg-neutral-100 text-[11px]">dispatch_locked_qty</code>
        {' '}from the locking order{"\u2019"}s dispatch plan.
        {' '}<strong>Preview</strong> never writes. Pallets whose locking
        order is missing or has no matching pick line are listed under
        {' '}<strong>Skipped</strong> with the reason {"\u2014"} no guessing.
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Button
          variant="outline"
          size="sm"
          onClick={() => trigger(false)}
          disabled={running}
          data-testid="fix-dispatch-lock-qty-preview-btn"
        >
          {running && mode === 'preview' ? 'Running\u2026' : 'Preview'}
        </Button>
        <Button
          size="sm"
          onClick={() => trigger(true)}
          disabled={!canApply || running}
          className="bg-[#0078D4] hover:bg-[#005A9E]"
          data-testid="fix-dispatch-lock-qty-apply-btn"
        >
          {running && mode === 'apply' ? 'Applying\u2026' : 'Apply'}
        </Button>
        {!running && !canApply && (
          <span
            className="text-[11px] text-neutral-500"
            data-testid="fix-dispatch-lock-qty-needs-preview-hint"
          >
            {previewResult
              ? 'No candidates in the last preview \u2014 nothing to apply.'
              : 'Run Preview first to enable Apply.'}
          </span>
        )}
      </div>

      {error && (
        <div
          className="mt-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2"
          data-testid="fix-dispatch-lock-qty-error"
        >
          {error}
        </div>
      )}

      {previewResult && (
        <div className="mt-4" data-testid="fix-dispatch-lock-qty-preview-result">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500">
              Preview result
            </div>
            <span className="text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
              Candidates: {previewResult.candidate_count ?? 0}
            </span>
            {(previewResult.skipped_count ?? 0) > 0 && (
              <span className="text-[11px] font-bold text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                Skipped: {previewResult.skipped_count}
              </span>
            )}
          </div>

          <div className="overflow-x-auto rounded-md border border-neutral-200">
            <table className="w-full text-[11px]" data-testid="fix-dispatch-lock-qty-candidates-table">
              <thead className="bg-neutral-50 text-neutral-600 uppercase tracking-wider">
                <tr>
                  <th className="px-2 py-1.5 text-left">Pallet</th>
                  <th className="px-2 py-1.5 text-right">Pallet qty</th>
                  <th className="px-2 py-1.5 text-left">Locking order</th>
                  <th className="px-2 py-1.5 text-left">Order status</th>
                  <th className="px-2 py-1.5 text-right">Derived lock qty</th>
                </tr>
              </thead>
              <tbody>
                {candidates.map((c, i) => (
                  <tr
                    key={`${c.placement_id}-${i}`}
                    className="border-t border-neutral-100"
                    data-testid={`fix-dispatch-lock-qty-candidate-row-${i}`}
                  >
                    <td className="px-2 py-1.5 font-mono">
                      <div>{c.pallet_id || '\u2014'}</div>
                      <div className="text-neutral-500">{c.location_id || ''}</div>
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono">{c.pallet_qty ?? 0}</td>
                    <td className="px-2 py-1.5 font-mono">
                      {c.order_number || c.locking_order_id || '\u2014'}
                    </td>
                    <td className="px-2 py-1.5 font-mono">{c.order_status || '\u2014'}</td>
                    <td className="px-2 py-1.5 text-right font-mono font-bold text-emerald-700">
                      {c.derived_lock_qty ?? 0}
                    </td>
                  </tr>
                ))}
                {candidates.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-2 py-3 text-center text-neutral-500">
                      No candidates {"\u2014"} nothing to apply.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {skipped.length > 0 && (
            <div className="mt-3">
              <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1.5">
                Skipped ({previewResult.skipped_count})
              </div>
              <div className="overflow-x-auto rounded-md border border-neutral-200">
                <table className="w-full text-[11px]" data-testid="fix-dispatch-lock-qty-skipped-table">
                  <thead className="bg-neutral-50 text-neutral-600 uppercase tracking-wider">
                    <tr>
                      <th className="px-2 py-1.5 text-left">Placement</th>
                      <th className="px-2 py-1.5 text-left">Locking order</th>
                      <th className="px-2 py-1.5 text-left">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {skipped.map((s, i) => (
                      <tr
                        key={`${s.placement_id}-${i}`}
                        className="border-t border-neutral-100"
                        data-testid={`fix-dispatch-lock-qty-skipped-row-${i}`}
                      >
                        <td className="px-2 py-1.5 font-mono">{s.placement_id || '\u2014'}</td>
                        <td className="px-2 py-1.5 font-mono">
                          {s.order_number || s.locking_order_id || '\u2014'}
                        </td>
                        <td className="px-2 py-1.5 text-neutral-700">{s.skip_reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {previewResult.skipped_count > skipped.length && (
                <div className="mt-1 text-[11px] text-neutral-500">
                  Showing first {skipped.length} of {previewResult.skipped_count} skipped rows.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {applyResult && (
        <div className="mt-4" data-testid="fix-dispatch-lock-qty-apply-result">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <div className="text-xs font-semibold uppercase tracking-wider text-emerald-700">
              Apply result
            </div>
            <span className="text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
              Patched: {applyResult.patched_count ?? 0}
            </span>
            {(applyResult.candidate_count ?? 0) !== (applyResult.patched_count ?? 0) && (
              <span className="text-[11px] font-bold text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                Candidates: {applyResult.candidate_count ?? 0}
              </span>
            )}
          </div>
          <pre className="max-h-72 overflow-auto text-[11px] bg-emerald-950 text-emerald-50 rounded-md p-3">
            {JSON.stringify(applyResult, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
};


// ---------------------------------------------------------------------------
// Maintenance Tools Index — read-only directory of every tool on this tab.
// Appears at the top of the Maintenance tab. Does not call any backend;
// purely a glance table so super-admins know what each tool does, whether
// it writes data, and whether it is safe to reuse. Feb 2026.
// ---------------------------------------------------------------------------
const _MAINTENANCE_TOOLS_INDEX = [
  // Core (built-in) blocks on the Maintenance tab.
  {
    name: 'Storefront maintenance toggle',
    does: 'Puts the public storefront into an offline/maintenance mode.',
    kind: 'writes',
    scope: 'general',
  },
  {
    name: 'Branch portal maintenance toggle',
    does: 'Puts the /branch portal into an offline/maintenance mode (independent of storefront).',
    kind: 'writes',
    scope: 'general',
  },
  // Data tools — stay in display order.
  {
    name: 'Reconcile Allocations',
    does: 'Recomputes the Allocated counter on every placement from live pick plans.',
    kind: 'writes',
    scope: 'general',
  },
  {
    name: 'Reconcile Placement Quantities',
    does: 'Rebuilds pallet qty from the ledger when physical counters drift.',
    kind: 'writes',
    scope: 'general',
  },
  {
    name: 'Recalculate Open Dispatch Plans',
    does: 'Regenerates FEFO pick plans for every open order.',
    kind: 'writes',
    scope: 'general',
  },
  {
    name: 'Clear False Shipped Flags (qty > 0)',
    does: 'Bulk-clears placements whose shipped_state is set but qty is still on hand.',
    kind: 'writes',
    scope: 'general',
  },
  {
    name: 'Reconcile Batch Ledger Net',
    does: 'Reconciles each product × batch ledger net against on-hand placements.',
    kind: 'writes',
    scope: 'general',
  },
  {
    name: 'Scan Negative Ledger Buckets',
    does: 'Lists ledger buckets whose net is below zero.',
    kind: 'reports',
    scope: 'general',
  },
  {
    name: 'Surgical Cleanup: Delete Empty Placement + Close Stale Task',
    does: 'Point-fix for a single empty pallet + its orphan picking task.',
    kind: 'writes',
    scope: 'general',
  },
  {
    name: 'Close Stale Pick Tasks on Delivered Orders',
    does: 'Closes pending picking tasks on orders already marked delivered.',
    kind: 'writes',
    scope: 'general',
  },
  {
    name: 'Location rules by category',
    does: 'Defines which product categories are allowed in which warehouse zones.',
    kind: 'writes',
    scope: 'general',
  },
  {
    name: 'Location rule violations (read-only)',
    does: 'Lists placements that break the location rules above.',
    kind: 'reports',
    scope: 'general',
  },
  {
    name: 'Release stale dispatch locks (terminal orders)',
    does: 'Releases dispatch locks on placements owned by cancelled/failed/delivered orders.',
    kind: 'writes',
    scope: 'general',
  },
  {
    name: 'Restore order reservations (allocated_qty)',
    does: 'Rebuilds allocated_qty on every pallet in a given order\u2019s plan.',
    kind: 'writes',
    scope: 'general',
  },
  {
    name: 'Allocate shortfall (per-line)',
    does: 'Additively fills a single shortfall or unassigned pick on one order line.',
    kind: 'writes',
    scope: 'general',
  },
  {
    name: 'Clear stale shipped flag (per pallet)',
    does: 'Clears a stale shipped_state/at/order_id on a listed set of pallets (qty > 0 only).',
    kind: 'writes',
    scope: 'general',
  },
  {
    name: 'Fix dispatch lock quantities',
    does: 'Derives dispatch_locked_qty for legacy boolean-locked placements from the locking order\u2019s dispatch plan. Preview \u2192 Apply.',
    kind: 'writes',
    scope: 'general',
  },
  // Archived one-off tools.
  {
    name: 'MR-495 Cleanup',
    does: 'Deletes orphan pending tasks left behind by order MR-00000495.',
    kind: 'writes',
    scope: 'one-off, do not reuse',
    archived: true,
  },
  {
    name: 'Phase 2 Repair',
    does: 'Historic ledger correction for the Phase-2 batch rebuild.',
    kind: 'writes',
    scope: 'one-off, do not reuse',
    archived: true,
  },
  {
    name: 'IFG0012 ASN-149 Reversal',
    does: 'Reverses the duplicate receipt from ASN-000149 against product IFG0012.',
    kind: 'writes',
    scope: 'one-off, do not reuse',
    archived: true,
  },
  {
    name: 'MR-00000785 L12 Correction',
    does: 'Clears the false shipped flag on FA22095 and re-points the L12 pick for order MR-00000785.',
    kind: 'writes',
    scope: 'one-off, do not reuse',
    archived: true,
  },
];

const MaintenanceToolsIndexBlock = () => {
  const rows = _MAINTENANCE_TOOLS_INDEX;
  return (
    <div
      className="mb-6 rounded-xl border border-neutral-200 bg-white"
      data-testid="maintenance-tools-index"
    >
      <div className="px-4 py-3 border-b border-neutral-100 flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-bold text-neutral-900">Tools index</div>
          <div className="text-[11px] text-neutral-500 mt-0.5 leading-relaxed">
            Read-only directory of every block on this tab. The one-off tools at
            the bottom are hidden by default under <strong>Archived one-off tools</strong>.
          </div>
        </div>
        <div className="text-[11px] text-neutral-500 shrink-0">
          <span className="font-bold text-neutral-700">{rows.length}</span> tools
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[11px]" data-testid="maintenance-tools-index-table">
          <thead className="bg-neutral-50 text-neutral-600 uppercase tracking-wider">
            <tr>
              <th className="px-3 py-1.5 text-left">Name</th>
              <th className="px-3 py-1.5 text-left">What it does</th>
              <th className="px-3 py-1.5 text-left">Writes / reports</th>
              <th className="px-3 py-1.5 text-left">Scope</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr
                key={`${r.name}-${i}`}
                className={`border-t border-neutral-100 ${r.archived ? 'bg-neutral-50/60' : ''}`}
                data-testid={`maintenance-tools-index-row-${i}`}
              >
                <td className="px-3 py-1.5 font-semibold text-neutral-900 align-top">
                  {r.name}
                </td>
                <td className="px-3 py-1.5 text-neutral-700 align-top">{r.does}</td>
                <td className="px-3 py-1.5 align-top">
                  {r.kind === 'writes' ? (
                    <span className="inline-flex items-center text-[10px] font-bold rounded-full border border-amber-200 bg-amber-50 text-amber-800 px-2 py-0.5">
                      writes data
                    </span>
                  ) : (
                    <span className="inline-flex items-center text-[10px] font-bold rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 px-2 py-0.5">
                      reports only
                    </span>
                  )}
                </td>
                <td className="px-3 py-1.5 align-top">
                  {r.archived ? (
                    <span className="inline-flex items-center text-[10px] font-bold rounded-full border border-red-200 bg-red-50 text-red-800 px-2 py-0.5">
                      {r.scope}
                    </span>
                  ) : (
                    <span className="inline-flex items-center text-[10px] font-bold rounded-full border border-neutral-200 bg-neutral-50 text-neutral-700 px-2 py-0.5">
                      {r.scope}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};


// ---------------------------------------------------------------------------
// Archived One-Off Tools — collapsible wrapper that groups historical,
// non-reusable tools at the bottom of the Maintenance tab. Collapsed by
// default; the children's own behaviour is NOT modified. Feb 2026.
// ---------------------------------------------------------------------------
const ArchivedOneOffToolsSection = ({ children }) => {
  const [open, setOpen] = React.useState(false);
  const count = React.Children.count(children);
  return (
    <div
      className="mt-10 pt-6 border-t border-neutral-200"
      data-testid="archived-one-off-tools"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 rounded-md bg-neutral-50 hover:bg-neutral-100 border border-neutral-200 px-4 py-3 text-left"
        aria-expanded={open}
        data-testid="archived-one-off-tools-toggle"
      >
        <div>
          <div className="text-sm font-bold text-neutral-900">
            Archived one-off tools
          </div>
          <div className="text-[11px] text-neutral-500">
            Historical fixes for specific incidents — do not reuse. Click to{' '}
            {open ? 'hide' : 'show'}.
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-[10px] font-bold rounded-full border border-red-200 bg-red-50 text-red-800 px-2 py-0.5">
            one-off, do not reuse
          </span>
          <span className="text-[11px] text-neutral-500">
            {count} tool{count === 1 ? '' : 's'}
          </span>
          <span
            className={`text-neutral-500 transition-transform ${open ? 'rotate-180' : ''}`}
            aria-hidden="true"
          >
            ▾
          </span>
        </div>
      </button>
      {open && (
        <div
          className="mt-3 rounded-md border border-neutral-200 bg-white p-3"
          data-testid="archived-one-off-tools-body"
        >
          {children}
        </div>
      )}
    </div>
  );
};

